import { DOMAIN_NAME, DOMAIN_URL } from '../consts/DomainConfig';
import { getAccessToken, setAccessToken, clearAccessToken } from './authTokens';
import { getSignupAttribution } from '../utils/signupAttribution';
import { getReferralCode } from '../utils/referralCode';

// File upload helper function
const createFormDataFromObject = (data) => {
  const formData = new FormData();
  
  Object.keys(data).forEach(key => {
    if (Array.isArray(data[key])) {
      data[key].forEach((item, index) => {
        formData.append(`${key}[${index}]`, item);
      });
    } else {
      formData.append(key, data[key]);
    }
  });
  
  return formData;
};

// Use environment-specific API URL
const normalizeApiUrl = () => {
    // If accessing through non-www domain in production, use www
    if (window.location.hostname === 'gabriela.app.br') {
        window.location.href = window.location.href.replace('gabriela.app.br', DOMAIN_NAME);
    }
    
    // Use environment-specific API base URL
    return process.env.REACT_APP_API_BASE_URL || 'https://www.gabriela.app.br/api/';
};

const BASE_URL = normalizeApiUrl();

// Auth endpoints
const LOGIN = BASE_URL + "auth/login.php";
const SESSION = BASE_URL + "auth/session.php";
const REGISTER = BASE_URL + "auth/register.php";
const REFRESH_TOKEN = BASE_URL + "auth/refresh-token.php";
const LOGOUT = BASE_URL + "auth/logout.php";

// Support endpoints
const SYSTEM_FEEDBACK_SUBMIT = BASE_URL + "support/system_feedback_submit.php";
const SYSTEM_FEEDBACK_GET = BASE_URL + "support/system_feedback_get.php";
const SYSTEM_FEEDBACK_UPDATE = BASE_URL + "support/system_feedback_update.php";
const SYSTEM_NPS_SUBMIT = BASE_URL + "support/system_nps_submit.php";
const SYSTEM_NPS_GET = BASE_URL + "support/system_nps_get.php";
const SYSTEM_NPS_GET_USER = BASE_URL + "support/system_nps_get_user.php";
const SYSTEM_FEEDBACK_UPLOAD_FILE = BASE_URL + "support/system_feedback_upload_file.php";
const CONTACT_SUBMIT = BASE_URL + "support/contact_submit.php";
const PASSWORD_VALIDATE = BASE_URL + "auth/validate_password.php";
const PASSWORD_REQUEST_RESET = BASE_URL + "auth/password_request_reset.php";
const PASSWORD_VERIFY_TOKEN = BASE_URL + "auth/password_verify_token.php";
const PASSWORD_RESET = BASE_URL + "auth/password_reset.php";
const EMAIL_VERIFICATION_CONFIRM = BASE_URL + "auth/email_verification_confirm.php";
const EMAIL_VERIFICATION_RESEND = BASE_URL + "auth/email_verification_resend.php";
const EMAIL_VERIFICATION_CHANGE_EMAIL = BASE_URL + "auth/email_verification_change_email.php";
const EMAIL_VERIFICATION_STATUS = BASE_URL + "auth/email_verification_status.php";

// User Profile endpoints
const USER_PROFILE_GET = BASE_URL + "user/profile/get_user_profile.php";
const USER_PROFILE_UPDATE = BASE_URL + "user/profile/update_user_profile.php";
const USER_PICTURE_UPDATE = BASE_URL + "user/profile/update_user_picture.php";
const USER_PASSWORD_UPDATE = BASE_URL + "user/profile/update_password.php";
const USER_COMMUNICATION_PREFERENCES_GET = BASE_URL + "user/profile/get_communication_preferences.php";
const USER_COMMUNICATION_PREFERENCES_UPDATE = BASE_URL + "user/profile/update_communication_preferences.php";

// User sessions (sessões ativas do próprio usuário, L-21)
const USER_SESSIONS_LIST = BASE_URL + "user/sessions/list.php";
const USER_SESSIONS_REVOKE = BASE_URL + "user/sessions/revoke.php";

// Endpoints que gravam o cookie HttpOnly de renovação: em dev o SPA e a API têm
// origens diferentes, então essas chamadas precisam de credentials: 'include'.
const AUTH_COOKIE_PATHS = [LOGIN, EMAIL_VERIFICATION_CONFIRM, EMAIL_VERIFICATION_STATUS];
const authCookieOptions = (path) => (AUTH_COOKIE_PATHS.includes(path) ? { credentials: 'include' } : {});

// Company Users APIs
const USERS_GET = BASE_URL + "company/users/get.php";
const USERS_INVITE = BASE_URL + "company/users/invite.php";
const USERS_ACCEPT_INVITE = BASE_URL + "company/users/accept-invite.php";
const USERS_REVOKE = BASE_URL + "company/users/revoke.php";
const USERS_DELETE = BASE_URL + "company/users/delete.php";
const USERS_VALIDATE_INVITE = BASE_URL + "company/users/validate-invite.php";
const USERS_UPDATE = BASE_URL + "company/users/update.php";
const USERS_TRANSFER_OWNERSHIP = BASE_URL + "company/users/transfer-ownership.php";
const USERS_SET_HEALTH_PROFESSIONAL = BASE_URL + "company/users/set_health_professional.php";
const USERS_SET_SCHEDULE_LINK = BASE_URL + "company/users/set_schedule_link.php";
const USERS_REACTIVATE = BASE_URL + "company/users/reactivate.php";
const USERS_CANCEL_INVITE = BASE_URL + "company/users/cancel-invite.php";
const USERS_RESEND_INVITE = BASE_URL + "company/users/resend-invite.php";

// Company Resources APIs
const RESOURCES_GET = BASE_URL + "get_company_resources.php";
const RESOURCES_SET = BASE_URL + "set_company_resource.php";
const RESOURCES_UPDATE = BASE_URL + "update_company_resource.php";
const RESOURCES_DELETE = BASE_URL + "delete_company_resource.php";

// Company Payment Methods APIs
const PAYMENT_METHODS_GET = BASE_URL + "get_company_payment_methods.php";
const PAYMENT_METHODS_SET = BASE_URL + "set_company_payment_method.php";
const PAYMENT_METHODS_UPDATE = BASE_URL + "update_company_payment_method.php";
const PAYMENT_METHODS_DELETE = BASE_URL + "delete_company_payment_method.php";

// Company Procedures APIs
const PROCEDURES_GET = BASE_URL + "get_company_procedures.php";
const PROCEDURES_SET = BASE_URL + "set_company_procedure.php";
const PROCEDURES_UPDATE = BASE_URL + "update_company_procedure.php";
const PROCEDURES_DELETE = BASE_URL + "delete_company_procedure.php";

// System Config APIs
const SYSTEM_CONFIG_PRIVATE = BASE_URL + "system-config/get.php";
const SYSTEM_CONFIG_PUBLIC = BASE_URL + "system-config/get-public.php";

// Company Setup APIs
const COMPANY_SETUP_GET_STATUS = BASE_URL + "company/setup/get_status.php";
const COMPANY_SETUP_UPDATE_STATUS = BASE_URL + "company/setup/update_status.php";
const COMPANY_SETUP_TRACK_STEP = BASE_URL + "company/setup/track_step.php";
const COMPANY_SETUP_GET_WIZARD = BASE_URL + "company/setup/get_wizard.php";
const COMPANY_SETUP_SAVE_AREA = BASE_URL + "company/setup/save_area.php";
const COMPANY_SETUP_SAVE_IDENTITY = BASE_URL + "company/setup/save_identity.php";
const COMPANY_SETUP_SAVE_SCHEDULE = BASE_URL + "company/setup/save_schedule.php";
const COMPANY_SETUP_SAVE_SERVICES = BASE_URL + "company/setup/save_services.php";
const COMPANY_SETUP_SEND_DEMO = BASE_URL + "company/setup/send_demo.php";
const COMPANY_SETUP_GET_DEMO_STATUS = BASE_URL + "company/setup/get_demo_status.php";
// Dados de exemplo da conta em teste (docs/features/trial-demo-data)
const COMPANY_DEMO_SEED = BASE_URL + "company/demo/seed.php";
const COMPANY_DEMO_GET_STATUS = BASE_URL + "company/demo/get_status.php";
const COMPANY_DEMO_REMOVE = BASE_URL + "company/demo/remove.php";
const COMPANY_FIRST_STEPS_GET = BASE_URL + "company/setup/get_first_steps.php";
const COMPANY_FIRST_STEPS_DISMISS = BASE_URL + "company/setup/dismiss_first_steps.php";
const COMPANY_FIRST_STEPS_SEEN = BASE_URL + "company/setup/see_first_step.php";

// Tutoriais: progresso do usuário nas lições
const TUTORIALS_GET_PROGRESS = BASE_URL + "tutorials/get_progress.php";
const TUTORIALS_TRACK = BASE_URL + "tutorials/track.php";

// Central de notificações (sino do cabeçalho)
const NOTIFICATIONS_POLL = BASE_URL + "notifications/poll.php";
const NOTIFICATIONS_LIST = BASE_URL + "notifications/list.php";
const NOTIFICATIONS_MARK_READ = BASE_URL + "notifications/mark_read.php";
const NOTIFICATIONS_MARK_ALL_READ = BASE_URL + "notifications/mark_all_read.php";

// Estatísticas v2: uma seção por chamada
const STATISTICS_V2_GET = BASE_URL + "statistics/get.php";
const STATISTICS_V2_GEOCODE = BASE_URL + "statistics/geocode.php";

// Google Business APIs
const GOOGLE_BUSINESS_GET = BASE_URL + "company/google-business/get.php";
const GOOGLE_BUSINESS_SET = BASE_URL + "company/google-business/set.php";

// Public Schedule Settings API
const PUBLIC_SCHEDULE_SETTINGS_UPDATE = BASE_URL + "company/public-schedule/update_settings.php";

// Company Message Templates APIs
const MESSAGE_TEMPLATES_GET = BASE_URL + "get_company_message_templates.php";
const MESSAGE_TEMPLATES_UPDATE = BASE_URL + "update_company_message_template.php";

// Anamnesis Templates APIs
const ANAMNESIS_TEMPLATES_GET = BASE_URL + "anamneses/get_templates.php";
const ANAMNESIS_TEMPLATES_SET = BASE_URL + "anamneses/set_template.php";
const ANAMNESIS_TEMPLATES_UPDATE = BASE_URL + "anamneses/update_template.php";
const ANAMNESIS_TEMPLATES_DELETE = BASE_URL + "anamneses/delete_template.php";

// Appointment Types (atendimentos modulares)
const APPOINTMENT_TYPES_GET = BASE_URL + "appointment-types/get_types.php";
const APPOINTMENT_TYPES_SET = BASE_URL + "appointment-types/set_type.php";
const APPOINTMENT_TYPES_UPDATE = BASE_URL + "appointment-types/update_type.php";
const APPOINTMENT_TYPES_DEACTIVATE = BASE_URL + "appointment-types/deactivate_type.php";
const APPOINTMENT_STEPS_GET = BASE_URL + "appointment-steps/get_steps.php";
const APPOINTMENT_STEPS_SET = BASE_URL + "appointment-steps/set_step.php";
const APPOINTMENT_STEPS_SET_ACTIVE = BASE_URL + "appointment-steps/set_step_active.php";
const APPOINTMENT_STEPS_REORDER = BASE_URL + "appointment-steps/reorder_steps.php";
const APPOINTMENT_STEPS_CHECK = BASE_URL + "appointment-steps/check_step.php";
const APPOINTMENT_STEPS_SET_STATUS_COLOR = BASE_URL + "appointment-steps/set_status_color.php";

// Patient Anamnesis APIs
const PATIENT_SET_ANAMNESE = BASE_URL + "anamneses/set_patient_anamnese.php";
const PATIENT_UPDATE_ANAMNESE = BASE_URL + "anamneses/update_patient_anamnese.php";
const PATIENT_DELETE_ANAMNESE = BASE_URL + "anamneses/delete_patient_anamnese.php";

// Wallet APIs
const WALLET_GET_PATIENT = BASE_URL + "wallet/get_patient_wallet.php";
const WALLET_GET_TRANSACTIONS = BASE_URL + "wallet/get_wallet_transactions.php";
const WALLET_PURCHASE_PACKAGE = BASE_URL + "packages/purchase_service_package.php";
const WALLET_DEDUCT_CREDITS = BASE_URL + "wallet/deduct_wallet_credits.php";

const PACKAGES_GET = BASE_URL + "packages/get_service_packages.php";
const PACKAGES_CREATE = BASE_URL + "packages/set_service_package.php";
const PACKAGES_UPDATE = BASE_URL + "packages/update_service_package.php";
const PACKAGES_DELETE = BASE_URL + "packages/delete_service_package.php";

// Financial APIs
const FINANCIAL_CATEGORIES_GET = BASE_URL + "get_financial_categories.php";
const FINANCIAL_CATEGORIES_SET = BASE_URL + "set_financial_category.php";
const FINANCIAL_CATEGORIES_UPDATE = BASE_URL + "update_financial_category.php";
const FINANCIAL_CATEGORIES_DELETE = BASE_URL + "delete_financial_category.php";

const FINANCIAL_TRANSACTIONS_GET = BASE_URL + "get_financial_transactions.php";
const FINANCIAL_TRANSACTIONS_SET = BASE_URL + "set_financial_transaction.php";
const FINANCIAL_TRANSACTIONS_UPDATE = BASE_URL + "update_financial_transaction.php";
const FINANCIAL_TRANSACTIONS_DELETE = BASE_URL + "delete_financial_transaction.php";
const FINANCIAL_TRANSACTIONS_GET_ACTIVE_RECURRING = BASE_URL + "get_active_recurring_transactions.php";

const FINANCIAL_DASHBOARD_GET = BASE_URL + "get_financial_dashboard.php";


const COMPANY_CONFIG_GET = BASE_URL + "get_company.php";
const COMPANY_UPDATE = BASE_URL + "update_company.php";
const COMPANY_CONFIG_UPDATE = BASE_URL + "update_company_config.php";

const DASHBOARD_GET = BASE_URL + "get_dashboard.php";
const RESOLVE_PUBLIC_APPOINTMENT_ACTION = BASE_URL + "appointment-confirmation/resolve.php";
const PREPARE_MANUAL_CONFIRMATION_MESSAGE = BASE_URL + "appointment-confirmation/prepare_manual_message.php";
const SEND_MANUAL_CONFIRMATION_REQUEST = BASE_URL + "appointment-confirmation/send_manual_request.php";
const APPOINTMENTS_KPI_GET = BASE_URL + "appointments/get_appointments_kpi.php";
const APPOINTMENTS_RECURRING_PREVIEW = BASE_URL + "appointments/preview_recurring_appointments.php";
const APPOINTMENTS_RECURRING_CREATE = BASE_URL + "appointments/create_recurring_appointments.php";
const APPOINTMENTS_NOTIFY_PATIENT_WHATSAPP = BASE_URL + "appointments/notify_patient_whatsapp.php";

const LEADS_GET = BASE_URL + "get_leads.php";
const LEADS_SET = BASE_URL + "set_lead.php";
const LEADS_UPDATE = BASE_URL + "update_lead.php";
const LEADS_FIND_BY_PHONE = BASE_URL + "leads/find_by_phone.php";
const LEADS_DELETE = BASE_URL + "leads/delete_lead.php";

// Marketing APIs
const CAMPAIGNS_GET = BASE_URL + "marketing/get_campaigns.php";
const CAMPAIGNS_SET = BASE_URL + "marketing/set_campaign.php";
const CAMPAIGNS_UPDATE = BASE_URL + "marketing/update_campaign.php";
const CAMPAIGNS_DELETE = BASE_URL + "marketing/delete_campaign.php";

// Marketing Mass Messaging APIs
const MASS_MESSAGING_RECIPIENTS_GET = BASE_URL + "marketing/mass-messaging/get_mass_messaging_recipients.php";
const MASS_MESSAGING_SEND = BASE_URL + "marketing/mass-messaging/send_mass_message.php";
const MASS_MESSAGING_PREDEFINED_QUERIES_GET = BASE_URL + "marketing/mass-messaging/queries/get_predefined_queries.php";
const MASS_MESSAGING_RECORD_HISTORY = BASE_URL + "marketing/mass-messaging/record_mass_message_history.php";
const MASS_MESSAGING_GET_HISTORY = BASE_URL + "marketing/mass-messaging/get_mass_message_history.php";
const MASS_MESSAGING_CHECK_RECIPIENT_HISTORY = BASE_URL + "marketing/mass-messaging/check_recipient_mass_message_history.php";

// Marketing Lead Sources APIs
const SOURCES_GET = BASE_URL + "marketing/get_sources.php";
const SOURCES_SET = BASE_URL + "marketing/set_source.php";
const SOURCES_UPDATE = BASE_URL + "marketing/update_source.php";
const SOURCES_DELETE = BASE_URL + "marketing/delete_source.php";
const CAMPAIGNS_BY_SOURCE_GET = BASE_URL + "marketing/get_campaigns_by_source.php";


const REPORTS_APPOINTMENTS_GET = BASE_URL + "get_reports_appointments.php";
const REPORTS_FINANCIAL_GET = BASE_URL + "get_reports_financial.php";

const APPOINTMENTS_TO_CONFIRM_GET = BASE_URL + "get_appointments_to_confirm.php"
const APPOINTMENTS_GET = BASE_URL + "get_appointments.php";
const APPOINTMENTS_SET = BASE_URL + "set_appointment.php";
const APPOINTMENTS_UPDATE = BASE_URL + "update_appointment.php";
const APPOINTMENTS_UPDATE_STATUS = BASE_URL + "update_appointment_status.php";
const APPOINTMENTS_DELETE = BASE_URL + "delete_appointment.php";

const APPOINTMENTS_WAITING_LIST_GET = BASE_URL + "waiting-list/get_waiting_list.php";
const APPOINTMENTS_WAITING_LIST_SET = BASE_URL + "waiting-list/set_waiting_list.php";
const APPOINTMENTS_WAITING_LIST_UPDATE = BASE_URL + "waiting-list/update_waiting_list.php";
const APPOINTMENTS_WAITING_LIST_DELETE = BASE_URL + "waiting-list/delete_waiting_list.php";
const APPOINTMENTS_WAITING_LIST_CHECK_AVAILABILITY = BASE_URL + "waiting-list/check_waiting_list_availability.php";
const APPOINTMENTS_WAITING_LIST_GET_OFFERS = BASE_URL + "waiting-list/get_offers.php";
const APPOINTMENTS_WAITING_LIST_SET_OFFER = BASE_URL + "waiting-list/set_offer.php";
const APPOINTMENTS_WAITING_LIST_UPDATE_OFFER = BASE_URL + "waiting-list/update_offer.php";

const APPOINTMENT_GET_FULLINFO = BASE_URL + "get_appointment_info.php";
const APPOINTMENT_CREATE_INVOICE = BASE_URL + "set_appointment_invoice.php";
const APPOINTMENT_GET_RECEIPT = BASE_URL + "get_receipt_from_appointment.php";
const APPOINTMENT_GET_INVOICE = BASE_URL + "get_invoice.php";

const INVOICE_UPDATE = BASE_URL + "update_invoice.php";
const INVOICE_UPDATE_NF_ISSUED = BASE_URL + "update_invoice_nf_issued.php";
const INVOICE_DELETE = BASE_URL + "delete_invoice.php";

const PATIENTS_GET = BASE_URL + "get_patients.php";
const PATIENT_GET = BASE_URL + "get_patient.php"
const PATIENT_HEALTH_GET = BASE_URL + "get_patient_health.php";
const PATIENT_SET = BASE_URL + "set_patient.php";
const PATIENT_UPDATE = BASE_URL + "update_patient.php";

const PATIENT_SET_HISTORY = BASE_URL + "set_patient_history.php";
const PATIENT_SET_NOTE = BASE_URL + "notes/set_patient_note.php";
const PATIENT_ANNUL_HISTORY = BASE_URL + "annul_patient_history_record.php";
const PATIENT_CREATE_REPLACEMENT_HISTORY = BASE_URL + "create_replacement_history_record.php";
const PATIENT_SET_COMPLAINTS = BASE_URL + "set_patient_complaint.php";
const PATIENT_SET_CRANIAL_ASYMMETRY = BASE_URL + "set_patient_cranial_asymmetry.php";
const PATIENT_UPDATE_CRANIAL_ASYMMETRY = BASE_URL + "update_patient_cranial_asymmetry.php";
const PATIENT_DELETE_CRANIAL_ASYMMETRY = BASE_URL + "delete_patient_cranial_asymmetry.php";
const PATIENT_SET_BODY_MEASUREMENTS = BASE_URL + "body-measurements/set_body_measurements.php";
const PATIENT_UPDATE_BODY_MEASUREMENTS = BASE_URL + "body-measurements/update_body_measurements.php";
const PATIENT_DELETE_BODY_MEASUREMENTS = BASE_URL + "body-measurements/delete_body_measurements.php";
const PATIENT_SET_BODY_MAP = BASE_URL + "body-map/set_body_map.php";
const PATIENT_SET_MEAL_PLAN = BASE_URL + "meal-plan/set_meal_plan.php";
const PATIENT_UPDATE_MEAL_PLAN = BASE_URL + "meal-plan/update_meal_plan.php";
const PATIENT_DELETE_MEAL_PLAN = BASE_URL + "meal-plan/delete_meal_plan.php";
const PATIENT_SET_APPLICATION_MAP = BASE_URL + "application-map/set_application_map.php";
const PATIENT_SET_SCALE_ASSESSMENT = BASE_URL + "scales/set_scale_assessment.php";
const PATIENT_SET_GROWTH_MEASUREMENT = BASE_URL + "growth/set_growth_measurement.php";
const PATIENT_DELETE_GROWTH_MEASUREMENT = BASE_URL + "growth/delete_growth_measurement.php";
const PATIENT_SET_MOTOR_MILESTONE = BASE_URL + "motor-milestones/set_motor_milestone.php";
const PATIENT_DELETE_MOTOR_MILESTONE = BASE_URL + "motor-milestones/delete_motor_milestone.php";
const PATIENT_SET_BIRTH_INFO = BASE_URL + "motor-milestones/set_birth_info.php";
const PATIENT_SET_THERMOGRAPHY = BASE_URL + "thermography/set_thermography.php";
const PATIENT_DELETE_THERMOGRAPHY = BASE_URL + "thermography/delete_thermography.php";
const PATIENT_SET_AIMS = BASE_URL + "aims/set_aims_assessment.php";
const PATIENT_DELETE_AIMS = BASE_URL + "aims/delete_aims_assessment.php";
const PATIENT_SET_PRESCRIPTION = BASE_URL + "prescription/set_prescription.php";
const PATIENT_DELETE_PRESCRIPTION = BASE_URL + "prescription/delete_prescription.php";
const COMPANY_SET_PRESCRIPTION_TEMPLATE = BASE_URL + "prescription/set_prescription_template.php";
const COMPANY_DELETE_PRESCRIPTION_TEMPLATE = BASE_URL + "prescription/delete_prescription_template.php";
const SEARCH_ICD = BASE_URL + "diagnoses/search_icd.php";
const PATIENT_SET_DIAGNOSIS = BASE_URL + "diagnoses/set_diagnosis.php";
const PATIENT_DELETE_DIAGNOSIS = BASE_URL + "diagnoses/delete_diagnosis.php";
const COMPANY_SET_APPLICATION_PRODUCTS = BASE_URL + "application-map/set_application_products.php";

const PATIENT_SET_DOCUMENT = BASE_URL + "set_patient_document.php";

// Anexos do paciente (exames, laudos e demais arquivos)
const PATIENT_ATTACHMENT_UPLOAD = BASE_URL + "patients/attachments/upload.php";
const PATIENT_ATTACHMENT_LIST = BASE_URL + "patients/attachments/list.php";
const PATIENT_ATTACHMENT_UPDATE = BASE_URL + "patients/attachments/update.php";
const PATIENT_ATTACHMENT_DELETE = BASE_URL + "patients/attachments/delete.php";
const PATIENT_ATTACHMENT_FILE = BASE_URL + "patients/attachments/get_file.php";
const PATIENT_REGISTRATION_LINK_CREATE = BASE_URL + "patients/registration_link/create.php";
const PATIENT_REGISTRATION_LINK_SEND_WHATSAPP = BASE_URL + "patients/registration_link/send_whatsapp.php";
const PATIENT_REGISTRATION_LINK_SET_ORIGIN = BASE_URL + "patients/registration_link/set_origin.php";
const APPOINTMENT_RESERVATION_CREATE = BASE_URL + "appointments/reservations/create.php";
const APPOINTMENT_RESERVATION_GET = BASE_URL + "appointments/reservations/get.php";
const APPOINTMENT_RESERVATION_RESEND = BASE_URL + "appointments/reservations/resend.php";
const APPOINTMENT_RESERVATION_NOTIFY_RELEASED = BASE_URL + "appointments/reservations/notify_released.php";
const PATIENT_EXPORT = BASE_URL + "patients/export.php";
const PATIENT_LIST_EXPORT = BASE_URL + "patients/export_list.php";
const PATIENT_ANONYMIZE = BASE_URL + "patients/anonymize.php";
const PATIENT_DELETE_PREVIEW = BASE_URL + "patients/delete_preview.php";
const PATIENT_DELETE = BASE_URL + "patients/delete.php";
const COMPANY_CLOSURE_REQUEST = BASE_URL + "company/closure/request.php";
const COMPANY_CLOSURE_REVERT = BASE_URL + "company/closure/revert.php";
const PATIENT_WHATSAPP_OPT_OUT = BASE_URL + "patients/whatsapp_opt_out.php";
const PATIENT_AUDIT_LOG = BASE_URL + "patients/audit_log.php";
const PATIENT_HISTORY = BASE_URL + "patients/history.php";
const APPOINTMENT_HISTORY = BASE_URL + "appointments/history.php";
const APPOINTMENTS_PATIENT_UPCOMING = BASE_URL + "appointments/patient_upcoming.php";
const APPOINTMENTS_MOVE_PAYMENT = BASE_URL + "appointments/move_payment.php";

const WHATSAPP_SEND_MESSAGE = BASE_URL + "whatsapp/send_message_whatsapp.php";

const WHATSAPP_GET_CHAT = BASE_URL + "get_message_chat_whatsapp.php";
const WHATSAPP_GET_CHAT_MESSAGES = BASE_URL + "get_messages_whatsapp.php";
const WHATSAPP_GET_CONTACT_CONTEXT = BASE_URL + "whatsapp/get_chat_contact_context.php";
const WHATSAPP_SUGGEST_REPLY = BASE_URL + "whatsapp/suggest_reply.php";
const WHATSAPP_SET_SUGGESTION_OUTCOME = BASE_URL + "whatsapp/set_suggestion_outcome.php";

// WhatsApp Configuration APIs
const WHATSAPP_CONFIG_GET = BASE_URL + "whatsapp/get_company_whatsapp_config.php";
const WHATSAPP_CONFIG_SET = BASE_URL + "whatsapp/set_company_whatsapp_config.php";
const WHATSAPP_CONFIG_TEST = BASE_URL + "whatsapp/test_company_whatsapp_config.php";
const WHATSAPP_DEVICE_INFO = BASE_URL + "whatsapp/get_whatsapp_device_info.php";
const WHATSAPP_GET_QRCODE = BASE_URL + "whatsapp/get_whatsapp_qrcode.php";
const WHATSAPP_GET_PHONECODE = BASE_URL + "whatsapp/get_whatsapp_phonecode.php";
const WHATSAPP_SEND_DOCUMENT = BASE_URL + "whatsapp/send_document_whatsapp.php";
const WHATSAPP_DISCONNECT = BASE_URL + "whatsapp/disconnect_whatsapp.php";
const WHATSAPP_SET_CHAT_READ = BASE_URL + "whatsapp/set_whatsapp_chat_read.php";
const WHATSAPP_SENDER_STATUS = BASE_URL + "whatsapp/get_sender_status.php";

// Export APIs
const EXPORT_CREATE = BASE_URL + "export/create.php";
const EXPORT_STATUS = BASE_URL + "export/status.php";
const EXPORT_HISTORY = BASE_URL + "export/get_history.php";
const EXPORT_CANCEL = BASE_URL + "export/cancel.php";
const EXPORT_ESTIMATE = BASE_URL + "export/estimate.php";
const EXPORT_BACKUP_STATUS = BASE_URL + "export/backup_status.php";


const TEMPLATES_GET_DOCUMENTS = BASE_URL + "get_company_documents_templates.php";
const TEMPLATES_SET_DOCUMENTS = BASE_URL + "set_company_documents_templates.php";
const TEMPLATES_UPDATE_DOCUMENTS = BASE_URL + "update_company_documents_templates.php";
const TEMPLATES_DELETE_DOCUMENTS = BASE_URL + "delete_company_documents_templates.php";

// AI Patient Response Engine (Simulador de Atendimento)
const AI_SIMULATOR_GENERATE_REPLY = BASE_URL + "ai-simulator/generate_reply.php";
const AI_SETTINGS_GET = BASE_URL + "ai-simulator/get_settings.php";
const AI_SETTINGS_SET = BASE_URL + "ai-simulator/set_settings.php";

// Recuperar atendimentos (beta do admin da plataforma)
const RECOVERY_GET_SUMMARY = BASE_URL + "intelligence/recovery/get_summary.php";
const RECOVERY_GET_OPPORTUNITIES = BASE_URL + "intelligence/recovery/get_opportunities.php";
const RECOVERY_GET_CANDIDATES = BASE_URL + "intelligence/recovery/get_candidates.php";
const RECOVERY_CONTACT = BASE_URL + "intelligence/recovery/contact.php";
const RECOVERY_COMPOSE_MESSAGE = BASE_URL + "intelligence/recovery/compose_message.php";
const RECOVERY_DISMISS = BASE_URL + "intelligence/recovery/dismiss.php";
const RECOVERY_GET_ACTIONS = BASE_URL + "intelligence/recovery/get_actions.php";
const RECOVERY_CLASSIFY_CHAT = BASE_URL + "intelligence/recovery/classify_chat.php";
// Cranial analysis tool (experimental, no persistence)
const TOOLS_CRANIAL_ANALYSIS_ANALYZE = BASE_URL + "tools/cranial-analysis/analyze.php";

// Automation Settings APIs
const AUTOMATION_SETTINGS_GET = BASE_URL + "company/automation_settings/get.php";
const AUTOMATION_SETTINGS_UPDATE = BASE_URL + "company/automation_settings/update.php";

const NPS_GET_RESPONSES = BASE_URL + "get_nps_responses.php";
const NPS_SET_RESPONSE = BASE_URL + "set_nps_response.php";
const NPS_UPDATE_GOOGLE_REVIEW = BASE_URL + "update_nps_google_review.php";
const NPS_PREPARE_MANUAL_MESSAGE = BASE_URL + "prepare_manual_nps_message.php";
const NPS_GOOGLE_REVIEW_SUGGESTIONS = BASE_URL + "nps/google_review_suggestions.php";

// Subscription endpoints
const SUBSCRIPTION_PLANS_GET = BASE_URL + "subscription/plans/get.php";
const SUBSCRIPTION_COMPANY_GET = BASE_URL + "subscription/company/get.php";
const SUBSCRIPTION_COMPANY_PAYMENT_HISTORY = BASE_URL + "subscription/company/payment_history.php";
const SUBSCRIPTION_COMPANY_UPDATE_PLAN = BASE_URL + "subscription/company/update_plan.php";
const SUBSCRIPTION_COMPANY_SIMULATE_PLAN_CHANGE = BASE_URL + "subscription/company/simulate_plan_change.php";
const SUBSCRIPTION_COMPANY_PLAN_FIT = BASE_URL + "subscription/company/plan_fit.php";
const SUBSCRIPTION_COMPANY_APPLY_PLAN_FIT = BASE_URL + "subscription/company/apply_plan_fit.php";
const SUBSCRIPTION_COMPANY_HISTORY = BASE_URL + "subscription/company/history.php";
const SUBSCRIPTION_COMPANY_CANCEL = BASE_URL + "subscription/company/cancel.php";
const SUBSCRIPTION_SPECIFIC_CANCEL = BASE_URL + "subscription/company/cancel_specific.php";

// Stripe endpoints
const STRIPE_CREATE_CHECKOUT_SESSION = BASE_URL + "subscription/stripe/create-checkout-session.php";
const STRIPE_CUSTOMER_PORTAL = BASE_URL + "subscription/stripe/customer-portal.php";
const STRIPE_GET_SESSION = BASE_URL + "subscription/stripe/get-session.php";
const STRIPE_GET_CONFIG = BASE_URL + "subscription/stripe/get-config.php";

// Terms and Privacy endpoints
const TERMS_ACCEPT = BASE_URL + "company/terms/accept.php";
const TERMS_CHECK = BASE_URL + "company/terms/check.php";
const TERMS_GET = BASE_URL + "company/terms/get.php";

// Beta Access endpoints
const BETA_ACCESS_SUBMIT = BASE_URL + "beta-access/submit.php";

// Rooms endpoints
const ROOMS_GET = BASE_URL + "rooms/get_company_rooms.php";
const ROOMS_SET = BASE_URL + "rooms/set_company_room.php";
const ROOMS_UPDATE = BASE_URL + "rooms/update_company_room.php";
const ROOMS_DELETE = BASE_URL + "rooms/delete_company_room.php";
const ROOMS_GET_OCCUPANCY = BASE_URL + "rooms/get_room_occupancy.php";
const ROOMS_GET_OCCUPANCY_BATCH = BASE_URL + "rooms/get_rooms_occupancy_batch.php";

// Convênios (docs/features/convenios): operadoras da empresa, carteirinhas, pedidos
// médicos, guias e documentos do agendamento
const INSURANCE_PROVIDERS_GET = BASE_URL + "insurance/get_providers.php";
const INSURANCE_PROVIDERS_SET = BASE_URL + "insurance/set_provider.php";
const INSURANCE_PROVIDERS_UPDATE = BASE_URL + "insurance/update_provider.php";
const INSURANCE_TUSS_SEARCH = BASE_URL + "insurance/procedures/search_tuss.php";
const INSURANCE_PROCEDURES_SET = BASE_URL + "insurance/procedures/set.php";
const INSURANCE_PROCEDURES_UPDATE = BASE_URL + "insurance/procedures/update.php";
const INSURANCE_PROCEDURES_COPY = BASE_URL + "insurance/procedures/copy.php";
const PATIENT_INSURANCE_LIST = BASE_URL + "patients/insurance/list.php";
const PATIENT_INSURANCE_SET = BASE_URL + "patients/insurance/set.php";
const PATIENT_INSURANCE_UPDATE = BASE_URL + "patients/insurance/update.php";
const PATIENT_INSURANCE_DOCUMENTS_LIST = BASE_URL + "patients/insurance/documents/list.php";
const PATIENT_MEDICAL_REQUEST_SET = BASE_URL + "patients/insurance/documents/set_request.php";
const PATIENT_MEDICAL_REQUEST_UPDATE = BASE_URL + "patients/insurance/documents/update_request.php";
const PATIENT_AUTHORIZATION_SET = BASE_URL + "patients/insurance/documents/set_authorization.php";
const PATIENT_AUTHORIZATION_UPDATE = BASE_URL + "patients/insurance/documents/update_authorization.php";
const APPOINTMENT_INSURANCE_DOCUMENTS_LIST = BASE_URL + "appointments/insurance_documents/list.php";
const APPOINTMENT_INSURANCE_DOCUMENTS_LINK = BASE_URL + "appointments/insurance_documents/link.php";
const APPOINTMENT_INSURANCE_DOCUMENTS_UNLINK = BASE_URL + "appointments/insurance_documents/unlink.php";
const APPOINTMENT_INSURANCE_UPCOMING_REQUEST = BASE_URL + "appointments/insurance_documents/upcoming_request.php";
const APPOINTMENT_INSURANCE_LINK_UPCOMING_REQUEST = BASE_URL + "appointments/insurance_documents/link_upcoming_request.php";
const APPOINTMENT_INSURANCE_PENDING_LIST = BASE_URL + "appointments/insurance_pending/list.php";
// Sala do dia (RECEP-01): sala de cada agenda por dia e turno
const ROOMS_DAY_GET = BASE_URL + "rooms/get_day_rooms.php";
const ROOMS_DAY_SET = BASE_URL + "rooms/set_day_room.php";

// Reschedule APIs
const RESCHEDULE_GET_AVAILABLE_SLOTS = BASE_URL + "reschedule/get_available_slots.php";

// Nota: APIs fiscais (NFS-e) estão em src/api/FiscalAPI.js


// Anamnesis Templates APIs
const GetAnamnesisTemplatesAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(ANAMNESIS_TEMPLATES_GET, json, callback);
}

const SetAnamnesisTemplateAPI = (companyId, data, callback) => {
    let json = "companyId=" + companyId +
        "&label=" + encodeURIComponent(data.label) +
        "&questions=" + encodeURIComponent(JSON.stringify(data.questions));
    CallPOSTAPI(ANAMNESIS_TEMPLATES_SET, json, callback);
}

const UpdateAnamnesisTemplateAPI = (data, callback) => {
    let json = "id=" + data.id +
        "&label=" + encodeURIComponent(data.label) +
        "&questions=" + encodeURIComponent(JSON.stringify(data.questions));
    CallPOSTAPI(ANAMNESIS_TEMPLATES_UPDATE, json, callback);
}

const DeleteAnamnesisTemplateAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(ANAMNESIS_TEMPLATES_DELETE, json, callback);
}

// Appointment Types APIs (atendimentos modulares)
const GetAppointmentTypesAPI = (callback) => {
    CallPOSTAPI(APPOINTMENT_TYPES_GET, "", callback);
}

const appointmentTypeBody = (data) => {
    let json = "label=" + encodeURIComponent(data.label) +
        "&modules=" + encodeURIComponent(JSON.stringify(data.modules)) +
        "&isDefault=" + (data.isDefault ? "1" : "0");
    if (data.config) {
        json += "&config=" + encodeURIComponent(JSON.stringify(data.config));
    }
    return json;
}

const SetAppointmentTypeAPI = (data, callback) => {
    CallPOSTAPI(APPOINTMENT_TYPES_SET, appointmentTypeBody(data), callback);
}

const UpdateAppointmentTypeAPI = (data, callback) => {
    let json = "id=" + data.id + "&" + appointmentTypeBody(data);
    CallPOSTAPI(APPOINTMENT_TYPES_UPDATE, json, callback);
}

const DeactivateAppointmentTypeAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(APPOINTMENT_TYPES_DEACTIVATE, json, callback);
}

// "Exibir inativos": a mesma leitura, com os desativados à parte em response.inactive
const GetInactiveAppointmentTypesAPI = (callback) => {
    CallPOSTAPI(APPOINTMENT_TYPES_GET, "include_inactive=1", callback);
}

const ReactivateAppointmentTypeAPI = (id, callback) => {
    let json = "id=" + id + "&reactivate=1";
    CallPOSTAPI(APPOINTMENT_TYPES_UPDATE, json, callback);
}

// Etapas do atendimento (docs/features/appointment-steps)
const GetAppointmentStepsAPI = (includeInactive, callback) => {
    CallPOSTAPI(APPOINTMENT_STEPS_GET, includeInactive ? "include_inactive=1" : "", callback);
}

const SetAppointmentStepAPI = (data, callback) => {
    let json = "name=" + encodeURIComponent(data.name) +
        "&isRequired=" + (data.isRequired ? "1" : "0") +
        "&color=" + encodeURIComponent(data.color || "");
    json += data.id ? "&id=" + data.id : "&status=" + encodeURIComponent(data.status);
    CallPOSTAPI(APPOINTMENT_STEPS_SET, json, callback);
}

const SetAppointmentStepActiveAPI = (id, active, callback) => {
    CallPOSTAPI(APPOINTMENT_STEPS_SET_ACTIVE, "id=" + id + "&active=" + (active ? "1" : "0"), callback);
}

const ReorderAppointmentStepsAPI = (status, ids, callback) => {
    let json = "status=" + encodeURIComponent(status) + "&ids=" + encodeURIComponent(JSON.stringify(ids));
    CallPOSTAPI(APPOINTMENT_STEPS_REORDER, json, callback);
}

const SetAppointmentStatusColorAPI = (status, color, callback) => {
    let json = "status=" + encodeURIComponent(status) + "&color=" + encodeURIComponent(color || "");
    CallPOSTAPI(APPOINTMENT_STEPS_SET_STATUS_COLOR, json, callback);
}

const CheckAppointmentStepAPI = (appointmentId, stepId, checked, callback) => {
    let json = "appointmentId=" + appointmentId + "&stepId=" + stepId + "&checked=" + (checked ? "1" : "0");
    CallPOSTAPI(APPOINTMENT_STEPS_CHECK, json, callback);
}

// Wallet APIs
const GetPatientWalletAPI = (patientId, callback) => {
    const json = "patient_id=" + patientId;
    CallPOSTAPI(WALLET_GET_PATIENT, json, callback);
};

const GetWalletTransactionsAPI = (walletId, startDate, endDate, callback) => {
    const json = "wallet_id=" + walletId +
        "&start_date=" + startDate +
        "&end_date=" + endDate;
    CallPOSTAPI(WALLET_GET_TRANSACTIONS, json, callback);
};

const DeductWalletCreditsAPI = (walletId, procedureId, description, createdBy, callback) => {
    const json = "wallet_id=" + walletId +
        "&type=SERVICE" +
        "&procedure_id=" + procedureId +
        "&service_credits=1" +
        "&description=" + encodeURIComponent(description) +
        "&created_by=" + createdBy;
    CallPOSTAPI(WALLET_DEDUCT_CREDITS, json, callback);
};

const GetServicePackagesAPI = (companyId, callback) => {
    const json = "company_id=" + companyId;
    CallPOSTAPI(PACKAGES_GET, json, callback);
};



const PurchaseServicePackageAPI = (walletId, packageId, paymentMethod, paymentStatus,createdBy, callback) => {
    const json = "wallet_id=" + walletId +
        "&package_id=" + packageId +
        "&payment_method=" + paymentMethod +
        "&payment_status=" + paymentStatus +
        "&created_by=" + createdBy;
        
    CallPOSTAPI(WALLET_PURCHASE_PACKAGE, json, callback);
};

// Gera a resposta sugerida pela AI Patient Response Engine.
// messages: [{ role: 'patient' | 'ai', content: string }, ...]
// knowledge: texto livre com FAQs/políticas/instruções da clínica (opcional)
const GenerateAISimulatorReplyAPI = (messages, knowledge, callback) => {
    CallPOSTAPIWithJSON(AI_SIMULATOR_GENERATE_REPLY, { messages, knowledge: knowledge || "" }, callback);
}

// Configuração da atendente virtual (AI Patient Response Engine)
const GetAISettingsAPI = (callback) => {
    CallPOSTAPIWithJSON(AI_SETTINGS_GET, {}, callback);
}

const SetAISettingsAPI = (settings, callback) => {
    CallPOSTAPIWithJSON(AI_SETTINGS_SET, settings, callback);
}

// Recuperar atendimentos: contagem para o card do Início (intervalos livres e pessoas para chamar)
const GetRecoverySummaryAPI = (days, callback) => {
    CallGETAPI(RECOVERY_GET_SUMMARY + "?days=" + (days || 7), callback);
}

// Intervalos livres por dia e profissional; 0 em professionalId/procedureId significa todos
const GetRecoveryOpportunitiesAPI = (days, professionalId, procedureId, callback) => {
    CallGETAPI(RECOVERY_GET_OPPORTUNITIES + "?days=" + (days || 7) + "&professionalId=" + (professionalId || 0) + "&procedureId=" + (procedureId || 0), callback);
}

// Pessoas para chamar num intervalo livre: { professionalId, date, start, end }
const GetRecoveryCandidatesAPI = (interval, callback) => {
    CallGETAPI(RECOVERY_GET_CANDIDATES + "?professionalId=" + (interval.professionalId || 0) + "&date=" + encodeURIComponent(interval.date) + "&start=" + encodeURIComponent(interval.start) + "&end=" + encodeURIComponent(interval.end), callback);
}

// Texto do convite personalizado pela IA (ou o modelo, se a IA não responder): { professionalId, date, time, minutes, patientId, procedureId }
const ComposeRecoveryMessageAPI = (data, callback) => {
    CallPOSTAPIWithJSON(RECOVERY_COMPOSE_MESSAGE, data, callback);
}

// Envia o convite pelo WhatsApp (fila): { professionalId, date, time, minutes, patientId, procedureId, message, reasons, messageSource }
const SendRecoveryContactAPI = (data, callback) => {
    CallPOSTAPIWithJSON(RECOVERY_CONTACT, data, callback);
}

// "Agora não": { patientId, scope: 'patient' } (30 dias) ou { patientId, scope: 'slot', professionalId, date, time }
const DismissRecoveryCandidateAPI = (data, callback) => {
    CallPOSTAPIWithJSON(RECOVERY_DISMISS, data, callback);
}

// Ações dos últimos N dias com status real e retorno atribuído
const GetRecoveryActionsAPI = (days, callback) => {
    CallGETAPI(RECOVERY_GET_ACTIONS + "?days=" + (days || 30), callback);
}

// Lê a última conversa de WhatsApp de até 3 pessoas e classifica a intenção (IA, identificadores mascarados)
const ClassifyRecoveryChatsAPI = (patientIds, callback) => {
    CallPOSTAPIWithJSON(RECOVERY_CLASSIFY_CHAT, { patientIds }, callback);
}

// Progresso do usuário logado nas lições dos tutoriais
const GetTutorialProgressAPI = (callback) => {
    CallGETAPI(TUTORIALS_GET_PROGRESS, callback);
}

// Evento numa lição: { lesson_id, event: open|progress|complete|watched|unwatched|helpful, ... }
const TrackTutorialProgressAPI = (data, callback) => {
    CallPOSTAPIWithJSON(TUTORIALS_TRACK, data, callback);
}

// Sino: resumo + avisos abertos; "since" é o stamp da chamada anterior (sem mudança, vem só o resumo)
const PollNotificationsAPI = (since, callback) => {
    const query = since ? "?since=" + encodeURIComponent(since) : "";
    CallGETAPI(NOTIFICATIONS_POLL + query, callback);
}

// Página de notificações: filter = all | unread | action | resolved
const ListNotificationsAPI = (filter, page, perPage, callback) => {
    CallGETAPI(NOTIFICATIONS_LIST + "?filter=" + (filter || "all") + "&page=" + (page || 1) + "&per_page=" + (perPage || 30), callback);
}

// Estatísticas v2: section = overview | schedule | patients | financial | leads | satisfaction
const GetClinicStatsAPI = (section, startDate, endDate, compare, callback) => {
    CallGETAPI(STATISTICS_V2_GET + "?section=" + section + "&start=" + startDate + "&end=" + endDate + "&compare=" + compare, callback);
}

const LocateClinicStatsDistrictsAPI = (startDate, endDate, callback) => {
    CallGETAPI(STATISTICS_V2_GEOCODE + "?start=" + startDate + "&end=" + endDate, callback);
}

const MarkNotificationsReadAPI = (ids, callback) => {
    CallPOSTAPIWithJSON(NOTIFICATIONS_MARK_READ, { ids }, callback);
}

const MarkAllNotificationsReadAPI = (callback) => {
    CallPOSTAPIWithJSON(NOTIFICATIONS_MARK_ALL_READ, {}, callback);
}

const GetTemplatesDocumentsAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(TEMPLATES_GET_DOCUMENTS, json, callback);
}

const SetTemplatesDocumentsAPI = (companyId, createdBy, data, callback) => {

    let json = "companyId=" + companyId +
        "&createdBy=" + createdBy +
        "&type=" + data.type +
        "&tag=" + data.tag +
        "&title=" + data.title +
        "&text=" + data.text;

    CallPOSTAPI(TEMPLATES_SET_DOCUMENTS, json, callback);
}

const UpdateTemplatesDocumentsAPI = (companyId, updatedBy, data, callback) => {

    let json = "companyId=" + companyId +
        "&id=" + data.id +
        "&updatedBy=" + updatedBy +
        "&type=" + data.type +
        "&tag=" + data.tag +
        "&title=" + data.title +
        "&text=" + data.text;

    CallPOSTAPI(TEMPLATES_UPDATE_DOCUMENTS, json, callback);
}

const DeleteTemplatesDocumentsAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(TEMPLATES_DELETE_DOCUMENTS, json, callback);
}

const GetWhatsappChatsAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(WHATSAPP_GET_CHAT, json, callback);
}

// options: { sinceId, beforeId, limit } — cursores de paginação/polling (opcional)
const GetWhatsappChatMessagesAPI = (companyId, chatId, options, callback) => {
    // Compatibilidade: chamada antiga sem options (companyId, chatId, callback)
    if (typeof options === 'function') {
        callback = options;
        options = {};
    }
    options = options || {};

    let json = "companyId=" + encodeURIComponent(companyId) + "&chatId=" + encodeURIComponent(chatId);
    if (options.sinceId != null) json += "&sinceId=" + encodeURIComponent(options.sinceId);
    if (options.beforeId != null) json += "&beforeId=" + encodeURIComponent(options.beforeId);
    if (options.limit != null) json += "&limit=" + encodeURIComponent(options.limit);

    CallPOSTAPI(WHATSAPP_GET_CHAT_MESSAGES, json, callback);
}

const SendWhatsappMessageAPI = (companyId, to, text, delay, callback) => {
    let json = "phone=" + encodeURIComponent(to)
    +"&company_id=" + encodeURIComponent(companyId)
    +"&message=" + encodeURIComponent(text)
    +"&delay=" + encodeURIComponent(delay);
    CallPOSTAPI(WHATSAPP_SEND_MESSAGE, json, callback);
}

// Contexto do contato (paciente, lead ou sem vínculo) pelo telefone da conversa
const GetWhatsappContactContextAPI = (companyId, phone, callback) => {
    let json = "companyId=" + encodeURIComponent(companyId)
    + "&phone=" + encodeURIComponent(phone);
    CallPOSTAPI(WHATSAPP_GET_CONTACT_CONTEXT, json, callback);
}

// Sugestão de resposta por IA para a conversa (copiloto): o histórico é lido
// no servidor pela chave da conversa; o telefone resolve o vínculo do contato.
const GetWhatsappAISuggestionAPI = (chatKey, phone, callback) => {
    CallPOSTAPIWithJSON(WHATSAPP_SUGGEST_REPLY, { chatKey, phone: phone || "" }, callback);
}

// Desfecho da sugestão: sent | edited | discarded (sentText só em edited)
const SetWhatsappAISuggestionOutcomeAPI = (suggestionId, outcome, sentText, callback) => {
    CallPOSTAPIWithJSON(WHATSAPP_SET_SUGGESTION_OUTCOME, { suggestionId, outcome, sentText: sentText || "" }, callback);
}

const SetWhatsappChatReadAPI = (companyId, chatId, callback) => {
    let json = "company_id=" + encodeURIComponent(companyId)
    +"&chatId=" + encodeURIComponent(chatId);
    CallPOSTAPI(WHATSAPP_SET_CHAT_READ, json, callback);
}

const GetWhatsappConfigAPI = (companyId, callback) => {
    let json = "company_id=" + companyId;
    CallPOSTAPI(WHATSAPP_CONFIG_GET, json, callback);
}

const SetWhatsappConfigAPI = (companyId, instanceId, instanceToken, securityToken, isActive, saveMessages, callback) => {
    const formData = new FormData();
    formData.append('company_id', companyId);
    formData.append('instance_id', instanceId);
    // Se um token vier vazio/null, não envia o campo: o backend mantém o token já salvo
    // (set_company_whatsapp_config.php não apaga instance_token/security_token quando o campo está ausente/vazio).
    if (instanceToken) {
        formData.append('instance_token', instanceToken);
    }
    if (securityToken) {
        formData.append('security_token', securityToken);
    }
    formData.append('is_active', isActive ? '1' : '0');
    formData.append('save_messages', saveMessages ? '1' : '0');

    CallPOSTAPIWithFormData(WHATSAPP_CONFIG_SET, formData, callback);
}

const GetWhatsappSenderStatusAPI = (companyId, callback) => {
    let json = "company_id=" + companyId;
    CallPOSTAPI(WHATSAPP_SENDER_STATUS, json, callback);
}

// Envia somente os toggles definidos (use_own_number / allow_platform_fallback). Nunca envia
// instance_id, instance_token, is_active ou save_messages: o backend mantém os valores já
// salvos quando esses campos vêm ausentes do request (set_company_whatsapp_config.php).
const SetWhatsappSenderTogglesAPI = (companyId, { useOwnNumber, allowPlatformFallback } = {}, callback) => {
    const formData = new FormData();
    formData.append('company_id', companyId);
    if (useOwnNumber !== undefined && useOwnNumber !== null) {
        formData.append('use_own_number', useOwnNumber ? '1' : '0');
    }
    if (allowPlatformFallback !== undefined && allowPlatformFallback !== null) {
        formData.append('allow_platform_fallback', allowPlatformFallback ? '1' : '0');
    }

    CallPOSTAPIWithFormData(WHATSAPP_CONFIG_SET, formData, callback);
}

const TestWhatsappConfigAPI = (companyId, callback) => {
    const formData = new FormData();
    formData.append('company_id', companyId);
    
    CallPOSTAPIWithFormData(WHATSAPP_CONFIG_TEST, formData, callback);
}

const GetWhatsappDeviceInfoAPI = (companyId, callback) => {
    let json = "company_id=" + companyId;
    CallPOSTAPI(WHATSAPP_DEVICE_INFO, json, callback);
}

const GetWhatsappQRCodeAPI = (companyId, callback) => {
    let json = "company_id=" + companyId;
    CallPOSTAPI(WHATSAPP_GET_QRCODE, json, callback);
}

const GetWhatsappPhoneCodeAPI = (companyId, phone, callback) => {
    let json = "company_id=" + companyId + "&phone=" + phone;
    CallPOSTAPI(WHATSAPP_GET_PHONECODE, json, callback);
}

const DisconnectWhatsappAPI = (companyId, callback) => {
    let json = "company_id=" + companyId;
    CallPOSTAPI(WHATSAPP_DISCONNECT, json, callback);
}

// Function to call API endpoints with JSON payload
const CallPOSTAPIWithJSON = (path, data, callback) => {
    const requestEmail = data && data.email; // captured before the response `data` shadows it below

    // Lê o token a cada tentativa: a repetição após o 401 usa o token renovado
    const doFetch = () => fetch(path, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${getAccessToken()}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
    });

    doFetch()
        .then(response => (response.status === 401 ? retryWithRefreshedToken(doFetch) : response))
        .then(response => {
            if (!response) {
                Disconnect();
                // Segue pelo fluxo normal para o callback receber o erro uma única vez
                return JSON.stringify(sessionExpiredError());
            }
            return response.text();
        })
        .then(data => {
            try {
                if (data) {
                    const jsonData = JSON.parse(data);
                    if (jsonData.code === 'EMAIL_VERIFICATION_REQUIRED') {
                        HandleEmailVerificationRequired(requestEmail);
                        return;
                    }
                    callback(jsonData);
                } else {
                    callback({ error: 'Resposta vazia do servidor' });
                }
            } catch (e) {
                callback({ error: 'Erro ao processar resposta do servidor' });
            }
        })
        .catch(error => {
            callback({ error: error.message || 'Erro de conexão' });
        });
}

const SendWhatsappDocumentAPI = (companyId, phone, document, fileName, caption, countryCode, callback) => {
    const data = {
        company_id: companyId,
        phone: phone,
        countryCode: countryCode || '',
        document: document,
        fileName: fileName
    };
    if (caption) data.caption = caption;

    CallPOSTAPIWithJSON(WHATSAPP_SEND_DOCUMENT, data, callback);
}

// Sem pagamento o endpoint responde 200 com { invoice: null, patient: null } (nunca 404)
const GetInvoiceAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(APPOINTMENT_GET_INVOICE, json, callback);
}

const UpdateInvoiceAPI = (invoiceId, price, paymentMethod, issueDocument, updatedBy, callback) => {
    let json = "id=" + invoiceId + "&price=" + price + "&paymentMethod=" + paymentMethod + "&issueDocument=" + issueDocument + "&updatedBy=" + updatedBy;
    CallPOSTAPI(INVOICE_UPDATE, json, callback);
}

const UpdateInvoiceNfIssuedAPI = (invoiceId, issuedDocument, updatedBy, callback) => {
    let json = "id=" + invoiceId +  "&issuedDocument=" + issuedDocument + "&updatedBy=" + updatedBy;
    CallPOSTAPI(INVOICE_UPDATE_NF_ISSUED, json, callback);
}

const DeleteInvoiceAPI = (companyId, invoiceId, callback) => {
    let json = "companyId=" + companyId + "&invoiceId=" + invoiceId;
    CallPOSTAPI(INVOICE_DELETE, json, callback);
}

const GetAppointmentFullInfo = (id, callback, errorCallback) => {
    let json = "id=" + id;
    CallPOSTAPI(APPOINTMENT_GET_FULLINFO, json, callback, false, errorCallback);
}

const DeletePatientAnamneseAPI = (id, deletedBy, callback) => {
    let json = "id=" + id +
        "&deletedBy=" + deletedBy;

    CallPOSTAPI(PATIENT_DELETE_ANAMNESE, json, callback);
}

const UpdatePatientAnamneseAPI = (id, patientId, anamnese, updatedBy, callback) => {
    let json = "id=" + id +
        "&patientId=" + patientId +
        "&anamnese=" + JSON.stringify(anamnese) +
        "&updatedBy=" + updatedBy;

    CallPOSTAPI(PATIENT_UPDATE_ANAMNESE, json, callback);
}

const SetPatientAnamneseAPI = (date, createdBy, patientId, appointmentId, anamnese, template, callback) => {
    let json = "appointmentId=" + appointmentId +
        "&createdAt=" + date +
        "&patientId=" + patientId +
        "&createdBy=" + createdBy +
        "&anamnese=" + JSON.stringify(anamnese) +
        "&template=" + template;

    CallPOSTAPI(PATIENT_SET_ANAMNESE, json, callback);
}

const SetPatientComplaints = (date, patientId, appointmentId, complaints, callback) => {
    let json = "appointmentId=" + appointmentId +
        "&createdAt=" + date +
        "&patientId=" + patientId +
        "&complaints=" + JSON.stringify(complaints);

    CallPOSTAPI(PATIENT_SET_COMPLAINTS, json, callback);
}

const SetPatientDocumentAPI = (patientId, date, appointmentId, title, description, createdBy, callback) => {

    let json = "appointmentId=" + appointmentId +
        "&date=" + date +
        "&patientId=" + patientId +
        "&title=" + title +
        "&description=" + description +
        "&createdBy=" + createdBy;

    CallPOSTAPI(PATIENT_SET_DOCUMENT, json, callback);
}

const SetPatientHistoryAPI = (date, createdBy, patientId, appointmentId, assessment, evolution, callback) => {

    let json = "appointmentId=" + appointmentId +
        "&createdAt=" + date +
        "&patientId=" + patientId +
        "&assessment=" + assessment +
        "&evolution=" + evolution +
        "&createdBy=" + createdBy;

    CallPOSTAPI(PATIENT_SET_HISTORY, json, callback);
}

// Anotação do atendimento (módulo "Anotações"): cria ou substitui a anotação da consulta
const SetPatientNoteAPI = (patientId, appointmentId, note, createdBy, callback) => {
    let json = "patientId=" + patientId +
        "&appointmentId=" + appointmentId +
        "&note=" + encodeURIComponent(note || '') +
        "&createdBy=" + createdBy;

    CallPOSTAPI(PATIENT_SET_NOTE, json, callback);
}

const AnnulPatientHistoryRecordAPI = (recordId, patientId, annulledBy, annulReason, callback) => {
    let json = "recordId=" + recordId +
        "&patientId=" + patientId +
        "&annulledBy=" + annulledBy;
    
    if (annulReason) {
        json += "&annulReason=" + encodeURIComponent(annulReason);
    }

    CallPOSTAPI(PATIENT_ANNUL_HISTORY, json, callback);
}

const CreateReplacementHistoryRecordAPI = (originalRecordId, patientId, assessment, evolution, createdBy, callback) => {
    let json = "originalRecordId=" + originalRecordId +
        "&patientId=" + patientId +
        "&assessment=" + encodeURIComponent(assessment) +
        "&evolution=" + encodeURIComponent(evolution) +
        "&createdBy=" + createdBy;

    CallPOSTAPI(PATIENT_CREATE_REPLACEMENT_HISTORY, json, callback);
}

const SetPatientCranialAsymmetryAPI = (date, createdBy, patientId, appointmentId, ap, bp, dd, de, callback) => {
    let json = "appointmentId=" + appointmentId +
        "&createdAt=" + date +
        "&patientId=" + patientId +
        "&createdBy=" + createdBy +
        "&ap=" + ap +
        "&bp=" + bp +
        "&dd=" + dd +
        "&de=" + de;

    CallPOSTAPI(PATIENT_SET_CRANIAL_ASYMMETRY, json, callback);
}

const UpdatePatientCranialAsymmetryAPI = (id, date, patientId, appointmentId, ap, bp, dd, de, callback) => {
    let json = "id=" + id +
        "&appointmentId=" + appointmentId +
        "&createdAt=" + date +
        "&patientId=" + patientId +
        "&ap=" + ap +
        "&bp=" + bp +
        "&dd=" + dd +
        "&de=" + de;

    CallPOSTAPI(PATIENT_UPDATE_CRANIAL_ASYMMETRY, json, callback);
}

const DeletePatientCranialAsymmetryAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(PATIENT_DELETE_CRANIAL_ASYMMETRY, json, callback);
}

// Medidas corporais: `data` = { measuredAt, measurements: {chave: valor}, bodyFatSource, notes }
const buildBodyMeasurementsBody = (data) =>
    "&measuredAt=" + encodeURIComponent(data.measuredAt || '') +
    "&measurements=" + encodeURIComponent(JSON.stringify(data.measurements || {})) +
    "&bodyFatSource=" + encodeURIComponent(data.bodyFatSource || '') +
    "&skinfoldProtocol=" + encodeURIComponent(data.skinfoldProtocol || '') +
    "&notes=" + encodeURIComponent(data.notes || '');

// Cria a avaliação do atendimento (ou substitui a já existente para ele)
const SetPatientBodyMeasurementsAPI = (patientId, appointmentId, data, createdBy, callback) => {
    let json = "patientId=" + patientId +
        "&appointmentId=" + appointmentId +
        "&createdBy=" + createdBy +
        buildBodyMeasurementsBody(data);

    CallPOSTAPI(PATIENT_SET_BODY_MEASUREMENTS, json, callback);
}

const UpdatePatientBodyMeasurementsAPI = (id, data, updatedBy, callback) => {
    let json = "id=" + id +
        "&updatedBy=" + updatedBy +
        buildBodyMeasurementsBody(data);

    CallPOSTAPI(PATIENT_UPDATE_BODY_MEASUREMENTS, json, callback);
}

const DeletePatientBodyMeasurementsAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(PATIENT_DELETE_BODY_MEASUREMENTS, json, callback);
}

// Mapa corporal: substitui os marcadores do atendimento (lista vazia apaga o registro)
const SetPatientBodyMapAPI = (patientId, appointmentId, markers, createdBy, callback) => {
    let json = "patientId=" + patientId +
        "&appointmentId=" + appointmentId +
        "&createdBy=" + createdBy +
        "&markers=" + encodeURIComponent(JSON.stringify(markers || []));

    CallPOSTAPI(PATIENT_SET_BODY_MAP, json, callback);
}

// Escalas clínicas: grava a resposta de uma escala no atendimento (valor nulo apaga o registro)
const SetPatientScaleAssessmentAPI = (patientId, appointmentId, scaleKey, value, createdBy, callback) => {
    let json = "patientId=" + patientId +
        "&appointmentId=" + appointmentId +
        "&scaleKey=" + encodeURIComponent(scaleKey) +
        "&createdBy=" + createdBy +
        "&value=" + (value ? encodeURIComponent(JSON.stringify(value)) : '');

    CallPOSTAPI(PATIENT_SET_SCALE_ASSESSMENT, json, callback);
}

// Curva de crescimento: cria uma medição no atendimento ou, com data.id, altera uma existente
const SetPatientGrowthMeasurementAPI = (patientId, appointmentId, data, createdBy, callback) => {
    let json = "patientId=" + patientId +
        "&appointmentId=" + appointmentId +
        "&createdBy=" + createdBy +
        (data.id ? "&id=" + data.id : "") +
        "&measuredAt=" + encodeURIComponent(data.measuredAt || '') +
        "&weight=" + encodeURIComponent(data.weight ?? '') +
        "&height=" + encodeURIComponent(data.height ?? '') +
        "&headCircumference=" + encodeURIComponent(data.headCircumference ?? '') +
        "&heightPosition=" + encodeURIComponent(data.heightPosition || '');

    CallPOSTAPI(PATIENT_SET_GROWTH_MEASUREMENT, json, callback);
}

const DeletePatientGrowthMeasurementAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(PATIENT_DELETE_GROWTH_MEASUREMENT, json, callback);
}

// Marcos motores: registra (ou altera, se o marco já existe) a data da conquista
const SetPatientMotorMilestoneAPI = (patientId, appointmentId, data, createdBy, callback) => {
    let json = "patientId=" + patientId +
        "&appointmentId=" + appointmentId +
        "&createdBy=" + createdBy +
        "&milestoneKey=" + encodeURIComponent(data.milestoneKey || '') +
        "&achievedAt=" + encodeURIComponent(data.achievedAt || '') +
        "&note=" + encodeURIComponent(data.note || '');

    CallPOSTAPI(PATIENT_SET_MOTOR_MILESTONE, json, callback);
}

const DeletePatientMotorMilestoneAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(PATIENT_DELETE_MOTOR_MILESTONE, json, callback);
}

// Termografia: grava (ou altera, se o atendimento já tem) a avaliação termográfica
const SetPatientThermographyAPI = (patientId, appointmentId, data, createdBy, callback) => {
    let json = "patientId=" + patientId +
        "&appointmentId=" + appointmentId +
        "&createdBy=" + createdBy +
        "&readings=" + encodeURIComponent(JSON.stringify(data.readings || [])) +
        "&measuredAt=" + encodeURIComponent(data.measuredAt || '') +
        "&ambientTemp=" + encodeURIComponent(data.ambientTemp ?? '') +
        "&notes=" + encodeURIComponent(data.notes || '');

    CallPOSTAPI(PATIENT_SET_THERMOGRAPHY, json, callback);
}

const DeletePatientThermographyAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(PATIENT_DELETE_THERMOGRAPHY, json, callback);
}

// Escala motora de Alberta: grava (ou altera, se o atendimento já tem) a avaliação
const SetPatientAimsAssessmentAPI = (patientId, appointmentId, data, createdBy, callback) => {
    let json = "patientId=" + patientId +
        "&appointmentId=" + appointmentId +
        "&createdBy=" + createdBy +
        "&assessedAt=" + encodeURIComponent(data.assessedAt || '') +
        "&proneScore=" + encodeURIComponent(data.proneScore ?? '') +
        "&supineScore=" + encodeURIComponent(data.supineScore ?? '') +
        "&sittingScore=" + encodeURIComponent(data.sittingScore ?? '') +
        "&standingScore=" + encodeURIComponent(data.standingScore ?? '') +
        "&percentile=" + encodeURIComponent(data.percentile ?? '') +
        "&notes=" + encodeURIComponent(data.notes || '');

    CallPOSTAPI(PATIENT_SET_AIMS, json, callback);
}

const DeletePatientAimsAssessmentAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(PATIENT_DELETE_AIMS, json, callback);
}

// Idade gestacional ao nascer (semanas vazias apagam a informação)
const SetPatientBirthInfoAPI = (patientId, data, createdBy, callback) => {
    let json = "patientId=" + patientId +
        "&createdBy=" + createdBy +
        "&gestationalWeeks=" + encodeURIComponent(data.gestationalWeeks ?? '') +
        "&gestationalDays=" + encodeURIComponent(data.gestationalDays ?? '');

    CallPOSTAPI(PATIENT_SET_BIRTH_INFO, json, callback);
}

// Receituário: cria (sem id) ou altera (com id) uma receita do atendimento
const SetPatientPrescriptionAPI = (patientId, appointmentId, data, createdBy, callback) => {
    let json = "patientId=" + patientId +
        "&appointmentId=" + appointmentId +
        "&createdBy=" + createdBy +
        "&id=" + encodeURIComponent(data.id ?? '') +
        "&professionalId=" + encodeURIComponent(data.professionalId ?? '') +
        "&kind=" + encodeURIComponent(data.kind || 'simple') +
        "&issuedAt=" + encodeURIComponent(data.issuedAt || '') +
        "&copies=" + encodeURIComponent(data.copies ?? 1) +
        "&signed=" + (data.signed ? 1 : 0) +
        "&content=" + encodeURIComponent(data.content || '');

    CallPOSTAPI(PATIENT_SET_PRESCRIPTION, json, callback);
}

const DeletePatientPrescriptionAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(PATIENT_DELETE_PRESCRIPTION, json, callback);
}

// Modelos de receita da clínica: devolvem a lista atualizada
const SetPrescriptionTemplateAPI = (data, createdBy, callback) => {
    let json = "createdBy=" + createdBy +
        "&id=" + encodeURIComponent(data.id ?? '') +
        "&title=" + encodeURIComponent(data.title || '') +
        "&content=" + encodeURIComponent(data.content || '');

    CallPOSTAPI(COMPANY_SET_PRESCRIPTION_TEMPLATE, json, callback);
}

const DeletePrescriptionTemplateAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(COMPANY_DELETE_PRESCRIPTION_TEMPLATE, json, callback);
}

// CID-10 ou CID-11 ('cid10' | 'cid11'): busca por código ou nome na tabela oficial (resultado limitado no servidor)
const SearchIcdAPI = (classification, query, callback) => {
    let json = "classification=" + encodeURIComponent(classification) +
        "&q=" + encodeURIComponent(query || '');
    CallPOSTAPI(SEARCH_ICD, json, callback);
}

const SetPatientDiagnosisAPI = (patientId, appointmentId, classification, code, createdBy, callback) => {
    let json = "patientId=" + patientId +
        "&appointmentId=" + appointmentId +
        "&createdBy=" + createdBy +
        "&classification=" + encodeURIComponent(classification) +
        "&code=" + encodeURIComponent(code || '');

    CallPOSTAPI(PATIENT_SET_DIAGNOSIS, json, callback);
}

const DeletePatientDiagnosisAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(PATIENT_DELETE_DIAGNOSIS, json, callback);
}

// Plano alimentar: um plano por atendimento; o novo vira o vigente do paciente
const buildMealPlanBody = (data) => {
    return "&title=" + encodeURIComponent(data.title || '') +
        "&startsAt=" + encodeURIComponent(data.startsAt || '') +
        "&guidelines=" + encodeURIComponent(data.guidelines || '') +
        "&previousPlanId=" + (data.previousPlanId ? data.previousPlanId : '') +
        "&meals=" + encodeURIComponent(JSON.stringify(data.meals || []));
}

const SetPatientMealPlanAPI = (patientId, appointmentId, data, createdBy, callback) => {
    let json = "patientId=" + patientId +
        "&appointmentId=" + appointmentId +
        "&createdBy=" + createdBy +
        buildMealPlanBody(data);

    CallPOSTAPI(PATIENT_SET_MEAL_PLAN, json, callback);
}

const UpdatePatientMealPlanAPI = (id, data, updatedBy, callback) => {
    let json = "id=" + id +
        "&updatedBy=" + updatedBy +
        buildMealPlanBody(data);

    CallPOSTAPI(PATIENT_UPDATE_MEAL_PLAN, json, callback);
}

const DeletePatientMealPlanAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(PATIENT_DELETE_MEAL_PLAN, json, callback);
}

// Mapa de aplicação: substitui as marcações do atendimento (tudo vazio apaga o registro)
const SetPatientApplicationMapAPI = (patientId, appointmentId, data, createdBy, callback) => {
    let json = "patientId=" + patientId +
        "&appointmentId=" + appointmentId +
        "&createdBy=" + createdBy +
        "&marks=" + encodeURIComponent(JSON.stringify(data.marks || [])) +
        "&backgrounds=" + encodeURIComponent(JSON.stringify(data.backgrounds || {})) +
        "&batch=" + encodeURIComponent(data.batch || '') +
        "&notes=" + encodeURIComponent(data.notes || '');

    CallPOSTAPI(PATIENT_SET_APPLICATION_MAP, json, callback);
}

// Mapa de aplicação: substitui a lista de produtos da clínica
const SetApplicationProductsAPI = (products, callback) => {
    let json = "products=" + encodeURIComponent(JSON.stringify(products || []));
    CallPOSTAPI(COMPANY_SET_APPLICATION_PRODUCTS, json, callback);
}

// Anexos do paciente -----------------------------------------------------

/**
 * Monta a URL do endpoint que serve o arquivo do anexo.
 * Fica aqui (e não em RemotePaths.js) porque precisa da mesma base das demais
 * chamadas de API (REACT_APP_API_BASE_URL), e não da raiz do site.
 */
const PATH_API_PATIENT_ATTACHMENT_FILE = (id, size = 'full', download = false) => {
    return PATIENT_ATTACHMENT_FILE +
        "?id=" + encodeURIComponent(id) +
        "&size=" + encodeURIComponent(size) +
        (download ? "&download=1" : "");
}

const UploadPatientAttachmentAPI = (formData, callback, errorCallback = null) => {
    CallPOSTAPIWithFormData(PATIENT_ATTACHMENT_UPLOAD, formData, callback, errorCallback);
}

const AnalyzeCranialImageAPI = (formData, callback, errorCallback = null) => {
    CallPOSTAPIWithFormData(TOOLS_CRANIAL_ANALYSIS_ANALYZE, formData, callback, errorCallback);
}

const ListPatientAttachmentsAPI = (patientId, context, callback) => {
    let json = "patientId=" + patientId;

    if (context) {
        json += "&context=" + encodeURIComponent(context);
    }

    CallPOSTAPI(PATIENT_ATTACHMENT_LIST, json, callback);
}

const UpdatePatientAttachmentAPI = (fields, callback) => {
    let json = Object.entries(fields || {})
        .filter(([, value]) => value !== undefined && value !== null)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');

    CallPOSTAPI(PATIENT_ATTACHMENT_UPDATE, json, callback);
}

// Gera um link de cadastro (token de uso único) para o paciente preencher os próprios dados
const CreatePatientRegistrationLinkAPI = (callback) => {
    CallPOSTAPIWithJSON(PATIENT_REGISTRATION_LINK_CREATE, {}, callback);
}

// Envia o link pelo número próprio da clínica, com o link em botão
const SendPatientRegistrationLinkWhatsAppAPI = (token, countryCode, phone, callback) => {
    CallPOSTAPIWithJSON(PATIENT_REGISTRATION_LINK_SEND_WHATSAPP, { token, countryCode, phone }, callback);
}

// Guarda no link o canal e a campanha de origem; quem preenche o cadastro nasce com eles
const SetPatientRegistrationLinkOriginAPI = (token, originChannelId, originCampaignId, callback) => {
    CallPOSTAPIWithJSON(PATIENT_REGISTRATION_LINK_SET_ORIGIN, {
        token, origin_channel_id: originChannelId, origin_campaign_id: originCampaignId,
    }, callback);
}

// Reserva de horário: bloqueio + link de cadastro; vira agendamento quando o paciente se cadastra
const CreateAppointmentReservationAPI = (reservation, callback) => {
    CallPOSTAPIWithJSON(APPOINTMENT_RESERVATION_CREATE, reservation, callback);
}

const GetAppointmentReservationAPI = (appointmentId, callback) => {
    CallGETAPI(APPOINTMENT_RESERVATION_GET + "?appointmentId=" + encodeURIComponent(appointmentId), callback);
}

const ResendAppointmentReservationAPI = (appointmentId, callback) => {
    CallPOSTAPIWithJSON(APPOINTMENT_RESERVATION_RESEND, { appointmentId }, callback);
}

const NotifyReleasedAppointmentReservationAPI = (appointmentId, callback) => {
    CallPOSTAPIWithJSON(APPOINTMENT_RESERVATION_NOTIFY_RELEASED, { appointmentId }, callback);
}

const DeletePatientAttachmentAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(PATIENT_ATTACHMENT_DELETE, json, callback);
}

/**
 * Baixa o arquivo do anexo autenticado. Retorna uma Promise<Blob>.
 * Usado pelas miniaturas e pelo visualizador, já que <img src> não envia o header.
 */
const GetPatientAttachmentFileAPI = (id, size = 'full') => {
    return CallGETBlobAPI(PATH_API_PATIENT_ATTACHMENT_FILE(id, size));
}

/**
 * Baixa o ZIP com todos os dados de um paciente (LGPD, só o dono da clínica).
 * Retorna Promise<{ blob, fileName }>; em erro, a mensagem vem do servidor.
 */
const ExportPatientDataAPI = (patientId) => {
    return CallGETBlobAPI(PATIENT_EXPORT + "?patientId=" + encodeURIComponent(patientId), { withFileName: true });
}

/**
 * Linhas da exportação da lista de pacientes (Excel/PDF), só Proprietário(a) e
 * Administrador(a). format: "excel" | "pdf" (vai para a auditoria).
 * Resposta: { success, patients: [{ name, cpf, phone, email, birthday, status }] };
 * 403 chega pelo errorCallback com { error }.
 */
const GetPatientListExportAPI = (format, callback, errorCallback) => {
    CallPOSTAPI(PATIENT_LIST_EXPORT, "format=" + encodeURIComponent(format), callback, false, errorCallback);
}

/**
 * Anonimiza um paciente (LGPD, só o dono da clínica): remove os dados pessoais
 * identificáveis e mantém o histórico clínico vinculado a "Paciente anonimizado #<id>".
 * Ação irreversível.
 */
const AnonymizePatientAPI = (patientId, callback, errorCallback) => {
    let json = "patientId=" + patientId;
    CallPOSTAPI(PATIENT_ANONYMIZE, json, callback, false, errorCallback);
}

/**
 * Prévia da exclusão de um paciente (LGPD, só o dono da clínica): o que será
 * apagado, o que fica, avisos e bloqueios. O CallGETAPI não separa erros, então
 * respostas com `error` ou `success: false` vão para o errorCallback.
 */
const GetPatientDeletePreviewAPI = (patientId, callback, errorCallback) => {
    CallGETAPI(PATIENT_DELETE_PREVIEW + "?patientId=" + encodeURIComponent(patientId), (data) => {
        const failed = !data || data.error || data.success === false;
        if (failed && errorCallback && typeof errorCallback == 'function') {
            errorCallback(data || {});
            return;
        }
        callback(data);
    });
}

/**
 * Exclui um paciente (LGPD, só o dono da clínica): anonimiza, apaga o conteúdo
 * clínico e mantém a linha como lápide para agenda, financeiro e notas fiscais.
 * Ação irreversível. 409 (ALREADY_DELETED/BLOCKED) chega pelo errorCallback.
 */
const DeletePatientAPI = (patientId, callback, errorCallback) => {
    let json = "patientId=" + patientId;
    CallPOSTAPI(PATIENT_DELETE, json, callback, false, errorCallback);
}

/**
 * Encerramento da conta (LGPD, só o dono): abre o ciclo de carência de 90 dias
 * e cancela a renovação da assinatura. payload: { confirmation_name, reason? }.
 * O CallPOSTAPIWithJSON entrega também as respostas de erro (400/403/409) ao
 * callback, então `error` ou `success: false` vão para o errorCallback.
 */
const RequestCompanyClosureAPI = (payload, callback, errorCallback) => {
    CallPOSTAPIWithJSON(COMPANY_CLOSURE_REQUEST, payload, (data) => {
        invalidateCompanySubscriptionShare();
        if (!data || data.error || data.success === false) {
            errorCallback(data || {});
            return;
        }
        callback(data);
    });
}

/**
 * "Manter conta" (LGPD, só o dono): desfaz o encerramento pedido pelo próprio
 * dono enquanto a conta continua usável. Fora disso o servidor responde 409
 * ("Para manter a conta, assine um plano."), entregue ao errorCallback.
 */
const RevertCompanyClosureAPI = (callback, errorCallback) => {
    CallPOSTAPIWithJSON(COMPANY_CLOSURE_REVERT, {}, (data) => {
        invalidateCompanySubscriptionShare();
        if (!data || data.error || data.success === false) {
            errorCallback(data || {});
            return;
        }
        callback(data);
    });
}

/**
 * Opt-out de mensagens de relacionamento do paciente (LGPD L-15).
 * action: "status" | "set" | "revert". Resposta: { status, data: { optedOut, since, byUserName, hasPhone } }.
 */
const PatientWhatsAppOptOutAPI = (patientId, action, callback) => {
    let json = "patientId=" + encodeURIComponent(patientId) + "&action=" + encodeURIComponent(action);
    CallPOSTAPI(PATIENT_WHATSAPP_OPT_OUT, json, callback);
}

/**
 * Log de auditoria do prontuário (LGPD L-14): quem abriu, alterou ou baixou dados de cada
 * paciente. Só o proprietário da clínica tem acesso; outros papéis recebem status ERROR.
 * params: { patientId?, action?, userId?, dateFrom?, dateTo?, page?, limit? } (todos opcionais).
 * Resposta: { status, data: { entries, pagination, actions, users, patient } } ou { status, message }.
 */
const GetPatientAuditLogAPI = (params = {}, callback) => {
    let json = Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');

    CallPOSTAPI(PATIENT_AUDIT_LOG, json, callback);
}

// Histórico (quem cadastrou/alterou e quando) do paciente e do agendamento
const GetPatientHistoryAPI = (patientId, callback) => {
    CallPOSTAPI(PATIENT_HISTORY, "patientId=" + patientId, callback);
}

const GetAppointmentHistoryAPI = (appointmentId, callback) => {
    CallPOSTAPI(APPOINTMENT_HISTORY, "appointmentId=" + appointmentId, callback);
}

// Próximos agendamentos ativos do paciente a partir de fromDate (YYYY-MM-DD)
const GetPatientUpcomingAppointmentsAPI = (patientId, fromDate, callback) => {
    CallPOSTAPI(APPOINTMENTS_PATIENT_UPCOMING, "patientId=" + encodeURIComponent(patientId) + "&fromDate=" + encodeURIComponent(fromDate), callback);
}

// Reagendamento: o pagamento do agendamento antigo passa para o novo
const MoveAppointmentPaymentAPI = (fromId, toId, callback) => {
    CallPOSTAPI(APPOINTMENTS_MOVE_PAYMENT, "fromId=" + encodeURIComponent(fromId) + "&toId=" + encodeURIComponent(toId), callback);
};

const SetAppointmentInvoiceAPI = (appointmentId, companyId, createdBy, patient, price, paymentMethod, procedure, issueDocument, callback) => {
    
    let json = "appointmentId=" + appointmentId +
        "&companyId=" + companyId +
        "&patient=" + patient +
        "&price=" + price +
        "&paymentMethod=" + String(paymentMethod) +
        "&procedure=" + procedure +
        "&issueDocument=" + issueDocument +
        "&createdBy=" + createdBy;

   CallPOSTAPI(APPOINTMENT_CREATE_INVOICE, json, callback);
}

const UpdateCompanyAPI = (companyId, data, callback) => {
    const formData = new FormData();
    formData.append("id", companyId);
    formData.append("name", data.name);
    // DDI da empresa: sem este campo o backend gravava 0 e os envios de WhatsApp
    // para a clínica saíam com o número errado (corrigido em 2026-10-01).
    formData.append("countryCode", data.countryCode || '');
    formData.append("phone", data.phone);
    formData.append("email", data.email);
    formData.append("cnpj", data.cnpj);
    formData.append("cep", data.cep);
    formData.append("address", data.address);
    formData.append("number", data.number);
    formData.append("complement", data.complement);
    formData.append("district", data.district);
    formData.append("city", data.city);
    formData.append("state", data.state);
    formData.append("country", data.country);
    if (data.logo) {
        formData.append("logo", data.logo); // Assuming data.logo is a File object
    }
    if (data.banner) {
        formData.append("banner", data.banner); // Assuming data.banner is a File object
    }

    CallPOSTAPIWithFormData(COMPANY_UPDATE, formData, callback);
}

const UpdateCompanyConfigAPI = (companyId, data, callback) => {

    const formData = new FormData();
    formData.append("id", companyId);
    formData.append("availableDaysOfWeek", JSON.stringify(data.availableDaysOfWeek));
    formData.append("availableTime", JSON.stringify(data.availableTime));
    formData.append("blockedTimes", JSON.stringify(data.blockedTimes));
    formData.append("targetOccupation", data.targetOccupation);
    formData.append("timeInterval", data.timeInterval);
    formData.append("goalAppointments", data.goalAppointments);
    formData.append("goalBilling", data.goalBilling);

   CallPOSTAPIWithFormData(COMPANY_CONFIG_UPDATE, formData, callback);
}

// Waiting List APIs
const GetWaitingListAPI = (params, callback) => {
    // Build query string from params
    let queryParams = Object.entries(params)
        .map(([key, value]) => {
            if (Array.isArray(value)) {
                return `${key}=${value.join(',')}`;
            } else if (value != undefined && value != null) {
                return `${key}=${encodeURIComponent(value)}`;
            }
            return '';
        })
        .filter(param => param != '')
        .join('&');
    
    CallPOSTAPI(APPOINTMENTS_WAITING_LIST_GET, queryParams, callback);
}

const SetWaitingListAPI = (companyId, createdBy, data, callback) => {
    // Convert arrays and objects to JSON strings
    const processedData = { ...data };
    if (Array.isArray(processedData.preferredDays)) {
        processedData.preferredDays = JSON.stringify(processedData.preferredDays);
    }
    if (Array.isArray(processedData.preferredTimes)) {
        processedData.preferredTimes = JSON.stringify(processedData.preferredTimes);
    }
    
    // Build query string
    let json = "companyId=" + companyId +
           "&createdBy=" + createdBy;
    
    // Add all other parameters
    Object.entries(processedData).forEach(([key, value]) => {
        if (value != undefined && value != null) {
            json += `&${key}=${encodeURIComponent(value)}`;
        }
    });

    CallPOSTAPI(APPOINTMENTS_WAITING_LIST_SET, json, callback);
}

const UpdateWaitingListAPI = (updatedBy, data, callback) => {
    // Convert arrays and objects to JSON strings
    const processedData = { ...data };
    if (Array.isArray(processedData.preferredDays)) {
        processedData.preferredDays = JSON.stringify(processedData.preferredDays);
    }
    if (Array.isArray(processedData.preferredTimes)) {
        processedData.preferredTimes = JSON.stringify(processedData.preferredTimes);
    }
    
    // Build query string
    let json = "id=" + data.id + "&updatedBy=" + updatedBy;
    
    // Add all other parameters
    Object.entries(processedData).forEach(([key, value]) => {
        if (key != 'id' && value != undefined && value != null) {
            json += `&${key}=${encodeURIComponent(value)}`;
        }
    });

    CallPOSTAPI(APPOINTMENTS_WAITING_LIST_UPDATE, json, callback);
}

const DeleteWaitingListAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(APPOINTMENTS_WAITING_LIST_DELETE, json, callback);
}

const CheckWaitingListAvailabilityAPI = (params, callback) => {
    // Build query string from params
    let queryParams = Object.entries(params)
        .map(([key, value]) => {
            if (Array.isArray(value)) {
                return `${key}=${value.join(',')}`;
            } else if (value != undefined && value != null) {
                return `${key}=${encodeURIComponent(value)}`;
            }
            return '';
        })
        .filter(param => param != '')
        .join('&');
    
    // Add cache-busting parameter to force reload
    const timestamp = new Date().getTime();
    if (queryParams) {
        queryParams += `&_nocache=${timestamp}`;
    } else {
        queryParams = `_nocache=${timestamp}`;
    }
    
    
    CallPOSTAPI(APPOINTMENTS_WAITING_LIST_CHECK_AVAILABILITY, queryParams, callback);
}

// Waiting List Offers APIs
const GetWaitingListOffersAPI = (params, callback) => {
    let queryParams = Object.entries(params)
        .filter(([, v]) => v != undefined && v != null)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join('&');
    CallPOSTAPI(APPOINTMENTS_WAITING_LIST_GET_OFFERS, queryParams, callback);
}

const SetWaitingListOfferAPI = (data, callback) => {
    let json = Object.entries(data)
        .filter(([, v]) => v != undefined && v != null)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join('&');
    CallPOSTAPI(APPOINTMENTS_WAITING_LIST_SET_OFFER, json, callback);
}

const UpdateWaitingListOfferAPI = (data, callback) => {
    let json = Object.entries(data)
        .filter(([, v]) => v != undefined && v != null)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join('&');
    CallPOSTAPI(APPOINTMENTS_WAITING_LIST_UPDATE_OFFER, json, callback);
}

const CallPOSTAPIWithFormData = (path, formData, callback, errorCallback = null) => {
    // Lê o token a cada tentativa: a repetição após o 401 usa o token renovado
    const doFetch = () => fetch(path, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${getAccessToken()}`
        },
        body: formData
    });

    doFetch()
        .then(response => (response.status === 401 ? retryWithRefreshedToken(doFetch) : response))
        .then(response => {
            if (!response) {
                Disconnect();
                // Segue pelo fluxo normal para o callback receber o erro uma única vez
                return JSON.stringify(sessionExpiredError());
            }
            return response.text();
        })
        .then(data => {
            try {
                if (data) {
                    const jsonData = JSON.parse(data);
                    callback(jsonData);
                } else {
                    if (errorCallback) errorCallback(new Error('Resposta vazia do servidor'));
                }
            } catch (e) {
                if (errorCallback) errorCallback(e);
            }
        })
        .catch(error => {
            if (errorCallback) errorCallback(error);
        });
}

/**
 * GET autenticado que devolve o arquivo como Blob.
 * Reaproveita a renovação compartilhada/Disconnect no 401, como o CallPOSTAPI.
 *
 * options.withFileName: devolve { blob, fileName } (nome do Content-Disposition)
 * e, em erro HTTP, a mensagem do JSON do servidor em vez da mensagem padrão.
 */
const CallGETBlobAPI = async (path, options = {}) => {
    const doFetch = () => fetch(path, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${getAccessToken()}`
        }
    });

    let response = await doFetch();

    if (response.status == 401) {
        response = await retryWithRefreshedToken(doFetch);

        if (!response) {
            Disconnect();
            const expired = sessionExpiredError();
            throw Object.assign(new Error(expired.error), { code: expired.code });
        }
    }

    if (!response.ok) {
        let message = 'Não foi possível carregar o arquivo.';

        if (options.withFileName) {
            try {
                const parsed = await response.json();
                if (parsed && parsed.message) message = parsed.message;
            } catch (e) {
                // resposta não-JSON: mantém a mensagem padrão
            }
        }

        throw new Error(message);
    }

    const contentType = response.headers.get('Content-Type') || '';

    if (contentType.indexOf('application/json') !== -1) {
        const text = await response.text();
        let message = 'Não foi possível carregar o arquivo.';

        try {
            const parsed = JSON.parse(text);
            if (parsed && parsed.message) message = parsed.message;
        } catch (e) {
            // resposta não-JSON: mantém a mensagem padrão
        }

        throw new Error(message);
    }

    if (options.withFileName) {
        const disposition = response.headers.get('Content-Disposition') || '';
        const match = disposition.match(/filename="?([^";]+)"?/i);
        return { blob: await response.blob(), fileName: match ? match[1] : null };
    }

    return await response.blob();
}

const GetCompanyConfigAPI = (companyId, callback) => {
    let json = "id=" + companyId;
    CallPOSTAPI(COMPANY_CONFIG_GET, json, callback);
}

const LoginAPI = (email, password, callback) => {
    let json = "email=" + encodeURIComponent(email) + "&password=" + encodeURIComponent(password);
    CallPOSTAPI(LOGIN, json, callback, true); // Show connection logs for login
}

// Renova o access token pelo cookie de renovação. Compartilha a chamada em andamento
// (boot, renovação proativa e 401 simultâneos disparam uma única requisição).
// Sem token em memória e sem sessão salva neste navegador (visitante na landing),
// não há o que renovar: responde sem chamar o servidor, em vez de um 401 no console.
const RefreshTokenAPI = (callback) => {
    if (!getAccessToken() && !localStorage.getItem('sessionData') && !localStorage.getItem('userData')) {
        callback({ error: 'Sessão expirada', code: 'refresh_token_expired' });
        return;
    }
    runSharedRefresh().then(callback);
}

const GetSessionAPI = (callback) => {
    CallPOSTAPI(SESSION, "", callback);
}

const GetDashboardAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(DASHBOARD_GET, json, callback);
}

const GetAppointmentsKpiAPI = (startDate, endDate, callback) => {
    let json = "startDate=" + startDate + "&endDate=" + endDate;
    CallPOSTAPI(APPOINTMENTS_KPI_GET, json, callback);
}

const ResolvePublicAppointmentActionAPI = (actionId, callback) => {
    let json = "id=" + actionId;
    CallPOSTAPI(RESOLVE_PUBLIC_APPOINTMENT_ACTION, json, callback);
}

/**
 * Prepares a personalized appointment confirmation message for manual sending
 * via WhatsApp Web. Generates (or reuses) a public confirmation token and
 * returns the message with the {confirmation_link} already substituted.
 *
 * @param {number} appointmentId - The appointment ID
 * @param {number} templateIndex - 0 = template 1, 1 = template 2, 2 = template 3
 * @param {function} callback - Callback with { success, message, confirmation_link, patient_name, company_name }
 */
const PrepareManualConfirmationMessageAPI = (appointmentId, templateIndex, callback) => {
    const data = JSON.stringify({
        appointment_id: appointmentId,
        template_index: templateIndex,
    });
    CallPOSTAPI(PREPARE_MANUAL_CONFIRMATION_MESSAGE, data, callback);
};

/**
 * Envia o pedido de confirmação pelo sistema, com os botões do cron, em vez de
 * abrir o WhatsApp Web. Falha com reason PLAN_REQUIRED / OWN_NUMBER_ONLY quando
 * o plano ou o número não permitem: aí o Início segue no WhatsApp Web.
 *
 * @param {number} appointmentId - The appointment ID
 * @param {number} templateIndex - 0 = template 1, 1 = template 2, 2 = template 3
 * @param {function} callback - Callback with { success, status: 'sent'|'queued' } or { success: false, reason, error }
 */
const SendManualConfirmationRequestAPI = (appointmentId, templateIndex, callback) => {
    CallPOSTAPIWithJSON(SEND_MANUAL_CONFIRMATION_REQUEST, {
        appointment_id: appointmentId,
        template_index: templateIndex,
    }, callback);
};

/**
 * Envia ao paciente, pelo sistema e na hora, o aviso da agenda (agendamento
 * criado, reagendado ou oferta de horário) em vez de abrir o WhatsApp Web.
 * Só funciona para planos com envio pelo sistema (ver capabilities.agenda_send).
 *
 * @param {Object} payload - { type: 'created', appointment_ids: [] } |
 *                           { type: 'rescheduled', appointment_id } |
 *                           { type: 'slot_offer', patient_id, date, time, resource_id, procedure_id }
 * @param {function} callback - Callback with { success, status: 'sent'|'queued' } or { success: false, reason, error }
 */
const NotifyAppointmentPatientWhatsAppAPI = (payload, callback) => {
    CallPOSTAPIWithJSON(APPOINTMENTS_NOTIFY_PATIENT_WHATSAPP, payload, callback);
};

const GetLeadsAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(LEADS_GET, json, callback);
}

// Lead ainda não convertido com o telefone digitado no cadastro (origem + vínculo)
const FindLeadByPhoneAPI = (countryCode, phone, callback) => {
    CallPOSTAPI(LEADS_FIND_BY_PHONE, "countryCode=" + encodeURIComponent(countryCode || "") + "&phone=" + encodeURIComponent(phone), callback);
}

const SetLeadAPI = (createdBy, data, callback) => {

    const payload = {
        ...data, // Inclui todos os campos de `data`
        createdBy: createdBy, // Adiciona o campo createdBy
      };
    
      // Converter o payload em uma string formatada para envio
      const json = Object.keys(payload)
        .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(payload[key]))
        .join("&");
    
      // Chamar a API usando a função CallPOSTAPI
      CallPOSTAPI(LEADS_SET, json, callback);
}

const UpdateLeadAPI = (updatedBy, data, callback) => {

    const payload = {
      ...data, // Inclui todos os campos de `data`
      updatedBy: updatedBy, // Adiciona o campo updatedBy
    };
  
    // Converter o payload em uma string formatada para envio
    const json = Object.keys(payload)
      .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(payload[key]))
      .join("&");
  
    // Chamar a API usando a função CallPOSTAPI
    CallPOSTAPI(LEADS_UPDATE, json, callback);
  };

// Exclui lead sem paciente vinculado (o servidor recusa o lead que virou paciente)
const DeleteLeadAPI = (id, callback) => {
    CallPOSTAPI(LEADS_DELETE, "id=" + encodeURIComponent(id), callback);
}
  
// Marketing Campaign APIs
const GetCampaignsAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(CAMPAIGNS_GET, json, callback);
}

const SetCampaignAPI = (companyId, createdBy, data, callback) => {
    const payload = {
        companyId: companyId,
        createdBy: createdBy,
        name: data.name,
        sourceId: data.sourceId,
        defaultMessage: data.defaultMessage || '',
        description: data.description || ''
    };
    
    // Converter o payload em uma string formatada para envio
    const json = Object.keys(payload)
        .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(payload[key]))
        .join("&");
    
    // Chamar a API usando a função CallPOSTAPI
    CallPOSTAPI(CAMPAIGNS_SET, json, callback);
}

const UpdateCampaignAPI = (companyId, updatedBy, data, callback) => {
    const payload = {
        id: data.id,
        companyId: companyId,
        updatedBy: updatedBy,
        name: data.name,
        sourceId: data.sourceId,
        defaultMessage: data.defaultMessage || '',
        description: data.description || ''
    };
    
    // Converter o payload em uma string formatada para envio
    const json = Object.keys(payload)
        .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(payload[key]))
        .join("&");
    
    // Chamar a API usando a função CallPOSTAPI
    CallPOSTAPI(CAMPAIGNS_UPDATE, json, callback);
}

const DeleteCampaignAPI = (companyId, id, callback) => {
    let json = "companyId=" + companyId + "&id=" + id;
    CallPOSTAPI(CAMPAIGNS_DELETE, json, callback);
}

// Marketing Lead Sources APIs
const GetSourcesAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(SOURCES_GET, json, callback);
}

const GetCampaignsBySourceAPI = (companyId, sourceId, callback) => {
    let json = "companyId=" + companyId + "&sourceId=" + sourceId;
    CallPOSTAPI(CAMPAIGNS_BY_SOURCE_GET, json, callback);
}

const SetSourceAPI = (companyId, createdBy, data, callback) => {
    const payload = {
        companyId: companyId,
        createdBy: createdBy,
        name: data.name
    };
    
    // Converter o payload em uma string formatada para envio
    const json = Object.keys(payload)
        .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(payload[key]))
        .join("&");
    
    // Chamar a API usando a função CallPOSTAPI
    CallPOSTAPI(SOURCES_SET, json, callback);
}

const UpdateSourceAPI = (companyId, updatedBy, data, callback) => {
    const payload = {
        id: data.id,
        companyId: companyId,
        updatedBy: updatedBy,
        name: data.name
    };
    
    // Converter o payload em uma string formatada para envio
    const json = Object.keys(payload)
        .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(payload[key]))
        .join("&");
    
    // Chamar a API usando a função CallPOSTAPI
    CallPOSTAPI(SOURCES_UPDATE, json, callback);
}

const DeleteSourceAPI = (companyId, id, callback) => {
    let json = "companyId=" + companyId + "&id=" + id;
    CallPOSTAPI(SOURCES_DELETE, json, callback);
}

const GetReportsAppointmentsAPI = (companyId, startDate, endDate, callback) => {
    let json = "companyId=" + companyId + "&startDate=" + startDate + "&endDate=" + endDate;
    CallPOSTAPI(REPORTS_APPOINTMENTS_GET, json, callback);
}

const GetReportsFinancialAPI = (companyId, startDate, endDate, callback) => {
    let json = "companyId=" + companyId + "&startDate=" + startDate + "&endDate=" + endDate;
    CallPOSTAPI(REPORTS_FINANCIAL_GET, json, callback);
}

const GetAppointmentsAPI = (companyId, startDate, endDate, callback) => {
    let json = "companyId=" + companyId;
    if (startDate) {
        json += "&startDate=" + startDate;
    }
    if (endDate) {
        json += "&endDate=" + endDate;
    }
    CallPOSTAPI(APPOINTMENTS_GET, json, callback);
}

const SetAppointmentAPI = (companyId, createdBy, data, callback) => {
    // resource null/undefined = bloqueio geral da empresa → envia "null" explícito para a API
    // para que o backend salve NULL no banco (e não confunda com resource id=0)
    const resourceValue = (data.resource === null || data.resource === undefined) ? 'null' : data.resource;
    const procedureValue = (data.procedure === null || data.procedure === undefined) ? 'null' : data.procedure;
    let json = "status=" + data.status + "&date=" + data.date + "&patient=" + data.patient + "&resource=" + resourceValue + "&procedure=" + procedureValue + "&duration=" + data.duration + "&price=" + data.price + "&companyId=" + companyId + "&createdBy=" + createdBy;
    
    // Adicionar roomId se fornecido
    if (data.roomId) {
        json += "&roomId=" + data.roomId;
    }
    json += appointmentProceduresParam(data);
    json += appointmentModalityParam(data);
    json += appointmentCoverageParam(data);

    CallPOSTAPI(APPOINTMENTS_SET, json, callback);
}

// Vários procedimentos por agendamento: [{ procedure_id, duration: "HH:MM:SS", price }]
// (docs/features/agendamento-multiplos-procedimentos). Sem a lista, vale o campo `procedure` solto.
const appointmentProceduresParam = (data) => {
    if (!Array.isArray(data?.procedures) || data.procedures.length === 0) return "";
    const lines = data.procedures.map((line) => ({
        procedure_id: line.procedure_id,
        duration: line.duration,
        price: line.price,
        // Convênio: item da tabela da operadora e o valor mostrado (docs/features/convenios)
        ...(line.insurance_procedure_id != null ? {
            insurance_procedure_id: line.insurance_procedure_id,
            insurance_price: line.insurance_price ?? null,
        } : {}),
    }));
    return "&procedures=" + encodeURIComponent(JSON.stringify(lines));
};

// Modalidade (presencial/online) e link da videochamada
// (docs/features/modalidade-de-atendimento). Sem `attendance_mode` no objeto nada é
// enviado e o backend assume presencial; link vazio limpa o que estava gravado.
const appointmentModalityParam = (data) => {
    if (!data || data.attendance_mode === undefined || data.attendance_mode === null) return "";
    return "&attendance_mode=" + encodeURIComponent(data.attendance_mode)
        + "&meeting_url=" + encodeURIComponent(data.meeting_url || "");
};

// Cobertura (particular/convênio) e carteirinha usada (docs/features/convenios). Sem
// `billing_type` no objeto nada é enviado e o backend assume particular. Em convênio a
// carteirinha é obrigatória; em particular o backend zera o id. ATENÇÃO: update_appointment
// sobrescreve todas as colunas, então quem atualiza precisa sempre mandar os dois campos
// a partir do agendamento carregado, senão ele volta a particular em silêncio.
const appointmentCoverageParam = (data) => {
    if (!data || data.billing_type === undefined || data.billing_type === null) return "";
    const insuranceId = data.patient_insurance_id ? data.patient_insurance_id : "";
    return "&billing_type=" + encodeURIComponent(data.billing_type)
        + "&patient_insurance_id=" + encodeURIComponent(insuranceId);
};

const UpdateAppointmentStatusAPI = (id, status, callback, appointmentTypeId = null) => {
    let json = "id=" + id + "&status=" + status;
    if (appointmentTypeId) {
        json += "&appointmentTypeId=" + appointmentTypeId;
    }
    CallPOSTAPI(APPOINTMENTS_UPDATE_STATUS, json, callback);
}
const UpdateAppointmentAPI = (data, callback) => {
    let json = "id=" + data.id + "&status=" + data.status + "&date=" + data.date + "&patient=" + data.patient + "&resource=" + data.resource + "&procedure=" + data.procedure + "&duration=" + data.duration + "&price=" + data.price;
    
    // Adicionar roomId se fornecido
    if (data.roomId) {
        json += "&roomId=" + data.roomId;
    }
    json += appointmentProceduresParam(data);
    json += appointmentModalityParam(data);
    json += appointmentCoverageParam(data);

    CallPOSTAPI(APPOINTMENTS_UPDATE, json, callback);
}

const DeleteAppointmentAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(APPOINTMENTS_DELETE, json, callback);
}

const GetAppointmentsToConfirmAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(APPOINTMENTS_TO_CONFIRM_GET, json, callback);
}

const GetPatientAPI = (id, callback, errorCallback) => {
    let json = "id=" + id;
    CallPOSTAPI(PATIENT_GET, json, callback, false, errorCallback);
}

const GetPatientHealthAPI = (id, callback, errorCallback) => {
    let json = "id=" + id;
    CallPOSTAPI(PATIENT_HEALTH_GET, json, callback, false, errorCallback);
}

const GetPatientsAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(PATIENTS_GET, json, callback);
}

const SetPatientAPI = (companyId, createdBy, data, callback) => {
    let json = "name=" + data.name +
        "&companyId=" + companyId +
        "&countryCode=" + data.countryCode +
        "&phone=" + data.phone +
        "&cpf=" + data.cpf +
        "&email=" + data.email +
        "&birthday=" + data.birthday +
        "&gender=" + data.gender +
        "&cep=" + data.cep +
        "&address=" + data.address +
        "&number=" + data.number +
        "&complement=" + data.complement +
        "&district=" + data.district +
        "&city=" + data.city +
        "&state=" + data.state +
        "&origin=" + data.origin +
        "&createdBy=" + createdBy;

    // Adicionar os novos campos de origem do marketing, se estiverem presentes
    if (data.origin_channel_id) {
        json += "&origin_channel_id=" + data.origin_channel_id;
    }

    if (data.origin_campaign_id) {
        json += "&origin_campaign_id=" + data.origin_campaign_id;
    }

    // Lead de origem: o backend vincula o lead ao paciente criado
    if (data.leadId) {
        json += "&leadId=" + data.leadId;
    }

    // Adicionar os campos de responsável financeiro
    json += "&financial_responsible_name=" + (data.financial_responsible_name || "");
    json += "&financial_responsible_document=" + (data.financial_responsible_document || "");
    json += "&financial_responsible_email=" + (data.financial_responsible_email || "");
    json += "&financial_responsible_phone=" + (data.financial_responsible_phone || "");
    json += "&financial_responsible_relationship=" + (data.financial_responsible_relationship || "");

    CallPOSTAPI(PATIENT_SET, json, callback);
}

const UpdatePatientAPI = (data, callback) => {
    let json = "id=" + data.id +
        "&status=" + data.status +
        "&name=" + data.name +
        "&countryCode=" + data.countryCode +
        "&phone=" + data.phone +
        "&cpf=" + data.cpf +
        "&email=" + data.email +
        "&birthday=" + data.birthday +
        "&gender=" + data.gender +
        "&cep=" + data.cep +
        "&address=" + data.address +
        "&number=" + data.number +
        "&complement=" + data.complement +
        "&district=" + data.district +
        "&city=" + data.city +
        "&state=" + data.state +
        "&origin=" + data.origin;
    
    // Adicionar os novos campos de origem do marketing, se estiverem presentes
    if (data.origin_channel_id) {
        json += "&origin_channel_id=" + data.origin_channel_id;
    }
    
    if (data.origin_campaign_id) {
        json += "&origin_campaign_id=" + data.origin_campaign_id;
    }

    // Adicionar os campos de responsável financeiro
    json += "&financial_responsible_name=" + (data.financial_responsible_name || "");
    json += "&financial_responsible_document=" + (data.financial_responsible_document || "");
    json += "&financial_responsible_email=" + (data.financial_responsible_email || "");
    json += "&financial_responsible_phone=" + (data.financial_responsible_phone || "");
    json += "&financial_responsible_relationship=" + (data.financial_responsible_relationship || "");

    CallPOSTAPI(PATIENT_UPDATE, json, callback);
}

const GetCEPInfoAPI = (value, callback) => {
    CallGETAPI("https://viacep.com.br/ws/" + value + "/json/", callback);
}

const CallGETAPI = (path, callback) => {
    // O access token só vai para a nossa API, nunca para serviços de terceiros (ex.: ViaCEP)
    const isOwnApi = path.startsWith(BASE_URL);

    // Lê o token a cada tentativa: a repetição após o 401 usa o token renovado
    const doFetch = () => fetch(path, {
        method: 'GET',
        headers: {
            ...(isOwnApi ? { 'Authorization': `Bearer ${getAccessToken()}` } : {}),
            'Content-Type': 'application/json',
        }
    });

    doFetch()
        .then(response => (isOwnApi && response.status === 401 ? retryWithRefreshedToken(doFetch) : response))
        .then(response => {
            if (!response) {
                Disconnect();
                return sessionExpiredError();
            }
            return response.json();
        })
        .then(data => {
            if (data && data.code === 'EMAIL_VERIFICATION_REQUIRED') {
                HandleEmailVerificationRequired(null);
                return;
            }
            callback(data);
        })
        .catch(error => {

            callback({ error: error.message || 'Erro de conexão' });
        });
}

// Evento disparado quando os tokens de autenticação mudam nesta aba
// (login, Disconnect). SystemConfigContext escuta para reavaliar a autenticação.
const AUTH_CHANGED_EVENT = 'gabriela:auth-changed';

// Rotas que não exigem autenticação (ver src/App.js). Nelas o Disconnect só limpa
// o armazenamento, sem navegar, para não recarregar a tela de login no meio da digitação.
const PUBLIC_ROUTES = [
    '/',
    '/login',
    '/forgot-password',
    '/reset-password',
    '/confirme-seu-email',
    '/confirmar-email',
    '/company-signup',
    '/company-join',
    '/terms-privacy',
    '/confirmar-demo',
    '/avaliacao',
    '/novidades'
];
const PUBLIC_ROUTE_PREFIXES = ['/agendar/', '/confirmar/', '/avaliacao/', '/cadastro/', '/novidades/', '/i/'];

const isPublicRoute = (pathname) => {
    return PUBLIC_ROUTES.includes(pathname) || PUBLIC_ROUTE_PREFIXES.some(prefix => pathname.startsWith(prefix));
};

// true depois que a sessão foi encerrada nesta página (Disconnect). Impede que uma
// renovação de token ainda em andamento grave um token novo sem o refresh token.
let sessionEnded = false;

// Promise da renovação em andamento: 401 simultâneos compartilham uma única chamada.
let refreshPromise = null;

const notifyAuthChanged = () => {
    window.dispatchEvent(new CustomEvent(AUTH_CHANGED_EVENT));
};

// Erro entregue ao callback quando a sessão não pôde ser renovada, para que
// quem aguarda a resposta (promises de carregamento) não fique pendurado.
const sessionExpiredError = () => ({ error: 'Sessão expirada', code: 'session_expired' });

// Grava o access token de um login bem-sucedido (só em memória) e reabre a sessão
// desta página. O refresh token não passa pelo SPA: o servidor o grava no cookie HttpOnly.
const SetAuthToken = (token) => {
    sessionEnded = false;
    setAccessToken(token);
    notifyAuthChanged();
}

// Uma única renovação por vez: resolve com a resposta do refresh-token.php.
// Nesta aba, refreshPromise junta as chamadas simultâneas. Entre abas, o Web Lock
// enfileira as renovações: o servidor gira o refresh token a cada uso e trata o
// reuso de um refresh antigo como roubo (revoga a sessão), então duas abas não
// podem mandar o mesmo cookie ao mesmo tempo (ex.: navegador reabrindo várias abas).
const REFRESH_LOCK_NAME = 'gabriela-auth-refresh';

const runSharedRefresh = () => {
    if (!refreshPromise) {
        const doRefresh = () => new Promise((resolve) => {
            CallRefreshTokenAPI(REFRESH_TOKEN, resolve);
        });
        const locks = typeof navigator !== 'undefined' ? navigator.locks : null;

        refreshPromise = (locks && typeof locks.request === 'function'
            ? locks.request(REFRESH_LOCK_NAME, doRefresh).catch(() => doRefresh())
            : doRefresh()
        ).finally(() => {
            refreshPromise = null;
        });
    }
    return refreshPromise;
}

// Renova o access token pelo cookie. Resolve true em caso de sucesso.
// Sem token em memória e sem sessão salva não há o que renovar (visitante anônimo).
const refreshAccessToken = () => {
    if (!getAccessToken() && !localStorage.getItem('sessionData')) {
        return Promise.resolve(false);
    }
    return runSharedRefresh().then((result) => !!(result && !result.error && getAccessToken()));
}

// Após um 401: renova o token (uma vez) e repete a requisição original uma única vez.
// Resolve com a nova resposta, ou null quando a sessão não pôde ser renovada.
const retryWithRefreshedToken = (doFetch) => {
    return refreshAccessToken().then((refreshed) => {
        if (!refreshed) {
            return null;
        }
        return doFetch().then((retryResponse) => (retryResponse.status === 401 ? null : retryResponse));
    });
}

// Encerra a sessão só nesta aba, sem chamar o servidor: descarta o token em memória
// e o estado salvo. Usado quando a sessão já acabou no servidor (renovação recusada)
// ou foi encerrada em outra aba.
const EndLocalSession = () => {
    const hadToken = !!getAccessToken();

    clearAccessToken();
    localStorage.removeItem('userData');
    // sessionData ficava órfão no localStorage e era usado por chamadas de API
    // (companyId). Precisa ser removido junto para não reintroduzir estado inválido.
    localStorage.removeItem('sessionData');

    // Sem token não havia sessão aberta: nada a notificar nem navegar.
    // Um segundo encerramento na mesma página também não navega de novo.
    if (!hadToken || sessionEnded) {
        return;
    }

    sessionEnded = true;
    notifyAuthChanged();

    // Redirecionar para a raiz (Landing/Login), exceto em rotas públicas
    if (!isPublicRoute(window.location.pathname)) {
        window.location.href = '/';
    }
}

// Encerra a sessão: revoga no servidor (sem esperar a resposta) e limpa esta aba.
const Disconnect = () => {
    if (getAccessToken() && !sessionEnded) {
        LogoutAPI();
    }
    EndLocalSession();
}

// Best-effort extraction of the "email" param from an application/x-www-form-urlencoded
// request body, used only to prefill the pending-verification screen. Never throws.
const extractEmailFromFormBody = (body) => {
    try {
        if (typeof body !== 'string' || !body) return null;
        const params = new URLSearchParams(body);
        return params.get('email') || null;
    } catch (e) {
        return null;
    }
};

// Central handling for the EMAIL_VERIFICATION_REQUIRED error code, returned by any
// protected endpoint (or login) when the account's email hasn't been confirmed yet.
// Clears local session state and redirects to the pending-verification screen.
const HandleEmailVerificationRequired = (pendingEmail) => {
    clearAccessToken();
    localStorage.removeItem('userData');
    localStorage.removeItem('sessionData');
    try {
        if (pendingEmail) {
            sessionStorage.setItem('pendingVerificationEmail', pendingEmail);
        }
    } catch (e) {
        // sessionStorage indisponível (modo privado, etc.) - segue sem bloquear o redirect
    }

    if (window.location.pathname !== '/confirme-seu-email') {
        window.location.href = '/confirme-seu-email';
    }
};


const GetNPSResponsesAPI = (companyId, startDate, endDate, category, callback) => {
    let json = "companyId=" + companyId;
    if (startDate) {
        json += "&startDate=" + startDate;
    }
    if (endDate) {
        json += "&endDate=" + endDate;
    }
    if (category && category != 'all') {
        json += "&category=" + category;
    }
    CallPOSTAPI(NPS_GET_RESPONSES, json, callback);
}

const SetNPSResponseAPI = (companyId, patientId, appointmentId, score, comments, callback) => {
    let json = "companyId=" + companyId +
        "&patient_id=" + patientId +
        "&appointment_id=" + appointmentId +
        "&score=" + score;
    
    if (comments) {
        json += "&comments=" + comments;
    }

    CallPOSTAPI(NPS_SET_RESPONSE, json, callback);
}

const UpdateNPSGoogleReviewAPI = (companyId, reviewId, status, callback) => {
    let json = "companyId=" + companyId +
        "&review_id=" + reviewId +
        "&status=" + status;

    CallPOSTAPI(NPS_UPDATE_GOOGLE_REVIEW, json, callback);
}

const PrepareManualNPSMessageAPI = (appointmentId, callback) => {
    const data = {
        appointmentId: appointmentId
    };
    
    CallPOSTAPI(NPS_PREPARE_MANUAL_MESSAGE, JSON.stringify(data), callback);
}

const GetGoogleReviewSuggestionsAPI = (companyId, startDate, endDate, status, callback) => {
    let json = "companyId=" + companyId;
    if (startDate) json += "&startDate=" + encodeURIComponent(startDate);
    if (endDate) json += "&endDate=" + encodeURIComponent(endDate);
    if (status) json += "&status=" + encodeURIComponent(status);
    
    CallPOSTAPI(NPS_GOOGLE_REVIEW_SUGGESTIONS, json, callback);
}

// Financial Categories APIs
const GetFinancialCategoriesAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(FINANCIAL_CATEGORIES_GET, json, callback);
}

const SetFinancialCategoryAPI = (companyId, data, callback) => {
    let json = "companyId=" + companyId +
        "&name=" + encodeURIComponent(data.name) +
        "&type=" + data.type;
    CallPOSTAPI(FINANCIAL_CATEGORIES_SET, json, callback);
}

const UpdateFinancialCategoryAPI = (data, callback) => {
    let json = "companyId=" + data.companyId +
        "&id=" + data.id +
        "&name=" + encodeURIComponent(data.name) +
        "&type=" + data.type +
        "&isActive=" + (data.isActive ? 1 : 0);
    CallPOSTAPI(FINANCIAL_CATEGORIES_UPDATE, json, callback);
}

const DeleteFinancialCategoryAPI = (companyId, id, callback) => {
    let json = "companyId=" + companyId + "&id=" + id;
    CallPOSTAPI(FINANCIAL_CATEGORIES_DELETE, json, callback);
}

// Financial Transactions APIs
const GetFinancialTransactionsAPI = (companyId, startDate, endDate, type, categoryId, callback) => {
    let json = "companyId=" + companyId;
    if (startDate) json += "&startDate=" + startDate;
    if (endDate) json += "&endDate=" + endDate;
    if (type) json += "&type=" + type;
    if (categoryId) json += "&categoryId=" + categoryId;
    CallPOSTAPI(FINANCIAL_TRANSACTIONS_GET, json, callback);
}

const SetFinancialTransactionAPI = (companyId, data, callback) => {
    let json = "companyId=" + companyId +
        "&type=" + data.type +
        "&description=" + data.description +
        "&amount=" + data.amount +
        "&transactionDate=" + data.transactionDate +
        "&categoryId=" + data.categoryId +
        "&createdBy=" + data.createdBy;

    if (data.isRecurring) {
        json += "&isRecurring=1" +
            "&recurrenceFrequency=" + data.recurrenceFrequency +
            "&recurrenceStartDate=" + data.recurrenceStartDate +
            "&recurrenceEndDate=" + (data.recurrenceEndDate || 'null');
    }

    CallPOSTAPI(FINANCIAL_TRANSACTIONS_SET, json, callback);
}

const UpdateFinancialTransactionAPI = (data, callback) => {
    let json = "companyId=" + data.companyId +
        "&id=" + data.id +
        "&type=" + data.type +
        "&description=" + data.description +
        "&amount=" + data.amount +
        "&transactionDate=" + data.transactionDate +
        "&categoryId=" + data.categoryId;

    if (data.isRecurring) {
        json += "&isRecurring=1" +
            "&recurrenceFrequency=" + data.recurrenceFrequency +
            "&recurrenceStartDate=" + data.recurrenceStartDate +
            "&recurrenceEndDate=" + (data.recurrenceEndDate || 'null');
    }

    CallPOSTAPI(FINANCIAL_TRANSACTIONS_UPDATE, json, callback);
}

const DeleteFinancialTransactionAPI = (companyId, id, callback) => {
    let json = "companyId=" + companyId + "&id=" + id;
    CallPOSTAPI(FINANCIAL_TRANSACTIONS_DELETE, json, callback);
}

const GetFinancialDashboardAPI = (companyId, month, year, callback) => {
    let json = "companyId=" + companyId;
    if (month) json += "&month=" + month;
    if (year) json += "&year=" + year;
    CallPOSTAPI(FINANCIAL_DASHBOARD_GET, json, callback);
}

const CreateServicePackageAPI = (companyId, data, callback) => {
  let json = "companyId=" + companyId +
      "&name=" + encodeURIComponent(data.name) +
      "&description=" + encodeURIComponent(data.description) +
      "&price=" + data.price +
      "&validity_days=" + data.validity_days +
      "&active=" + (data.active ? 1 : 0) +
      "&procedures=" + JSON.stringify(data.procedures);

  CallPOSTAPI(PACKAGES_CREATE, json, callback);
};

const UpdateServicePackageAPI = (packageId, data, callback) => {
  let json = "package_id=" + packageId +
      "&name=" + encodeURIComponent(data.name) +
      "&description=" + encodeURIComponent(data.description) +
      "&price=" + data.price +
      "&validity_days=" + data.validity_days +
      "&active=" + (data.active ? 1 : 0) +
      "&procedures=" + JSON.stringify(data.procedures);

  CallPOSTAPI(PACKAGES_UPDATE, json, callback);
};

const DeleteServicePackageAPI = (packageId, callback) => {
  let json = "package_id=" + packageId;
  CallPOSTAPI(PACKAGES_DELETE, json, callback);
};

// Resources APIs
const GetCompanyResourcesAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(RESOURCES_GET, json, callback);
}

const SetCompanyResourceAPI = (companyId, data, callback) => {
    // Se tiver um arquivo de assinatura, use FormData
    if (data.signatureFile) {
        const formData = new FormData();
        formData.append("companyId", companyId);
        formData.append("label", data.label);
        formData.append("regulator", data.regulator || '');
        formData.append("expertise", data.expertise || '');
        formData.append("phone", data.phone || '');
        formData.append("countryCode", data.countryCode || '');
        formData.append("email", data.email || '');
        formData.append("signatureFile", data.signatureFile);
        if (data.default_room_id !== undefined) formData.append("default_room_id", data.default_room_id || '');
        // Horário e limites próprios da agenda; null = segue a clínica
        if (data.schedule_config !== undefined) formData.append("schedule_config", JSON.stringify(data.schedule_config));

        CallPOSTAPIWithFormData(RESOURCES_SET, formData, callback);
    } else {
        // Caso contrário, use o método padrão
        let json = "companyId=" + companyId +
            "&label=" + encodeURIComponent(data.label) +
            "&regulator=" + encodeURIComponent(data.regulator || '') +
            "&signature=" + encodeURIComponent(data.signature || '') +
            "&expertise=" + encodeURIComponent(data.expertise || '') +
            "&phone=" + encodeURIComponent(data.phone || '') +
            "&countryCode=" + encodeURIComponent(data.countryCode || '') +
            "&email=" + encodeURIComponent(data.email || '');
        if (data.default_room_id !== undefined) json += "&default_room_id=" + encodeURIComponent(data.default_room_id || '');
        // Horário e limites próprios da agenda; null = segue a clínica
        if (data.schedule_config !== undefined) json += "&schedule_config=" + encodeURIComponent(JSON.stringify(data.schedule_config));
        CallPOSTAPI(RESOURCES_SET, json, callback);
    }
}

const UpdateCompanyResourceAPI = (data, callback) => {
    // Se tiver um arquivo de assinatura, use FormData
    if (data.signatureFile) {
        const formData = new FormData();
        formData.append("id", data.id);
        formData.append("label", data.label);
        formData.append("regulator", data.regulator || '');
        formData.append("expertise", data.expertise || '');
        formData.append("phone", data.phone || '');
        formData.append("countryCode", data.countryCode || '');
        formData.append("email", data.email || '');
        formData.append("signatureFile", data.signatureFile);
        if (data.active !== undefined) formData.append("active", data.active);
        // Public schedule fields
        if (data.public_schedule_enabled !== undefined) formData.append("public_schedule_enabled", data.public_schedule_enabled ? 1 : 0);
        if (data.public_schedule_name !== undefined) formData.append("public_schedule_name", data.public_schedule_name || '');
        if (data.public_schedule_bio !== undefined) formData.append("public_schedule_bio", data.public_schedule_bio || '');
        if (data.public_schedule_slug !== undefined) formData.append("public_schedule_slug", data.public_schedule_slug || '');
        if (data.default_room_id !== undefined) formData.append("default_room_id", data.default_room_id || '');
        // Horário e limites próprios da agenda; null = segue a clínica
        if (data.schedule_config !== undefined) formData.append("schedule_config", JSON.stringify(data.schedule_config));

        CallPOSTAPIWithFormData(RESOURCES_UPDATE, formData, callback);
    } else if (data.active !== undefined && data.label === undefined) {
        // Apenas atualização de status (reativação/desativação)
        let json = "id=" + data.id + "&active=" + data.active;
        CallPOSTAPI(RESOURCES_UPDATE, json, callback);
    } else {
        // Caso contrário, use o método padrão
        let json = "id=" + data.id +
            "&label=" + encodeURIComponent(data.label) +
            "&regulator=" + encodeURIComponent(data.regulator || '') +
            "&signature=" + encodeURIComponent(data.signature || '') +
            "&expertise=" + encodeURIComponent(data.expertise || '') +
            "&phone=" + encodeURIComponent(data.phone || '') +
            "&countryCode=" + encodeURIComponent(data.countryCode || '') +
            "&email=" + encodeURIComponent(data.email || '');
        if (data.active !== undefined) json += "&active=" + data.active;
        // Public schedule fields
        if (data.public_schedule_enabled !== undefined) json += "&public_schedule_enabled=" + (data.public_schedule_enabled ? 1 : 0);
        if (data.public_schedule_name !== undefined) json += "&public_schedule_name=" + encodeURIComponent(data.public_schedule_name || '');
        if (data.public_schedule_bio !== undefined) json += "&public_schedule_bio=" + encodeURIComponent(data.public_schedule_bio || '');
        if (data.public_schedule_slug !== undefined) json += "&public_schedule_slug=" + encodeURIComponent(data.public_schedule_slug || '');
        if (data.default_room_id !== undefined) json += "&default_room_id=" + encodeURIComponent(data.default_room_id || '');
        // Horário e limites próprios da agenda; null = segue a clínica
        if (data.schedule_config !== undefined) json += "&schedule_config=" + encodeURIComponent(JSON.stringify(data.schedule_config));
        CallPOSTAPI(RESOURCES_UPDATE, json, callback);
    }
}

const DeleteCompanyResourceAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(RESOURCES_DELETE, json, callback);
}

// Procedures APIs
const GetCompanyProceduresAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(PROCEDURES_GET, json, callback);
}

const SetCompanyProcedureAPI = (companyId, data, callback) => {
    let json = "companyId=" + companyId +
        "&label=" + encodeURIComponent(data.label) +
        "&price=" + data.price +
        "&duration=" + data.duration +
        "&description=" + encodeURIComponent(data.description || '');
    if (data.public_schedule_enabled !== undefined) json += "&public_schedule_enabled=" + (data.public_schedule_enabled ? 1 : 0);
    if (data.public_schedule_name !== undefined) json += "&public_schedule_name=" + encodeURIComponent(data.public_schedule_name || '');
    if (data.public_schedule_description !== undefined) json += "&public_schedule_description=" + encodeURIComponent(data.public_schedule_description || '');
    CallPOSTAPI(PROCEDURES_SET, json, callback);
}

const UpdateCompanyProcedureAPI = (data, callback) => {
    if (data.active !== undefined && data.label === undefined) {
        // Apenas atualização de status (reativação/desativação)
        let json = "id=" + data.id + "&active=" + data.active;
        CallPOSTAPI(PROCEDURES_UPDATE, json, callback);
    } else {
        let json = "id=" + data.id +
            "&label=" + encodeURIComponent(data.label) +
            "&price=" + data.price +
            "&duration=" + data.duration +
            "&description=" + encodeURIComponent(data.description || '');
        if (data.active !== undefined) json += "&active=" + data.active;
        // Public schedule fields
        if (data.public_schedule_enabled !== undefined) json += "&public_schedule_enabled=" + (data.public_schedule_enabled ? 1 : 0);
        if (data.public_schedule_name !== undefined) json += "&public_schedule_name=" + encodeURIComponent(data.public_schedule_name || '');
        if (data.public_schedule_description !== undefined) json += "&public_schedule_description=" + encodeURIComponent(data.public_schedule_description || '');
        CallPOSTAPI(PROCEDURES_UPDATE, json, callback);
    }
}

const DeleteCompanyProcedureAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(PROCEDURES_DELETE, json, callback);
}

// Function to call API endpoints that require authentication
const CallPOSTAPI = (path, body, callback, showConnectionLogs = false, errorCallback = null) => {
    const token = getAccessToken();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    const log = (msg, type = 'log') => {
        if (showConnectionLogs || type == 'error') {
            window.visualLogger?.[type](msg);
        }
    };

    const requestOptions = {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body,
        signal: controller.signal,
        ...authCookieOptions(path)
    };

    fetch(path, requestOptions)
        .then(response => {
            clearTimeout(timeout);

            // Handle 401 (Unauthorized): renova o token (se houver refresh token)
            // e repete a requisição uma vez; sem renovação, encerra a sessão.
            if (response.status == 401 && path != LOGIN && path != USER_PASSWORD_UPDATE && path != REFRESH_TOKEN && path != LOGOUT) {
                log('Sessão expirada, tentando renovar...', 'log');

                const doRetryFetch = () => fetch(path, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${getAccessToken()}`,
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: body
                });

                return retryWithRefreshedToken(doRetryFetch).then(retryResponse => {
                    if (!retryResponse) {
                        log('Sessão expirada, redirecionando...', 'error');
                        Disconnect();
                        if (errorCallback && typeof errorCallback == 'function') {
                            errorCallback(sessionExpiredError(), 401);
                        } else {
                            callback(sessionExpiredError());
                        }
                        return null;
                    }

                    log('Token renovado com sucesso, repetindo requisição original', 'log');
                    return retryResponse.text().then(text => {
                        try {
                            callback(JSON.parse(text));
                        } catch (e) {
                            callback({ error: 'Erro ao processar resposta do servidor' });
                        }
                        return null;
                    });
                });
            }
            
            // Handle rate limiting (429 Too Many Requests)
            if (response.status == 429) {
                return response.text().then(text => {
                    try {
                        const errorData = JSON.parse(text);
                        const retryAfter = errorData.retry_after || 60; // Default to 60 seconds if not provided
                        const minutes = Math.ceil(retryAfter / 60);
                        const message = `Muitas requisições. Por favor, aguarde ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'} e tente novamente.`;
                        
                        log(message, 'error');
                        
                        // Use errorCallback if provided, otherwise fall back to regular callback
                        if (errorCallback && typeof errorCallback == 'function') {
                            errorCallback({ error: message, retry_after: retryAfter }, response.status);
                        } else {
                            callback({ error: message, retry_after: retryAfter });
                        }
                        return null;
                    } catch (e) {
                        // If can't parse JSON, use a generic message
                        const message = 'Muitas requisições. Por favor, aguarde um momento e tente novamente.';
                        log(message, 'error');
                        
                        if (errorCallback && typeof errorCallback == 'function') {
                            errorCallback({ error: message }, response.status);
                        } else {
                            callback({ error: message });
                        }
                        return null;
                    }
                });
            }

            return response.text().then(text => {
                if (!response.ok) {
                    try {
                        const errorData = JSON.parse(text);
                        if (errorData.code === 'EMAIL_VERIFICATION_REQUIRED') {
                            HandleEmailVerificationRequired(extractEmailFromFormBody(body));
                            return null;
                        }
                        // Check for error property or any other property that might contain error information
                        if (errorData.error) {
                            // Use errorCallback if provided, otherwise fall back to regular callback
                            if (errorCallback && typeof errorCallback == 'function') {
                                errorCallback(errorData, response.status);
                            } else {
                                callback(errorData);
                            }
                            return null;
                        } else {
                            // If there's no error property but we have a valid JSON response,
                            // create an error object with the first property we find
                            const firstKey = Object.keys(errorData)[0];
                            if (firstKey) {
                                const errorObj = { error: errorData[firstKey] };
                                if (errorCallback && typeof errorCallback == 'function') {
                                    errorCallback(errorObj, response.status);
                                } else {
                                    callback(errorObj);
                                }
                                return null;
                            }
                        }
                    } catch (e) {
                        // If can't parse JSON, try to return the raw text as an error
                        if (text) {
                            const errorObj = { error: text };
                            if (errorCallback && typeof errorCallback == 'function') {
                                errorCallback(errorObj, response.status);
                            } else {
                                callback(errorObj);
                            }
                            return null;
                        }
                    }
                    // Only throw a generic error if we couldn't extract any meaningful error message
                    const errorObj = { error: `HTTP error! status: ${response.status}` };
                    if (errorCallback && typeof errorCallback == 'function') {
                        errorCallback(errorObj, response.status);
                    } else {
                        callback(errorObj);
                    }
                    return null;
                }
                return text;
            });
        })
        .then(data => {
            try {
                if (data) {
                    const jsonData = JSON.parse(data);
                    if (jsonData.error) {
                        log(jsonData.error, 'error');
                    }
                    callback(jsonData);
                } else {
                    log('Resposta vazia do servidor', 'error');
                }
            } catch (e) {
                log('Erro ao processar resposta do servidor', 'error');
                throw e;
            }
        })
        .catch(error => {
            if (error.name == 'AbortError') {
                log('Tempo limite de conexão excedido', 'error');
            } else if (!navigator.onLine) {
                log('Sem conexão com a internet', 'error');
            } else {
                log('Erro de conexão com o servidor', 'error');
            }
            // Use errorCallback if provided, otherwise fall back to regular callback
            if (errorCallback && typeof errorCallback == 'function') {
                errorCallback({ error: error.message || 'Erro de conexão' });
            } else {
                callback({ error: error.message || 'Erro de conexão' });
            }
        });
}

// Renova o access token pelo cookie HttpOnly gabriela_refresh (L-21): sem
// Authorization e sem corpo; o navegador envia o cookie só para /api/auth/.
// 401 (cookie ausente, expirado ou sessão revogada) encerra a sessão local sem
// chamar o logout; falhas de rede ou limite de requisições só devolvem o erro.
const CallRefreshTokenAPI = (path, callback) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    fetch(path, { method: 'POST', credentials: 'include', signal: controller.signal })
        .then(response => {
            clearTimeout(timeout);
            return response.text().then(text => {
                let data = null;
                try {
                    data = text ? JSON.parse(text) : null;
                } catch (e) {
                    data = null;
                }

                if (data && data.code === 'EMAIL_VERIFICATION_REQUIRED') {
                    HandleEmailVerificationRequired(null);
                    callback({ error: data.error || 'E-mail não confirmado', code: data.code });
                    return;
                }

                if (response.status === 401) {
                    EndLocalSession();
                    callback({
                        error: (data && data.error) || 'Sessão expirada',
                        code: (data && data.code) || 'refresh_token_expired'
                    });
                    return;
                }

                if (response.status === 429) {
                    const retryAfter = (data && data.retry_after) || 60;
                    const minutes = Math.ceil(retryAfter / 60);
                    const message = `Muitas requisições. Por favor, aguarde ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'} e tente novamente.`;
                    window.visualLogger?.error(message);
                    callback({ error: message, retry_after: retryAfter });
                    return;
                }

                if (!response.ok || !data || data.error || !data.token) {
                    const message = (data && data.error) || `HTTP error! status: ${response.status}`;
                    window.visualLogger?.error(message);
                    callback({ error: message, code: data && data.code });
                    return;
                }

                // Se a sessão foi encerrada durante a renovação, não reabre com um token órfão.
                if (!sessionEnded) {
                    const wasLoggedOut = !getAccessToken();
                    setAccessToken(data.token);
                    if (wasLoggedOut) {
                        notifyAuthChanged();
                    }
                }
                callback(data);
            });
        })
        .catch(error => {
            clearTimeout(timeout);
            if (error.name === 'AbortError') {
                window.visualLogger?.error('Tempo limite de conexão excedido');
            } else if (!navigator.onLine) {
                window.visualLogger?.error('Sem conexão com a internet');
            } else {
                window.visualLogger?.error('Erro de conexão com o servidor');
            }
            callback({ error: error.message || 'Erro de conexão' });
        });
}

// Encerra a sessão no servidor (revoga a sessão e apaga o cookie de renovação).
// Nunca lança e sempre resolve. keepalive: a requisição sobrevive à navegação
// que costuma vir logo depois do logout.
const LogoutAPI = () => {
    const token = getAccessToken();
    try {
        return fetch(LOGOUT, {
            method: 'POST',
            credentials: 'include',
            keepalive: true,
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        })
            .then(() => true)
            .catch(() => false);
    } catch (e) {
        return Promise.resolve(false);
    }
}

// Function to call public API endpoints (no authentication required)
const CallPublicAPI = (path, body, callback, showConnectionLogs = false, errorCallback = null) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    const log = (msg, type = 'log') => {
        if (showConnectionLogs || type == 'error') {
            window.visualLogger?.[type](msg);
        }
    };

    const requestOptions = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body,
        signal: controller.signal,
        ...authCookieOptions(path)
    };

    fetch(path, requestOptions)
        .then(response => {
            clearTimeout(timeout);
            
            // Handle rate limiting (429 Too Many Requests)
            if (response.status == 429) {
                return response.text().then(text => {
                    try {
                        const errorData = JSON.parse(text);
                        const retryAfter = errorData.retry_after || 60; // Default to 60 seconds if not provided
                        const minutes = Math.ceil(retryAfter / 60);
                        const message = `Muitas requisições. Por favor, aguarde ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'} e tente novamente.`;
                        
                        log(message, 'error');
                        
                        // Use errorCallback if provided, otherwise fall back to regular callback
                        if (errorCallback && typeof errorCallback == 'function') {
                            errorCallback({ error: message, retry_after: retryAfter }, response.status);
                        } else {
                            callback({ error: message, retry_after: retryAfter });
                        }
                        return null;
                    } catch (e) {
                        // If can't parse JSON, use a generic message
                        const message = 'Muitas requisições. Por favor, aguarde um momento e tente novamente.';
                        log(message, 'error');
                        
                        if (errorCallback && typeof errorCallback == 'function') {
                            errorCallback({ error: message }, response.status);
                        } else {
                            callback({ error: message });
                        }
                        return null;
                    }
                });
            }
            
            return response.text().then(text => {
                if (!response.ok) {
                    try {
                        const errorData = JSON.parse(text);
                        if (errorData.error) {
                            if (errorCallback && typeof errorCallback == 'function') {
                                errorCallback(errorData, response.status);
                            } else {
                                callback(errorData);
                            }
                            return null;
                        } else {
                            const firstKey = Object.keys(errorData)[0];
                            if (firstKey) {
                                const errorObj = { error: errorData[firstKey] };
                                if (errorCallback && typeof errorCallback == 'function') {
                                    errorCallback(errorObj, response.status);
                                } else {
                                    callback(errorObj);
                                }
                                return null;
                            }
                        }
                    } catch (e) {
                        if (text) {
                            const errorObj = { error: text };
                            if (errorCallback && typeof errorCallback == 'function') {
                                errorCallback(errorObj, response.status);
                            } else {
                                callback(errorObj);
                            }
                            return null;
                        }
                    }
                    const errorObj = { error: `HTTP error! status: ${response.status}` };
                    if (errorCallback && typeof errorCallback == 'function') {
                        errorCallback(errorObj, response.status);
                    } else {
                        callback(errorObj);
                    }
                    return null;
                }
                return text;
            });
        })
        .then(data => {
            try {
                if (data) {
                    const jsonData = JSON.parse(data);
                    if (jsonData.error) {
                        log(jsonData.error, 'error');
                    }
                    callback(jsonData);
                } else {
                    log('Resposta vazia do servidor', 'error');
                }
            } catch (e) {
                log('Erro ao processar resposta do servidor', 'error');
                throw e;
            }
        })
        .catch(error => {
            if (error.name == 'AbortError') {
                log('Tempo limite de conexão excedido', 'error');
            } else if (!navigator.onLine) {
                log('Sem conexão com a internet', 'error');
            } else {
                log('Erro de conexão com o servidor', 'error');
            }
            // Use errorCallback if provided, otherwise fall back to regular callback
            if (errorCallback && typeof errorCallback == 'function') {
                errorCallback({ error: error.message || 'Erro de conexão' });
            } else {
                callback({ error: error.message || 'Erro de conexão' });
            }
        });
};

// System Config API methods
const GetSystemConfigPublicAPI = (callback) => {
    CallPublicAPI(SYSTEM_CONFIG_PUBLIC, "", callback);
};

const GetSystemConfigPrivateAPI = (callback) => {
    CallPOSTAPI(SYSTEM_CONFIG_PRIVATE, "", callback);
};

const GetActiveRecurringTransactionsAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(FINANCIAL_TRANSACTIONS_GET_ACTIVE_RECURRING, json, callback);
}

// Payment Methods APIs
const GetCompanyPaymentMethodsAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(PAYMENT_METHODS_GET, json, callback);
}

const SetCompanyPaymentMethodAPI = (companyId, data, callback) => {
    let json = "companyId=" + companyId +
        "&label=" + encodeURIComponent(data.label) +
        "&priceModifier=" + data.priceModifier;
    CallPOSTAPI(PAYMENT_METHODS_SET, json, callback);
}

const UpdateCompanyPaymentMethodAPI = (data, callback) => {
    let json = "id=" + data.id +
        "&label=" + encodeURIComponent(data.label) +
        "&priceModifier=" + data.priceModifier;
    CallPOSTAPI(PAYMENT_METHODS_UPDATE, json, callback);
}

const DeleteCompanyPaymentMethodAPI = (id, callback) => {
    let json = "id=" + id;
    CallPOSTAPI(PAYMENT_METHODS_DELETE, json, callback);
}

// Message Templates APIs
const GetCompanyMessageTemplatesAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(MESSAGE_TEMPLATES_GET, json, callback);
}

const UpdateCompanyMessageTemplateAPI = (companyId, id, template_content, callback) => {
    let json = "companyId=" + companyId + "&id=" + id + "&template_content=" + encodeURIComponent(template_content);
    CallPOSTAPI(MESSAGE_TEMPLATES_UPDATE, json, callback);
}

// Export APIs
const CreateExportJobAPI = (companyId, includeAttachments, callback) => {
    let json = "companyId=" + companyId + "&includeAttachments=" + (includeAttachments ? 1 : 0);
    CallPOSTAPI(EXPORT_CREATE, json, callback);
}

const GetExportStatusAPI = (exportId, companyId, callback) => {
    let json = "exportId=" + exportId + "&companyId=" + companyId;
    CallPOSTAPI(EXPORT_STATUS, json, callback);
}

const GetExportHistoryAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(EXPORT_HISTORY, json, callback);
}

const CancelExportAPI = (exportId, companyId, callback) => {
    let json = "exportId=" + exportId + "&companyId=" + companyId;
    CallPOSTAPI(EXPORT_CANCEL, json, callback);
}

const GetExportEstimateAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(EXPORT_ESTIMATE, json, callback);
}

const GetBackupStatusAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(EXPORT_BACKUP_STATUS, json, callback);
}

// Automation Settings APIs
const GetCompanyAutomationSettingsAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(AUTOMATION_SETTINGS_GET, json, callback);
}

const UpdateCompanyAutomationSettingAPI = (companyId, automationType, isEnabled, callback) => {
    let json = "companyId=" + companyId +
        "&automation_type=" + automationType +
        "&is_enabled=" + (isEnabled ? 1 : 0);
    CallPOSTAPI(AUTOMATION_SETTINGS_UPDATE, json, callback);
}

const RegisterAPI = (data, callback) => {
    let json = "email=" + encodeURIComponent(data.email) +
        "&password=" + encodeURIComponent(data.password) +
        "&name=" + encodeURIComponent(data.name) +
        "&companyName=" + encodeURIComponent(data.companyName);
    
    // Add optional fields if they exist
    const phone = data.companyPhone || data.phone;
    const countryCode = data.companyCountryCode || data.countryCode;

    if (phone) {
        json += "&companyPhone=" + encodeURIComponent(String(phone).replace(/\D/g, ''));
    }
    if (countryCode) {
        json += "&companyCountryCode=" + encodeURIComponent(countryCode);
    }
    if (data.signupOption) {
        json += "&signupOption=" + encodeURIComponent(data.signupOption);
    }
    if (data.step) {
        json += "&step=" + encodeURIComponent(data.step);
    }
    if (data.invite_code) {
        json += "&invite_code=" + encodeURIComponent(data.invite_code);
    }
    // Origem do cadastro (utm/gclid/referrer), só com consentimento de marketing
    const attribution = Number(data.step) === 1 ? null : getSignupAttribution();
    if (attribution) {
        json += "&attribution=" + encodeURIComponent(JSON.stringify(attribution));
    }
    // Indique e ganhe: código de quem indicou (link /i/CODIGO), sem depender do consentimento
    const referralCode = Number(data.step) === 1 ? null : getReferralCode();
    if (referralCode) {
        json += "&referral_code=" + encodeURIComponent(referralCode);
    }
    
    CallPOSTAPI(REGISTER, json, callback);
};

const ValidatePasswordAPI = (password, callback) => {
    const json = "password=" + encodeURIComponent(password);
    CallPOSTAPI(PASSWORD_VALIDATE, json, callback);
};

const InviteUserAPI = (companyId, email, roleId, healthProfessionalRequested, callback) => {
    const json = "companyId=" + companyId +
        "&email=" + encodeURIComponent(email) +
        "&roleId=" + roleId +
        "&healthProfessionalRequested=" + (healthProfessionalRequested ? 1 : 0);
    CallPOSTAPI(USERS_INVITE, json, callback);
};

const AcceptInviteAPI = (token, data, callback) => {
    const json = "token=" + encodeURIComponent(token) +
        "&password=" + encodeURIComponent(data.password) +
        "&name=" + encodeURIComponent(data.name);
    CallPOSTAPI(USERS_ACCEPT_INVITE, json, callback);
};

// Company Setup API Functions
const GetCompanySetupStatusAPI = (callback, explicitCompanyId = null) => {
    // Use explicitly provided companyId if available
    let companyId = explicitCompanyId;
    
    // If no explicit companyId, try to get it from session
    if (companyId == null || companyId == undefined) {
        const sessionData = localStorage.getItem('sessionData');
        
        if (sessionData) {
            try {
                const parsedSession = JSON.parse(sessionData);
                companyId = parsedSession.currentCompany;
            } catch {}
        }
    }
    
    // Only make the API call if we have a companyId (including 0)
    if (companyId != null && companyId != undefined) {
        const json = `companyId=${companyId}`;
        CallPOSTAPI(COMPANY_SETUP_GET_STATUS, json, callback);
    } else {
        
        callback({ error: 'No company ID available' });
    }
};

const UpdateCompanySetupStatusAPI = (status, callback) => {
    const json = "status=" + status;
    CallPOSTAPI(COMPANY_SETUP_UPDATE_STATUS, json, callback);
};

// Registra a etapa do wizard exibida (card "Configuração inicial" do Admin Geral)
const TrackCompanySetupStepAPI = (step, callback) => {
    const json = "step=" + encodeURIComponent(step);
    CallPOSTAPI(COMPANY_SETUP_TRACK_STEP, json, callback);
};

// Configuração inicial v2 (company/setup): tudo o que a tela precisa para abrir já preenchida
const GetSetupWizardAPI = (callback) => {
    CallGETAPI(COMPANY_SETUP_GET_WIZARD, callback);
};

// { segment, team_mode: 'solo' | 'team' }
const SaveSetupAreaAPI = (data, callback) => {
    CallPOSTAPIWithJSON(COMPANY_SETUP_SAVE_AREA, data, callback);
};

// { professional_name, company_name, whatsapp }
const SaveSetupIdentityAPI = (data, callback) => {
    CallPOSTAPIWithJSON(COMPANY_SETUP_SAVE_IDENTITY, data, callback);
};

// { days: [0-6], start, end, lunch_start | null, lunch_end | null }
const SaveSetupScheduleAPI = (data, callback) => {
    CallPOSTAPIWithJSON(COMPANY_SETUP_SAVE_SCHEDULE, data, callback);
};

// { services: [{ id?, label, minutes, price }] }
const SaveSetupServicesAPI = (data, callback) => {
    CallPOSTAPIWithJSON(COMPANY_SETUP_SAVE_SERVICES, data, callback);
};

// Teste no WhatsApp: { phone }
const SendSetupDemoAPI = (data, callback) => {
    CallPOSTAPIWithJSON(COMPANY_SETUP_SEND_DEMO, data, callback);
};

// withPreview: traz também o texto e os botões que o teste envia (celular desenhado no passo)
const GetSetupDemoStatusAPI = (callback, withPreview = false) => {
    CallGETAPI(COMPANY_SETUP_GET_DEMO_STATUS + (withPreview ? "?preview=1" : ""), callback);
};

// Dados de exemplo: preencher (passo "Exemplo"), estado e remover (só durante o teste)
// Resposta: { success, counts, demo_data } ou { success: false, code, error }
const SeedDemoDataAPI = (callback) => {
    CallPOSTAPIWithJSON(COMPANY_DEMO_SEED, {}, callback);
};

const GetDemoDataStatusAPI = (callback) => {
    CallGETAPI(COMPANY_DEMO_GET_STATUS, callback);
};

const RemoveDemoDataAPI = (callback) => {
    CallPOSTAPIWithJSON(COMPANY_DEMO_REMOVE, {}, callback);
};

// Card "Primeiros passos" do Início
const GetFirstStepsAPI = (callback) => {
    CallGETAPI(COMPANY_FIRST_STEPS_GET, callback);
};

const DismissFirstStepsAPI = (callback) => {
    CallPOSTAPIWithJSON(COMPANY_FIRST_STEPS_DISMISS, {}, callback);
};

const SeeFirstStepAPI = (key, callback) => {
    CallPOSTAPIWithJSON(COMPANY_FIRST_STEPS_SEEN, { key }, callback);
};

const GetCompanyUsersAPI = (companyId, callback) => {
    const json = "companyId=" + companyId;
    CallPOSTAPI(USERS_GET, json, callback);
};

const RevokeUserAPI = (companyId, userId, callback) => {
    const json = "companyId=" + companyId + "&userId=" + userId;
    CallPOSTAPI(USERS_REVOKE, json, callback);
};

const UpdateUserAPI = (companyId, userId, roleId, callback) => {
    const json = "companyId=" + companyId + "&userId=" + userId + "&roleId=" + roleId;
    CallPOSTAPI(USERS_UPDATE, json, callback);
};

const TransferOwnershipAPI = (companyId, targetUserId, password, callback) => {
    const json = "companyId=" + companyId + "&targetUserId=" + targetUserId + "&password=" + encodeURIComponent(password);
    CallPOSTAPI(USERS_TRANSFER_OWNERSHIP, json, callback);
};

// scheduleScope (all | own) é opcional: ao ligar, grava o alcance junto e o aviso do sino já diz qual é
const SetHealthProfessionalAPI = (companyId, userId, enabled, callback, scheduleScope = null) => {
    const json = "companyId=" + companyId + "&userId=" + userId + "&enabled=" + (enabled ? 1 : 0)
        + (scheduleScope ? "&scheduleScope=" + encodeURIComponent(scheduleScope) : "");
    CallPOSTAPI(USERS_SET_HEALTH_PROFESSIONAL, json, callback);
};
// Devolve o acesso a quem está "Sem acesso", com o papel de antes (ocupa vaga do plano)
const ReactivateUserAPI = (companyId, userId, callback) => {
    const json = "companyId=" + companyId + "&userId=" + userId;
    CallPOSTAPI(USERS_REACTIVATE, json, callback);
};
// Convite pendente: cancelar libera a vaga; reenviar manda o e-mail de novo e renova por 7 dias
const CancelInviteAPI = (companyId, invitationId, callback) => {
    const json = "companyId=" + companyId + "&invitationId=" + invitationId;
    CallPOSTAPI(USERS_CANCEL_INVITE, json, callback);
};
const ResendInviteAPI = (companyId, invitationId, callback) => {
    const json = "companyId=" + companyId + "&invitationId=" + invitationId;
    CallPOSTAPI(USERS_RESEND_INVITE, json, callback);
};
// Agenda ligada à pessoa (resourceId vazio = sem agenda) e alcance (all | own); só o proprietário
const SetScheduleLinkAPI = (companyId, userId, resourceId, scheduleScope, callback) => {
    const json = "companyId=" + companyId + "&userId=" + userId
        + "&resourceId=" + (resourceId == null ? "" : resourceId)
        + "&scheduleScope=" + encodeURIComponent(scheduleScope || "");
    CallPOSTAPI(USERS_SET_SCHEDULE_LINK, json, callback);
};
const DeleteUserAPI = (userId, callback) => {
    const json = "userId=" + userId;
    CallPOSTAPI(USERS_DELETE, json, callback);
};

const ValidateInviteAPI = (token, callback) => {
    const json = "token=" + encodeURIComponent(token);
    CallPOSTAPI(USERS_VALIDATE_INVITE, json, callback);
};

// Password Recovery APIs
const RequestPasswordResetAPI = (email, callback) => {
    const json = "email=" + encodeURIComponent(email);
    CallPOSTAPI(PASSWORD_REQUEST_RESET, json, callback);
};

const VerifyPasswordTokenAPI = (token, code, callback) => {
    const json = "token=" + encodeURIComponent(token) + "&code=" + encodeURIComponent(code);
    CallPOSTAPI(PASSWORD_VERIFY_TOKEN, json, callback);
};

const ResetPasswordAPI = (token, password, callback) => {
    const json = "token=" + encodeURIComponent(token) + "&password=" + encodeURIComponent(password);
    CallPOSTAPI(PASSWORD_RESET, json, callback);
};

// User Profile API Functions
const GetUserProfileAPI = (callback) => {
    CallPOSTAPI(USER_PROFILE_GET, "", callback);
};

const UpdateUserProfileAPI = (name, phone, callback) => {
    const json = "name=" + encodeURIComponent(name) +
                 "&phone=" + encodeURIComponent(phone);
    CallPOSTAPI(USER_PROFILE_UPDATE, json, callback);
};

const UpdateUserPictureAPI = (pictureFile, callback) => {
    const formData = new FormData();
    formData.append('picture', pictureFile);
    
    const requestOptions = {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${getAccessToken()}`
        },
        body: formData
    };
    
    fetch(USER_PICTURE_UPDATE, requestOptions)
        .then(response => response.json())
        .then(data => {
            callback(data);
        })
        .catch(error => {
            callback({ success: false, error: 'Network error' });
        });
};

const UpdateUserPasswordAPI = (currentPassword, newPassword, callback) => {
    const json = "currentPassword=" + encodeURIComponent(currentPassword) +
                 "&newPassword=" + encodeURIComponent(newPassword);
    CallPOSTAPI(USER_PASSWORD_UPDATE, json, callback);
};

// Preferências de comunicação do usuário logado: { preferences: { product_updates_email, onboarding_email } }
const GetCommunicationPreferencesAPI = (callback) => {
    CallGETAPI(USER_COMMUNICATION_PREFERENCES_GET, callback);
};

// Liga ou desliga as Novidades e as dicas de uso por e-mail ({ product_updates_email?, onboarding_email? })
const UpdateCommunicationPreferencesAPI = (preferences, callback) => {
    CallPOSTAPIWithJSON(USER_COMMUNICATION_PREFERENCES_UPDATE, preferences, callback);
};

// Sessões ativas do usuário logado (mais recente primeiro)
const ListUserSessionsAPI = (callback) => {
    CallGETAPI(USER_SESSIONS_LIST, callback);
};

// Encerra uma sessão ({ sessionId }) ou todas as outras ({ others: true })
const RevokeUserSessionAPI = (target, callback) => {
    CallPOSTAPIWithJSON(USER_SESSIONS_REVOKE, target, callback);
};

// Email Verification APIs
const ConfirmEmailAPI = (token, callback) => {
    const json = "token=" + encodeURIComponent(token);
    CallPublicAPI(EMAIL_VERIFICATION_CONFIRM, json, callback);
};

const ResendVerificationEmailAPI = (email, callback) => {
    const json = "email=" + encodeURIComponent(email);
    CallPublicAPI(EMAIL_VERIFICATION_RESEND, json, callback);
};

const ChangePendingSignupEmailAPI = (email, password, newEmail, callback) => {
    const json = "email=" + encodeURIComponent(email) +
        "&password=" + encodeURIComponent(password) +
        "&new_email=" + encodeURIComponent(newEmail);
    CallPublicAPI(EMAIL_VERIFICATION_CHANGE_EMAIL, json, callback);
};

// Situação do cadastro pendente ({ status: 'pending' | 'confirmed' }), pela chave
// devolvida no cadastro (status_key). Usada pela tela "Confirme seu e-mail".
const GetPendingSignupStatusAPI = (email, statusKey, callback) => {
    const json = "email=" + encodeURIComponent(email) +
        "&status_key=" + encodeURIComponent(statusKey);
    CallPublicAPI(EMAIL_VERIFICATION_STATUS, json, callback);
};

// Mass Messaging API functions
const GetMassMessagingRecipientsAPI = (companyId, queryType, parameters, filterHistory = true, historyDays = 30, callback) => {
    let json = "companyId=" + companyId +
               "&query_type=" + queryType +
               "&parameters=" + JSON.stringify(parameters) +
               "&filterHistory=" + filterHistory +
               "&historyDays=" + historyDays;
    CallPOSTAPI(MASS_MESSAGING_RECIPIENTS_GET, json, callback);
}

const SendMassMessageAPI = (companyId, userId, recipients, messageTemplate, variables, channel, queryType = null, callback) => {
    // Create a FormData object for more reliable parameter passing
    const formData = new FormData();
    formData.append('companyId', companyId);
    formData.append('userId', userId);
    formData.append('recipients', JSON.stringify(recipients));
    formData.append('messageTemplate', messageTemplate);
    formData.append('variables', JSON.stringify(variables));
    formData.append('channel', channel);
    
    // Add query_type parameter if provided
    if (queryType) {
        formData.append('query_type', queryType);
    }
    
    // Use FormData for more reliable parameter passing
    CallPOSTAPIWithFormData(MASS_MESSAGING_SEND, formData, callback);
}

const GetPredefinedQueriesAPI = (callback) => {
    CallPOSTAPI(MASS_MESSAGING_PREDEFINED_QUERIES_GET, "", callback);
}

const RecordMassMessageHistoryAPI = (companyId, recipientId, messageContent, queryType, channel, sentBy, status, callback) => {
    // Create a FormData object for more reliable parameter passing
    const formData = new FormData();
    formData.append('company_id', companyId);
    formData.append('recipient_id', recipientId);
    formData.append('message_content', messageContent);
    
    if (queryType) {
        formData.append('query_type', queryType);
    }
    
    formData.append('channel', channel);
    formData.append('sent_by', sentBy);
    
    if (status) {
        formData.append('status', status);
    }
    
    // Use FormData for more reliable parameter passing
    CallPOSTAPIWithFormData(MASS_MESSAGING_RECORD_HISTORY, formData, callback);
}

const GetMassMessageHistoryAPI = (companyId, recipientId, queryType, startDate, endDate, page, limit, callback) => {
    let json = "company_id=" + companyId;
    
    if (recipientId) {
        json += "&recipient_id=" + recipientId;
    }
    
    if (queryType) {
        json += "&query_type=" + encodeURIComponent(queryType);
    }
    
    if (startDate) {
        json += "&start_date=" + startDate;
    }
    
    if (endDate) {
        json += "&end_date=" + endDate;
    }
    
    if (page) {
        json += "&page=" + page;
    }
    
    if (limit) {
        json += "&limit=" + limit;
    }
    
    CallPOSTAPI(MASS_MESSAGING_GET_HISTORY, json, callback);
}

const CheckRecipientMassMessageHistoryAPI = (companyId, recipientId, queryType, daysThreshold, callback) => {
    let json = "company_id=" + companyId +
               "&recipient_id=" + recipientId +
               "&query_type=" + encodeURIComponent(queryType);
    
    if (daysThreshold) {
        json += "&days_threshold=" + daysThreshold;
    }
    
    CallPOSTAPI(MASS_MESSAGING_CHECK_RECIPIENT_HISTORY, json, callback);
}

/**
 * Sends a problem report to the server
 * @param {FormData} formData - FormData object containing the report details and files
 * @param {function} callback - Callback function to handle the response
 */
const ReportProblemAPI = (formData, callback) => {
    formData.append('type', 'problem');
    CallPOSTAPIWithFormData(SYSTEM_FEEDBACK_SUBMIT, formData, callback);
};

/**
 * Sends an improvement suggestion to the server
 * @param {FormData} formData - FormData object containing the suggestion details and files
 * @param {function} callback - Callback function to handle the response
 */
const SuggestImprovementAPI = (formData, callback) => {
    formData.append('type', 'improvement');
    CallPOSTAPIWithFormData(SYSTEM_FEEDBACK_SUBMIT, formData, callback);
};

/**
 * Sends a product evaluation (NPS) to the server
 * @param {FormData} formData - FormData object containing the evaluation details
 * @param {function} callback - Callback function to handle the response
 */
const EvaluateProductAPI = (formData, callback) => {
    CallPOSTAPIWithFormData(SYSTEM_NPS_SUBMIT, formData, callback);
};

/**
 * Sends a contact form message to the support email
 * @param {FormData} formData - FormData object containing the contact details
 * @param {function} callback - Callback function to handle the response
 */
const ContactAPI = (formData, callback) => {
    CallPOSTAPIWithFormData(CONTACT_SUBMIT, formData, callback);
};

/**
 * Get system feedback entries
 */
const GetSystemFeedbackAPI = (companyId, startDate, endDate, type, status, callback) => {
    let json = "company_id=" + companyId;
    
    if (startDate) json += "&start_date=" + startDate;
    if (endDate) json += "&end_date=" + endDate;
    if (type) json += "&type=" + type;
    if (status) json += "&status=" + status;
    
    CallPOSTAPI(SYSTEM_FEEDBACK_GET, json, callback);
};

/**
 * Update system feedback status
 */
const UpdateSystemFeedbackAPI = (feedbackId, status, updatedBy, callback) => {
    let json = "feedback_id=" + feedbackId +
        "&status=" + status +
        "&updated_by=" + updatedBy;
    
    CallPOSTAPI(SYSTEM_FEEDBACK_UPDATE, json, callback);
};

/**
 * Get NPS responses
 */
const GetSystemNpsAPI = (companyId, startDate, endDate, callback) => {
    let json = "company_id=" + companyId;
    
    if (startDate) json += "&start_date=" + startDate;
    if (endDate) json += "&end_date=" + endDate;
    
    CallPOSTAPI(SYSTEM_NPS_GET, json, callback);
};

/**
 * Get the most recent NPS evaluation for the current user
 */
const GetUserNpsAPI = (callback) => {
    CallPOSTAPI(SYSTEM_NPS_GET_USER, "", callback);
};

/**
 * Upload file attachment for feedback or NPS
 */
const UploadFeedbackFileAPI = (formData, callback) => {
    CallPOSTAPIWithFormData(SYSTEM_FEEDBACK_UPLOAD_FILE, formData, callback);
};

/**
 * Get subscription plans available for the company
 */
const GetSubscriptionPlansAPI = (callback) => {
    CallPOSTAPI(SUBSCRIPTION_PLANS_GET, "", callback);
};

// Consulta da assinatura compartilhada, como a refreshPromise da renovação de sessão:
// o contexto, a página e a montagem dupla do StrictMode pedem a assinatura quase ao
// mesmo tempo, e todos recebem a mesma resposta. Uma resposta de sucesso ainda serve
// por SUBSCRIPTION_SHARE_MS; quem muda a assinatura invalida (invalidateCompanySubscriptionShare).
const SUBSCRIPTION_SHARE_MS = 3000;
// Uma consulta sem resposta depois disso (o CallPOSTAPI desiste em 10 s) não segura as próximas
const SUBSCRIPTION_INFLIGHT_MAX_MS = 15000;
// Sem resposta até aqui, a fila recebe um erro amigável (o CallPOSTAPI desiste em 10 s)
const SUBSCRIPTION_SAFETY_TIMEOUT_MS = 11000;
let companySubscriptionShare = null; // { key, callbacks, result, at, startedAt }

const invalidateCompanySubscriptionShare = () => {
    companySubscriptionShare = null;
};

// Callback de quem muda a assinatura: a próxima consulta busca de novo
const afterSubscriptionChange = (cb) => (cb ? (...args) => {
    invalidateCompanySubscriptionShare();
    cb(...args);
} : cb);

/**
 * Get company subscription details
 * options.fresh: ignora a resposta recente e busca de novo (depois de uma mudança)
 */
const GetCompanySubscriptionAPI = (callback, options = {}) => {
    const key = getAccessToken() || '';
    const share = companySubscriptionShare;

    if (!options.fresh && share && share.key === key) {
        if (share.callbacks && Date.now() - share.startedAt < SUBSCRIPTION_INFLIGHT_MAX_MS) {
            share.callbacks.push(callback);
            return;
        }
        if (!share.callbacks && Date.now() - share.at < SUBSCRIPTION_SHARE_MS) {
            Promise.resolve().then(() => callback(share.result));
            return;
        }
    }

    const current = { key, callbacks: [callback], result: null, at: 0, startedAt: Date.now() };
    companySubscriptionShare = current;

    // Entrega uma única vez a todos da fila; um consumidor com erro não impede os outros
    const deliver = (result) => {
        if (!current.callbacks) return;
        clearTimeout(safetyTimer);
        const callbacks = current.callbacks;
        current.callbacks = null;
        if (companySubscriptionShare === current) {
            if (result && result.success) {
                current.result = result;
                current.at = Date.now();
            } else {
                companySubscriptionShare = null;
            }
        }
        callbacks.forEach((cb) => {
            if (!cb) return;
            try {
                cb(result);
            } catch {}
        });
    };

    // Rede de segurança: se o CallPOSTAPI não responder (corpo vazio, verificação de
    // e-mail, etc.), ninguém fica preso em carregando
    const safetyTimer = setTimeout(() => deliver({
        success: false,
        error: 'Não foi possível carregar os dados da assinatura agora. Tente de novo em instantes.'
    }), SUBSCRIPTION_SAFETY_TIMEOUT_MS);

    CallPOSTAPI(SUBSCRIPTION_COMPANY_GET, "", deliver);
};

/**
 * Get subscription payment history
 */
const GetSubscriptionPaymentHistoryAPI = (page = 1, limit = 10, callback) => {
    const json = `page=${page}&limit=${limit}`;
    CallPOSTAPI(SUBSCRIPTION_COMPANY_PAYMENT_HISTORY, json, callback);
};

/**
 * Update company subscription plan
 * keep (opcional): escolha do que fica (PlanFitModal) na troca para plano menor de quem
 * já assina. O backend confere antes de trocar no Stripe e aplica depois da troca.
 */
const UpdateCompanySubscriptionPlanAPI = (planId, prorationEnabled = true, whenToApply = 'now', callback, keep = null, errorCallback = null) => {
    const json = JSON.stringify({
        plan_id: planId,
        proration_enabled: prorationEnabled,
        when_to_apply: whenToApply,
        ...(keep ? { keep } : {})
    });
    // errorCallback recebe a resposta de erro inteira ({ success:false, message }),
    // para a tela mostrar a mensagem do servidor
    CallPOSTAPI(SUBSCRIPTION_COMPANY_UPDATE_PLAN, json, afterSubscriptionChange(callback), false, afterSubscriptionChange(errorCallback));
};

/**
 * Simulate plan change to calculate proration amounts
 */
const SimulatePlanChangeAPI = (planId, prorationEnabled = true, whenToApply = 'now', callback) => {
    const json = JSON.stringify({
        plan_id: planId,
        proration_enabled: prorationEnabled,
        when_to_apply: whenToApply
    });
    CallPOSTAPI(SUBSCRIPTION_COMPANY_SIMULATE_PLAN_CHANGE, json, callback);
};

/**
 * Get company subscription history
 */
const GetSubscriptionHistoryAPI = (page = 1, limit = 10, callback) => {
    const json = `page=${page}&limit=${limit}`;
    CallPOSTAPI(SUBSCRIPTION_COMPANY_HISTORY, json, callback);
};

/**
 * Cancel company subscription
 * @param {function} callback
 * @param {string|null} cancelReason   - chave do motivo (ex: 'too_expensive')
 * @param {string|null} cancelReasonDetails - texto livre opcional
 */
const CancelCompanySubscriptionAPI = (callback, cancelReason = null, cancelReasonDetails = null) => {
    const data = cancelReason
        ? { cancel_reason: cancelReason, cancel_reason_details: cancelReasonDetails || null }
        : {};
    CallPOSTAPIWithJSON(SUBSCRIPTION_COMPANY_CANCEL, data, afterSubscriptionChange(callback));
};

/**
 * Cancel a specific subscription (main plan or add-on)
 * @param {string} subscriptionId - The Stripe subscription ID to cancel
 * @param {function} callback - Callback function
 */
const CancelSpecificSubscriptionAPI = (subscriptionId, callback) => {
    const json = `subscription_id=${subscriptionId}`;
    CallPOSTAPI(SUBSCRIPTION_SPECIFIC_CANCEL, json, afterSubscriptionChange(callback));
};

/**
 * Stripe API functions
 */
// keep (opcional): escolha do que fica (PlanFitModal). O backend só guarda a escolha na
// sessão do Stripe; ela é aplicada quando o pagamento é confirmado.
const CreateStripeCheckoutSessionAPI = (planId, callback, keep = null) => {
    // Enviar no formato JSON conforme esperado pelo backend
    const json = JSON.stringify(keep ? { plan_id: planId, keep } : { plan_id: planId });
    CallPOSTAPI(STRIPE_CREATE_CHECKOUT_SESSION, json, callback, true);
};

// Encaixe no plano: a clínica cabe nos limites do plano escolhido? Lista o que passa
// do limite (profissionais na agenda, usuários e convites, salas, procedimentos).
const GetPlanFitAPI = (planId, callback) => {
    CallPOSTAPIWithJSON(SUBSCRIPTION_COMPANY_PLAN_FIT, { plan_id: planId }, callback);
};

// Aplica a escolha do que fica na hora; o resto é inativado (nunca apagado). Para troca
// que passa pelo Stripe, use CreateStripeCheckoutSessionAPI(planId, cb, keep).
// keep = { resources: [ids], users: [userIds], invitations: [ids], rooms: [ids], procedures: [ids] }
const ApplyPlanFitAPI = (planId, keep, callback) => {
    CallPOSTAPIWithJSON(SUBSCRIPTION_COMPANY_APPLY_PLAN_FIT, { plan_id: planId, keep }, afterSubscriptionChange(callback));
};

const CreateStripeCustomerPortalAPI = (callback) => {
    CallPOSTAPI(STRIPE_CUSTOMER_PORTAL, "", callback, true);
};

const GetStripeCheckoutSessionAPI = (sessionId, callback) => {
    const json = "session_id=" + sessionId;
    CallPOSTAPI(STRIPE_GET_SESSION, json, callback);
};

const GetStripeConfigAPI = (callback) => {
    CallPOSTAPI(STRIPE_GET_CONFIG, "", callback);
};

// Terms and Privacy APIs
const AcceptTermsAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(TERMS_ACCEPT, json, callback);
}

const CheckTermsAcceptedAPI = (companyId, callback) => {
    let json = "companyId=" + companyId;
    CallPOSTAPI(TERMS_CHECK, json, callback);
}

const GetTermsContentAPI = (callback) => {
    CallPublicAPI(TERMS_GET, "", callback);
}

// Beta Access API
const SubmitBetaAccessFormAPI = (formData, callback) => {
    formData = formData || {};

    let json = "fullName=" + encodeURIComponent(formData.fullName || '') +
        "&email=" + encodeURIComponent(formData.email || '') +
        "&whatsapp=" + encodeURIComponent(formData.whatsapp || '');

    const optionalFields = ['profession', 'professionalsCount', 'hasSecretary', 'insuranceType'];
    optionalFields.forEach((field) => {
        const value = formData[field];
        if (value !== undefined && value !== null && value !== '') {
            json += "&" + field + "=" + encodeURIComponent(value);
        }
    });

    CallPOSTAPI(BETA_ACCESS_SUBMIT, json, callback);
};

// Public Schedule Settings API
const UpdatePublicScheduleSettingsAPI = (data, callback) => {
    let json = "public_schedule_enabled=" + (data.public_schedule_enabled ? 1 : 0) +
        "&public_schedule_slug=" + encodeURIComponent(data.public_schedule_slug || '') +
        "&public_schedule_whatsapp=" + encodeURIComponent(data.public_schedule_whatsapp || '');
    CallPOSTAPI(PUBLIC_SCHEDULE_SETTINGS_UPDATE, json, callback);
};

// Google Business APIs
const GetGoogleBusinessConfigAPI = (callback) => {
    CallGETAPI(GOOGLE_BUSINESS_GET, callback);
};

const SetGoogleBusinessConfigAPI = (googleReviewUrl, maskedUrl, callback) => {
    const json = "google_review_url=" + encodeURIComponent(googleReviewUrl) +
                 "&masked_url=" + encodeURIComponent(maskedUrl || '');
    CallPOSTAPI(GOOGLE_BUSINESS_SET, json, callback);
};

// Rooms APIs
const GetCompanyRoomsAPI = (companyId, callback) => {
    const json = "company_id=" + companyId;
    CallPOSTAPI(ROOMS_GET, json, callback);
};

const SetCompanyRoomAPI = (companyId, createdBy, data, callback) => {
    const json = "company_id=" + companyId +
        "&created_by=" + createdBy +
        "&name=" + encodeURIComponent(data.name) +
        "&description=" + encodeURIComponent(data.description || "") +
        "&capacity=" + data.capacity +
        "&is_active=" + (data.is_active ? 1 : 0);
    CallPOSTAPI(ROOMS_SET, json, callback);
};

const UpdateCompanyRoomAPI = (data, callback) => {
    const json = "id=" + data.id +
        "&name=" + encodeURIComponent(data.name) +
        "&description=" + encodeURIComponent(data.description || "") +
        "&capacity=" + data.capacity +
        "&is_active=" + (data.is_active ? 1 : 0);
    CallPOSTAPI(ROOMS_UPDATE, json, callback);
};

// Convênios: operadoras e planos da empresa (docs/features/convenios)
const GetInsuranceProvidersAPI = (callback) => {
    CallPOSTAPI(INSURANCE_PROVIDERS_GET, "", callback);
};

// Exigências da operadora (Fase 3): só vão quando definidas; ausentes, o backend mantém/usa 0
const insuranceProviderFlagParams = (data) => ["requires_medical_request", "requires_authorization"]
    .filter(key => data[key] !== undefined && data[key] !== null)
    .map(key => "&" + key + "=" + (data[key] ? 1 : 0))
    .join("");

// data: { name, ans_registry, notes, requires_medical_request, requires_authorization, plans: ["Celeste", ...] }
const SetInsuranceProviderAPI = (data, callback) => {
    const json = "name=" + encodeURIComponent(data.name || "") +
        "&ans_registry=" + encodeURIComponent(data.ans_registry || "") +
        "&notes=" + encodeURIComponent(data.notes || "") +
        insuranceProviderFlagParams(data) +
        "&plans=" + encodeURIComponent(JSON.stringify(data.plans || []));
    CallPOSTAPI(INSURANCE_PROVIDERS_SET, json, callback);
};

// data: { id, name, ans_registry, notes, active, requires_medical_request, requires_authorization, plans: [{ id?, name, active }] }
const UpdateInsuranceProviderAPI = (data, callback) => {
    let json = "id=" + data.id +
        "&name=" + encodeURIComponent(data.name || "") +
        "&ans_registry=" + encodeURIComponent(data.ans_registry || "") +
        "&notes=" + encodeURIComponent(data.notes || "") +
        insuranceProviderFlagParams(data) +
        "&plans=" + encodeURIComponent(JSON.stringify(data.plans || []));
    if (data.active !== undefined && data.active !== null) {
        json += "&active=" + (data.active ? 1 : 0);
    }
    CallPOSTAPI(INSURANCE_PROVIDERS_UPDATE, json, callback);
};

// Tabela de procedimentos por operadora: busca no catálogo TUSS (tabela 22 da ANS)
const SearchTussProceduresAPI = (query, callback) => {
    CallPOSTAPI(INSURANCE_TUSS_SEARCH, "q=" + encodeURIComponent(query || ""), callback);
};

const insuranceProcedureParams = (data) =>
    "&code=" + encodeURIComponent(data.code || "") +
    "&name=" + encodeURIComponent(data.name || "") +
    "&price=" + encodeURIComponent(data.price ?? "") +
    "&procedure_id=" + encodeURIComponent(data.procedure_id ?? "");

// data: { provider_id, tuss_code?, code, name, price?, procedure_id? }
const SetInsuranceProcedureAPI = (data, callback) => {
    const json = "provider_id=" + encodeURIComponent(data.provider_id) +
        "&tuss_code=" + encodeURIComponent(data.tuss_code || "") +
        insuranceProcedureParams(data);
    CallPOSTAPI(INSURANCE_PROCEDURES_SET, json, callback);
};

// data: { id, code, name, price?, procedure_id?, active? }
const UpdateInsuranceProcedureAPI = (data, callback) => {
    let json = "id=" + encodeURIComponent(data.id) + insuranceProcedureParams(data);
    if (data.active !== undefined && data.active !== null) {
        json += "&active=" + (data.active ? 1 : 0);
    }
    CallPOSTAPI(INSURANCE_PROCEDURES_UPDATE, json, callback);
};

const CopyInsuranceProceduresAPI = (fromProviderId, toProviderId, callback) => {
    CallPOSTAPI(INSURANCE_PROCEDURES_COPY,
        "from_provider_id=" + encodeURIComponent(fromProviderId) + "&to_provider_id=" + encodeURIComponent(toProviderId),
        callback);
};

// Carteirinhas da pessoa (+ última cobertura usada em agendamento)
const GetPatientInsurancesAPI = (patientId, callback) => {
    CallPOSTAPI(PATIENT_INSURANCE_LIST, "patientId=" + encodeURIComponent(patientId), callback);
};

const patientInsuranceParams = (data) => (
    "&provider_id=" + encodeURIComponent(data.provider_id || "") +
    "&plan_id=" + encodeURIComponent(data.plan_id || "") +
    "&card_number=" + encodeURIComponent(data.card_number || "") +
    "&holder_name=" + encodeURIComponent(data.holder_name || "") +
    "&valid_until=" + encodeURIComponent(data.valid_until || "") +
    "&notes=" + encodeURIComponent(data.notes || "")
);

// data: { patientId, provider_id, plan_id, card_number, holder_name, valid_until (YYYY-MM-DD), notes }
const SetPatientInsuranceAPI = (data, callback) => {
    const json = "patientId=" + encodeURIComponent(data.patientId) + patientInsuranceParams(data);
    CallPOSTAPI(PATIENT_INSURANCE_SET, json, callback);
};

// data: { id, patientId, ...campos, active } (active 0 inativa, 1 reativa)
const UpdatePatientInsuranceAPI = (data, callback) => {
    let json = "id=" + data.id + "&patientId=" + encodeURIComponent(data.patientId) + patientInsuranceParams(data);
    if (data.active !== undefined && data.active !== null) {
        json += "&active=" + (data.active ? 1 : 0);
    }
    CallPOSTAPI(PATIENT_INSURANCE_UPDATE, json, callback);
};

// Fase 2: pedidos médicos e guias da pessoa
const GetPatientInsuranceDocumentsAPI = (patientId, callback) => {
    CallPOSTAPI(PATIENT_INSURANCE_DOCUMENTS_LIST, "patientId=" + encodeURIComponent(patientId), callback);
};

const medicalRequestParams = (data) => (
    "&title=" + encodeURIComponent(data.title || "") +
    "&requesting_professional=" + encodeURIComponent(data.requesting_professional || "") +
    "&issued_at=" + encodeURIComponent(data.issued_at || "") +
    "&valid_until=" + encodeURIComponent(data.valid_until || "") +
    "&notes=" + encodeURIComponent(data.notes || "")
);

// data: { patientId, title, requesting_professional, issued_at (YYYY-MM-DD), valid_until, notes }
const SetMedicalRequestAPI = (data, callback) => {
    CallPOSTAPI(PATIENT_MEDICAL_REQUEST_SET, "patientId=" + encodeURIComponent(data.patientId) + medicalRequestParams(data), callback);
};

// data: { id, patientId, ...campos, active }
const UpdateMedicalRequestAPI = (data, callback) => {
    let json = "id=" + data.id + "&patientId=" + encodeURIComponent(data.patientId) + medicalRequestParams(data);
    if (data.active !== undefined && data.active !== null) {
        json += "&active=" + (data.active ? 1 : 0);
    }
    CallPOSTAPI(PATIENT_MEDICAL_REQUEST_UPDATE, json, callback);
};

const authorizationParams = (data) => (
    "&provider_id=" + encodeURIComponent(data.provider_id || "") +
    "&patient_insurance_id=" + encodeURIComponent(data.patient_insurance_id || "") +
    "&medical_request_id=" + encodeURIComponent(data.medical_request_id || "") +
    "&authorization_type=" + encodeURIComponent(data.authorization_type || "consultation") +
    "&reference_number=" + encodeURIComponent(data.reference_number || "") +
    "&password=" + encodeURIComponent(data.password || "") +
    "&status=" + encodeURIComponent(data.status || "pending") +
    "&valid_from=" + encodeURIComponent(data.valid_from || "") +
    "&valid_until=" + encodeURIComponent(data.valid_until || "") +
    "&authorized_quantity=" + encodeURIComponent(data.authorized_quantity || "") +
    "&notes=" + encodeURIComponent(data.notes || "")
);

// data: { patientId, provider_id, patient_insurance_id, medical_request_id, authorization_type,
//         reference_number, password, status, valid_from, valid_until, authorized_quantity, notes }
const SetInsuranceAuthorizationAPI = (data, callback) => {
    CallPOSTAPI(PATIENT_AUTHORIZATION_SET, "patientId=" + encodeURIComponent(data.patientId) + authorizationParams(data), callback);
};

// data: { id, patientId, ...campos, active }
const UpdateInsuranceAuthorizationAPI = (data, callback) => {
    let json = "id=" + data.id + "&patientId=" + encodeURIComponent(data.patientId) + authorizationParams(data);
    if (data.active !== undefined && data.active !== null) {
        json += "&active=" + (data.active ? 1 : 0);
    }
    CallPOSTAPI(PATIENT_AUTHORIZATION_UPDATE, json, callback);
};

// Fase 2: documentos (pedido/guia) vinculados a um agendamento
const GetAppointmentInsuranceDocumentsAPI = (appointmentId, callback) => {
    CallPOSTAPI(APPOINTMENT_INSURANCE_DOCUMENTS_LIST, "appointmentId=" + encodeURIComponent(appointmentId), callback);
};

// documentType: 'medical_request' | 'authorization'
const LinkAppointmentInsuranceDocumentAPI = (appointmentId, documentType, documentId, callback) => {
    const json = "appointmentId=" + encodeURIComponent(appointmentId) +
        "&document_type=" + encodeURIComponent(documentType) +
        "&document_id=" + encodeURIComponent(documentId);
    CallPOSTAPI(APPOINTMENT_INSURANCE_DOCUMENTS_LINK, json, callback);
};

const UnlinkAppointmentInsuranceDocumentAPI = (linkId, callback) => {
    CallPOSTAPI(APPOINTMENT_INSURANCE_DOCUMENTS_UNLINK, "id=" + encodeURIComponent(linkId), callback);
};

// Próximos atendimentos (mesma pessoa e carteirinha, sem pedido válido) que podem usar o pedido
const GetUpcomingRequestTargetsAPI = (appointmentId, requestId, callback) => {
    const json = "appointmentId=" + encodeURIComponent(appointmentId) + "&document_id=" + encodeURIComponent(requestId);
    CallPOSTAPI(APPOINTMENT_INSURANCE_UPCOMING_REQUEST, json, callback);
};

const LinkUpcomingRequestAPI = (appointmentId, requestId, callback) => {
    const json = "appointmentId=" + encodeURIComponent(appointmentId) + "&document_id=" + encodeURIComponent(requestId);
    CallPOSTAPI(APPOINTMENT_INSURANCE_LINK_UPCOMING_REQUEST, json, callback);
};

// Fila de pendências de convênio (Fase 4). filters: { startDate, endDate, providerId }
const GetInsurancePendingAPI = (filters, callback) => {
    const json = "startDate=" + encodeURIComponent(filters.startDate || "") +
        "&endDate=" + encodeURIComponent(filters.endDate || "") +
        "&providerId=" + encodeURIComponent(filters.providerId || "");
    CallPOSTAPI(APPOINTMENT_INSURANCE_PENDING_LIST, json, callback);
};

const DeleteCompanyRoomAPI = (roomId, callback) => {
    const json = "id=" + roomId;
    CallPOSTAPI(ROOMS_DELETE, json, callback);
};

const GetRoomOccupancyAPI = (roomId, startDateTime, endDateTime, callback) => {
    const json = "room_id=" + roomId +
        "&start_datetime=" + encodeURIComponent(startDateTime) +
        "&end_datetime=" + encodeURIComponent(endDateTime);
    CallPOSTAPI(ROOMS_GET_OCCUPANCY, json, callback);
};

const GetRoomsOccupancyBatchAPI = (roomIds, startDateTime, endDateTime, callback) => {
    const json = "room_ids=" + encodeURIComponent(JSON.stringify(roomIds)) +
        "&start_datetime=" + encodeURIComponent(startDateTime) +
        "&end_datetime=" + encodeURIComponent(endDateTime);
    CallPOSTAPI(ROOMS_GET_OCCUPANCY_BATCH, json, callback);
};

// Sala do dia (RECEP-01): faixas de sala de todas as agendas num dia (YYYY-MM-DD)
const GetDayRoomsAPI = (date, callback) => {
    CallPOSTAPI(ROOMS_DAY_GET, "date=" + encodeURIComponent(date), callback);
};

// Define a sala de uma agenda a partir de um horário do dia; preview=1 só conta o que mudaria
const SetDayRoomAPI = ({ resourceId, date, roomId, startTime, preview }, callback) => {
    const json = "resourceId=" + encodeURIComponent(resourceId) +
        "&date=" + encodeURIComponent(date) +
        "&roomId=" + encodeURIComponent(roomId ?? '') +
        "&startTime=" + encodeURIComponent(startTime || '00:00') +
        "&preview=" + (preview ? 1 : 0);
    CallPOSTAPI(ROOMS_DAY_SET, json, callback);
};

// Reschedule APIs
/**
 * Busca os slots de horário disponíveis para um profissional em uma data específica.
 * @param {number|string} professionalId - ID do profissional (resource)
 * @param {string} date - Data no formato YYYY-MM-DD
 * @param {function} callback - Callback com a resposta { success, date, professional_id, slots: [{time, available}] }
 */
const GetAvailableSlotsAPI = (professionalId, date, callback) => {
    const json = "professional_id=" + professionalId + "&date=" + encodeURIComponent(date);
    CallPOSTAPI(RESCHEDULE_GET_AVAILABLE_SLOTS, json, callback);
};

// Recurring Appointments APIs
/**
 * Calcula as datas de uma recorrência (diária ou semanal) e retorna a
 * disponibilidade de cada ocorrência, junto com os horários alternativos livres
 * do mesmo dia.
 *
 * Não persiste nada: serve apenas para a etapa de revisão antes da criação.
 *
 * @param {Object} params
 * @param {number|string} params.resourceId - ID do profissional
 * @param {string} params.startDate - Data inicial YYYY-MM-DD
 * @param {string} params.startTime - Horário inicial HH:MM
 * @param {number} params.intervalDays - Intervalo em dias (1,2,7,14,21,28)
 * @param {number} params.occurrences - Quantidade de ocorrências
 * @param {number} [params.procedureDuration] - Duração do procedimento em minutos
 * @param {function} callback - Callback com { success, occurrences: [{index, date, time, weekday, status, alternatives}] }
 */
const PreviewRecurringAppointmentsAPI = (params, callback) => {
    let json = "resource_id=" + params.resourceId +
        "&start_date=" + encodeURIComponent(params.startDate) +
        "&start_time=" + encodeURIComponent(params.startTime) +
        "&interval_days=" + params.intervalDays +
        "&occurrences=" + params.occurrences;

    if (params.procedureDuration) {
        json += "&procedure_duration=" + params.procedureDuration;
    }

    CallPOSTAPI(APPOINTMENTS_RECURRING_PREVIEW, json, callback);
};

/**
 * Cria em lote os agendamentos aprovados na revisão da recorrência.
 * Cada item gera um agendamento individual e independente.
 *
 * @param {number|string} companyId
 * @param {number|string} createdBy
 * @param {Object} data - { status, patient, resource, procedure, duration, price, roomId }
 * @param {Array} items - [{ date: 'YYYY-MM-DD', time: 'HH:MM' }, ...]
 * @param {function} callback - Callback com { status, created, failed, previousAppointments }
 */
const CreateRecurringAppointmentsAPI = (companyId, createdBy, data, items, callback) => {
    const resourceValue = (data.resource === null || data.resource === undefined) ? 'null' : data.resource;
    const procedureValue = (data.procedure === null || data.procedure === undefined) ? 'null' : data.procedure;

    let json = "status=" + data.status +
        "&patient=" + data.patient +
        "&resource=" + resourceValue +
        "&procedure=" + procedureValue +
        "&duration=" + data.duration +
        "&price=" + data.price +
        "&companyId=" + companyId +
        "&createdBy=" + createdBy +
        "&items=" + encodeURIComponent(JSON.stringify(items));

    if (data.roomId) {
        json += "&roomId=" + data.roomId;
    }
    json += appointmentProceduresParam(data);
    json += appointmentModalityParam(data);
    json += appointmentCoverageParam(data);

    CallPOSTAPI(APPOINTMENTS_RECURRING_CREATE, json, callback);
};


export {
    ValidateInviteAPI,
    GetSystemConfigPublicAPI,
    GetSystemConfigPrivateAPI,
    GetCompanyUsersAPI,
    RevokeUserAPI,
    UpdateUserAPI,
    TransferOwnershipAPI,
    SetHealthProfessionalAPI,
    SetScheduleLinkAPI,
    ReactivateUserAPI,
    CancelInviteAPI,
    ResendInviteAPI,
    RegisterAPI,
    ValidatePasswordAPI,
    InviteUserAPI,
    AcceptInviteAPI,
    
    // Company Setup APIs
    GetCompanySetupStatusAPI,
    UpdateCompanySetupStatusAPI,
    TrackCompanySetupStepAPI,
    GetTutorialProgressAPI,
    TrackTutorialProgressAPI,
    PollNotificationsAPI,
    ListNotificationsAPI,
    GetClinicStatsAPI,
    LocateClinicStatsDistrictsAPI,
    MarkNotificationsReadAPI,
    MarkAllNotificationsReadAPI,
    GetSetupWizardAPI,
    SaveSetupAreaAPI,
    SaveSetupIdentityAPI,
    SaveSetupScheduleAPI,
    SaveSetupServicesAPI,
    SendSetupDemoAPI,
    GetSetupDemoStatusAPI,
    SeedDemoDataAPI,
    GetDemoDataStatusAPI,
    RemoveDemoDataAPI,
    GetFirstStepsAPI,
    DismissFirstStepsAPI,
    SeeFirstStepAPI,
    GetActiveRecurringTransactionsAPI,
    GenerateAISimulatorReplyAPI,
    GetAISettingsAPI,
    SetAISettingsAPI,
    // Recuperar atendimentos
    GetRecoverySummaryAPI, GetRecoveryOpportunitiesAPI, GetRecoveryCandidatesAPI,
    ComposeRecoveryMessageAPI, SendRecoveryContactAPI, DismissRecoveryCandidateAPI, GetRecoveryActionsAPI,
    ClassifyRecoveryChatsAPI,
    GetWhatsappAISuggestionAPI,
    SetWhatsappAISuggestionOutcomeAPI,
    APPOINTMENT_GET_RECEIPT,
    LoginAPI,
    GetSessionAPI,
    RefreshTokenAPI,
    SetAuthToken,
    LogoutAPI,
    AUTH_CHANGED_EVENT,
    EndLocalSession,
    GetUserProfileAPI,
    GetCompanyConfigAPI,UpdateCompanyAPI,UpdateCompanyConfigAPI,
    GetLeadsAPI, SetLeadAPI, UpdateLeadAPI, FindLeadByPhoneAPI, DeleteLeadAPI,
    GetCampaignsAPI, SetCampaignAPI, UpdateCampaignAPI, DeleteCampaignAPI, GetCampaignsBySourceAPI,
    GetMassMessagingRecipientsAPI, SendMassMessageAPI, GetPredefinedQueriesAPI, RecordMassMessageHistoryAPI, GetMassMessageHistoryAPI, CheckRecipientMassMessageHistoryAPI,
    GetSourcesAPI, SetSourceAPI,UpdateSourceAPI,DeleteSourceAPI,
    GetCEPInfoAPI,
    GetDashboardAPI,
    GetReportsAppointmentsAPI,GetReportsFinancialAPI,
    GetAppointmentsToConfirmAPI,
    UpdateAppointmentStatusAPI,
    GetPatientsAPI, GetPatientAPI, GetPatientHealthAPI, SetPatientAPI, UpdatePatientAPI,
    SetPatientHistoryAPI, SetPatientNoteAPI, AnnulPatientHistoryRecordAPI, CreateReplacementHistoryRecordAPI, SetPatientComplaints, SetPatientAnamneseAPI, UpdatePatientAnamneseAPI, DeletePatientAnamneseAPI, SetPatientCranialAsymmetryAPI, UpdatePatientCranialAsymmetryAPI, DeletePatientCranialAsymmetryAPI, SetPatientBodyMeasurementsAPI, UpdatePatientBodyMeasurementsAPI, DeletePatientBodyMeasurementsAPI, SetPatientBodyMapAPI, SetPatientMealPlanAPI, UpdatePatientMealPlanAPI, DeletePatientMealPlanAPI, SetPatientApplicationMapAPI, SetApplicationProductsAPI, SetPatientScaleAssessmentAPI, SetPatientGrowthMeasurementAPI, DeletePatientGrowthMeasurementAPI, SetPatientMotorMilestoneAPI, DeletePatientMotorMilestoneAPI, SetPatientThermographyAPI, DeletePatientThermographyAPI, SetPatientAimsAssessmentAPI, DeletePatientAimsAssessmentAPI, SetPatientBirthInfoAPI, SetPatientDocumentAPI, SetPatientPrescriptionAPI, DeletePatientPrescriptionAPI, SetPrescriptionTemplateAPI, DeletePrescriptionTemplateAPI, SearchIcdAPI, SetPatientDiagnosisAPI, DeletePatientDiagnosisAPI,
    // Patient Attachments APIs
    UploadPatientAttachmentAPI, ListPatientAttachmentsAPI, UpdatePatientAttachmentAPI, DeletePatientAttachmentAPI,
    CreatePatientRegistrationLinkAPI, SendPatientRegistrationLinkWhatsAppAPI, SetPatientRegistrationLinkOriginAPI,
    CreateAppointmentReservationAPI, GetAppointmentReservationAPI, ResendAppointmentReservationAPI, NotifyReleasedAppointmentReservationAPI,
    GetPatientAttachmentFileAPI, PATH_API_PATIENT_ATTACHMENT_FILE, CallGETBlobAPI, ExportPatientDataAPI, GetPatientListExportAPI, AnonymizePatientAPI,
    GetPatientDeletePreviewAPI, DeletePatientAPI, PatientWhatsAppOptOutAPI, GetPatientAuditLogAPI,
    GetPatientHistoryAPI, GetAppointmentHistoryAPI, GetPatientUpcomingAppointmentsAPI, MoveAppointmentPaymentAPI,
    RequestCompanyClosureAPI, RevertCompanyClosureAPI,
    AnalyzeCranialImageAPI,
    GetAppointmentsAPI, SetAppointmentAPI, UpdateAppointmentAPI, DeleteAppointmentAPI,
    NotifyAppointmentPatientWhatsAppAPI,
    GetWaitingListAPI, SetWaitingListAPI, UpdateWaitingListAPI, DeleteWaitingListAPI, CheckWaitingListAvailabilityAPI,
    GetWaitingListOffersAPI, SetWaitingListOfferAPI, UpdateWaitingListOfferAPI,
    SetAppointmentInvoiceAPI,
    GetAppointmentFullInfo,
    GetInvoiceAPI,UpdateInvoiceAPI,UpdateInvoiceNfIssuedAPI,DeleteInvoiceAPI,
    SendWhatsappMessageAPI,GetWhatsappChatsAPI,GetWhatsappChatMessagesAPI,SetWhatsappChatReadAPI,GetWhatsappContactContextAPI,
    GetWhatsappConfigAPI,SetWhatsappConfigAPI,TestWhatsappConfigAPI,GetWhatsappDeviceInfoAPI,GetWhatsappQRCodeAPI,GetWhatsappPhoneCodeAPI,SendWhatsappDocumentAPI,DisconnectWhatsappAPI,
    GetWhatsappSenderStatusAPI,SetWhatsappSenderTogglesAPI,
    GetTemplatesDocumentsAPI, SetTemplatesDocumentsAPI, UpdateTemplatesDocumentsAPI, DeleteTemplatesDocumentsAPI,
    // Message Templates APIs
    GetCompanyMessageTemplatesAPI,
    UpdateCompanyMessageTemplateAPI,
    // Anamnesis Templates APIs
    GetAnamnesisTemplatesAPI,
    SetAnamnesisTemplateAPI,
    UpdateAnamnesisTemplateAPI,
    DeleteAnamnesisTemplateAPI,
    GetAppointmentTypesAPI,
    SetAppointmentTypeAPI,
    UpdateAppointmentTypeAPI,
    DeactivateAppointmentTypeAPI,
    GetInactiveAppointmentTypesAPI,
    ReactivateAppointmentTypeAPI,
    GetAppointmentStepsAPI,
    SetAppointmentStepAPI,
    SetAppointmentStepActiveAPI,
    ReorderAppointmentStepsAPI,
    CheckAppointmentStepAPI,
    SetAppointmentStatusColorAPI,
    // NPS APIs
    GetNPSResponsesAPI,
    SetNPSResponseAPI,
    UpdateNPSGoogleReviewAPI,
    PrepareManualNPSMessageAPI,
    GetGoogleReviewSuggestionsAPI,
    // Financial APIs
    GetFinancialCategoriesAPI,
    SetFinancialCategoryAPI,
    UpdateFinancialCategoryAPI,
    DeleteFinancialCategoryAPI,
    GetFinancialTransactionsAPI,
    SetFinancialTransactionAPI,
    UpdateFinancialTransactionAPI,
    DeleteFinancialTransactionAPI,
    GetFinancialDashboardAPI,
    // Wallet APIs
    GetPatientWalletAPI,
    GetWalletTransactionsAPI,
    GetServicePackagesAPI,
    PurchaseServicePackageAPI,
    DeductWalletCreditsAPI,
    CreateServicePackageAPI,
    UpdateServicePackageAPI,
    DeleteServicePackageAPI,
    // Resources APIs
    GetCompanyResourcesAPI,
    SetCompanyResourceAPI,
    UpdateCompanyResourceAPI,
    DeleteCompanyResourceAPI,
    // Procedures APIs
    GetCompanyProceduresAPI,
    SetCompanyProcedureAPI,
    UpdateCompanyProcedureAPI,
    DeleteCompanyProcedureAPI,
    // Payment Methods APIs
    GetCompanyPaymentMethodsAPI,
    SetCompanyPaymentMethodAPI,
    UpdateCompanyPaymentMethodAPI,
    DeleteCompanyPaymentMethodAPI,
    // Export APIs
    CreateExportJobAPI,
    GetExportStatusAPI,
    GetExportHistoryAPI,
    CancelExportAPI,
    GetExportEstimateAPI,
    GetBackupStatusAPI,
    // Automation Settings APIs
    GetCompanyAutomationSettingsAPI,
    UpdateCompanyAutomationSettingAPI,
    // Password Recovery APIs
    RequestPasswordResetAPI,
    VerifyPasswordTokenAPI,
    ResetPasswordAPI,
    // User Profile APIs
    UpdateUserProfileAPI,
    UpdateUserPictureAPI,
    UpdateUserPasswordAPI,
    ListUserSessionsAPI,
    RevokeUserSessionAPI,
    GetCommunicationPreferencesAPI,
    UpdateCommunicationPreferencesAPI,
    // Email Verification APIs
    ConfirmEmailAPI,
    ResendVerificationEmailAPI,
    GetPendingSignupStatusAPI,
    ChangePendingSignupEmailAPI,
    // Support APIs
    ReportProblemAPI,
    SuggestImprovementAPI,
    EvaluateProductAPI,
    ContactAPI,
    GetSystemFeedbackAPI,
    UpdateSystemFeedbackAPI,
    GetSystemNpsAPI,
    GetUserNpsAPI,
    UploadFeedbackFileAPI,
    // Subscription APIs
    GetSubscriptionPlansAPI,
    GetCompanySubscriptionAPI,
    GetSubscriptionPaymentHistoryAPI,
    UpdateCompanySubscriptionPlanAPI,
    SimulatePlanChangeAPI,
    GetSubscriptionHistoryAPI,
    CancelCompanySubscriptionAPI,
    CancelSpecificSubscriptionAPI,
    // Stripe APIs
    CreateStripeCheckoutSessionAPI,
    GetPlanFitAPI,
    ApplyPlanFitAPI,
    CreateStripeCustomerPortalAPI,
    GetStripeCheckoutSessionAPI,
    GetStripeConfigAPI,
    // Beta Access APIs
    SubmitBetaAccessFormAPI,
    // Terms and Privacy APIs
    AcceptTermsAPI,
    CheckTermsAcceptedAPI,
    GetTermsContentAPI,
    // Public Schedule Settings API
    UpdatePublicScheduleSettingsAPI,
    // Google Business APIs
    GetGoogleBusinessConfigAPI,
    SetGoogleBusinessConfigAPI,
    // Rooms APIs
    GetCompanyRoomsAPI,
    SetCompanyRoomAPI,
    UpdateCompanyRoomAPI,
    DeleteCompanyRoomAPI,
    GetRoomOccupancyAPI,
    GetRoomsOccupancyBatchAPI,
    GetDayRoomsAPI,
    SetDayRoomAPI,
    // Convênios APIs
    GetInsuranceProvidersAPI,
    SetInsuranceProviderAPI,
    UpdateInsuranceProviderAPI,
    SearchTussProceduresAPI,
    SetInsuranceProcedureAPI,
    UpdateInsuranceProcedureAPI,
    CopyInsuranceProceduresAPI,
    GetPatientInsurancesAPI,
    SetPatientInsuranceAPI,
    UpdatePatientInsuranceAPI,
    GetPatientInsuranceDocumentsAPI,
    SetMedicalRequestAPI,
    UpdateMedicalRequestAPI,
    SetInsuranceAuthorizationAPI,
    UpdateInsuranceAuthorizationAPI,
    GetAppointmentInsuranceDocumentsAPI,
    LinkAppointmentInsuranceDocumentAPI,
    UnlinkAppointmentInsuranceDocumentAPI,
    GetUpcomingRequestTargetsAPI,
    LinkUpcomingRequestAPI,
    GetInsurancePendingAPI,
    // Reschedule APIs
    GetAvailableSlotsAPI,
    // Recurring Appointments APIs
    PreviewRecurringAppointmentsAPI,
    CreateRecurringAppointmentsAPI,
    // Appointments KPI API
    GetAppointmentsKpiAPI,
    // Public Appointment Actions
    ResolvePublicAppointmentActionAPI,
    PrepareManualConfirmationMessageAPI,
    SendManualConfirmationRequestAPI,
    // Nota: APIs fiscais (NFS-e) estão em src/api/FiscalAPI.js
  };
